import * as i0 from '@angular/core';
import { Input, ViewEncapsulation, Component, EventEmitter, Output, ViewChild, forwardRef, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { fileToDataURL } from '@engineering11/web-utilities';
import heic2any from 'heic2any';
import { uuid, mergeDeep } from '@engineering11/utility';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Database, Picker } from 'emoji-picker-element';
import { escapeHtml, normalizeReference, isSpace } from 'markdown-it/lib/common/utils';
import { undo, redo, history } from 'prosemirror-history';
import { Plugin, PluginKey, TextSelection, EditorState, Selection } from 'prosemirror-state';
import { DecorationSet, Decoration, EditorView } from 'prosemirror-view';
import { Schema, DOMParser, DOMSerializer, Slice, Fragment } from 'prosemirror-model';
import MarkdownIt from 'markdown-it/lib';
import { defaultMarkdownParser, MarkdownParser, MarkdownSerializerState, MarkdownSerializer, defaultMarkdownSerializer } from 'prosemirror-markdown';
import Token from 'markdown-it/lib/token';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap, toggleMark, chainCommands, exitCode, setBlockType as setBlockType$1, wrapIn as wrapIn$1, splitBlock } from 'prosemirror-commands';
import { Strikethrough, Table, parser } from '@lezer/markdown';
import { tagHighlighter, tags, styleTags } from '@lezer/highlight';
import { highlightPlugin } from 'prosemirror-lezer';
import { liftTarget, Transform } from 'prosemirror-transform';
import { highlightPlugin as highlightPlugin$1 } from 'prosemirror-highlightjs';
import { InputRule, wrappingInputRule, textblockTypeInputRule, inputRules, undoInputRule } from 'prosemirror-inputrules';
import { wrapInList, liftListItem, splitListItem, sinkListItem } from 'prosemirror-schema-list';
import OrderedMap from 'orderedmap';
import 'highlight.js';

class E11MarkdownEditorIconComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MarkdownEditorIconComponent, selector: "e11-markdown-editor-icon", inputs: { customCss: "customCss", button: "button" }, ngImport: i0, template: "<button\n  type=\"button\"\n  class=\"e11-flex e11-p-1 e11-text-gray-500 e11-rounded e11-cursor-pointer hover:e11-text-gray-900 hover:e11-bg-gray-100 {{ customCss }}\"\n>\n  <span class=\"material-icons e11-text-[18px]\"> {{ button.icon }}</span>\n  <span class=\"e11-sr-only\">{{ button.type }}</span>\n</button>\n", encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-markdown-editor-icon', encapsulation: ViewEncapsulation.None, template: "<button\n  type=\"button\"\n  class=\"e11-flex e11-p-1 e11-text-gray-500 e11-rounded e11-cursor-pointer hover:e11-text-gray-900 hover:e11-bg-gray-100 {{ customCss }}\"\n>\n  <span class=\"material-icons e11-text-[18px]\"> {{ button.icon }}</span>\n  <span class=\"e11-sr-only\">{{ button.type }}</span>\n</button>\n" }]
        }], propDecorators: { customCss: [{
                type: Input
            }], button: [{
                type: Input
            }] } });

class E11MarkdownEditorFooterComponent {
    constructor() {
        this.fileSelected = new EventEmitter();
        this.action = new EventEmitter();
    }
    selectFile(event) {
        const { files } = event.target;
        // file validation
        if (files && files.length > 0) {
            this.fileSelected.emit(Array.from(files));
            this.fileInput.nativeElement.value = '';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MarkdownEditorFooterComponent, selector: "e11-markdown-editor-footer", inputs: { footer: "footer" }, outputs: { fileSelected: "fileSelected", action: "action" }, viewQueries: [{ propertyName: "fileInput", first: true, predicate: ["fileInput"], descendants: true }], ngImport: i0, template: "<input #fileInput type=\"file\" hidden multiple (change)=\"selectFile($event)\" />\n<div *ngIf=\"footer\" class=\"e11-flex e11-justify-between e11-items-center e11-px-2\">\n  <div class=\"e11-flex e11-flex-wrap e11-items-center e11-my-1 e11-divide-gray-200 sm:e11-divide-x\">\n    <div *ngIf=\"footer.attachment.length > 0\" class=\"e11-flex e11-items-center e11-space-x-1 sm:e11-pr-2\">\n      <ng-container *ngFor=\"let button of footer.attachment\">\n        <e11-markdown-editor-icon *ngIf=\"button.visible\" (click)=\"fileInput.click()\" [button]=\"button\"></e11-markdown-editor-icon>\n      </ng-container>\n    </div>\n    <div *ngIf=\"footer.audio.length > 0\" class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n      <ng-container *ngFor=\"let button of footer.audio\">\n        <e11-markdown-editor-icon *ngIf=\"button.visible\" [button]=\"button\" (click)=\"action.emit(button)\"></e11-markdown-editor-icon>\n      </ng-container>\n    </div>\n    <div *ngIf=\"footer.social.length > 0\" class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n      <ng-container *ngFor=\"let button of footer.social\">\n        <e11-markdown-editor-icon\n          *ngIf=\"button.visible\"\n          [id]=\"button.type\"\n          [button]=\"button\"\n          (click)=\"action.emit(button)\"\n        ></e11-markdown-editor-icon>\n      </ng-container>\n    </div>\n  </div>\n  <ng-container *ngFor=\"let button of footer.action\">\n    <e11-markdown-editor-icon\n      *ngIf=\"button.visible\"\n      [button]=\"button\"\n      (click)=\"action.emit(button)\"\n      [customCss]=\"'e11-p-1'\"\n    ></e11-markdown-editor-icon>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: E11MarkdownEditorIconComponent, selector: "e11-markdown-editor-icon", inputs: ["customCss", "button"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-markdown-editor-footer', encapsulation: ViewEncapsulation.None, template: "<input #fileInput type=\"file\" hidden multiple (change)=\"selectFile($event)\" />\n<div *ngIf=\"footer\" class=\"e11-flex e11-justify-between e11-items-center e11-px-2\">\n  <div class=\"e11-flex e11-flex-wrap e11-items-center e11-my-1 e11-divide-gray-200 sm:e11-divide-x\">\n    <div *ngIf=\"footer.attachment.length > 0\" class=\"e11-flex e11-items-center e11-space-x-1 sm:e11-pr-2\">\n      <ng-container *ngFor=\"let button of footer.attachment\">\n        <e11-markdown-editor-icon *ngIf=\"button.visible\" (click)=\"fileInput.click()\" [button]=\"button\"></e11-markdown-editor-icon>\n      </ng-container>\n    </div>\n    <div *ngIf=\"footer.audio.length > 0\" class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n      <ng-container *ngFor=\"let button of footer.audio\">\n        <e11-markdown-editor-icon *ngIf=\"button.visible\" [button]=\"button\" (click)=\"action.emit(button)\"></e11-markdown-editor-icon>\n      </ng-container>\n    </div>\n    <div *ngIf=\"footer.social.length > 0\" class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n      <ng-container *ngFor=\"let button of footer.social\">\n        <e11-markdown-editor-icon\n          *ngIf=\"button.visible\"\n          [id]=\"button.type\"\n          [button]=\"button\"\n          (click)=\"action.emit(button)\"\n        ></e11-markdown-editor-icon>\n      </ng-container>\n    </div>\n  </div>\n  <ng-container *ngFor=\"let button of footer.action\">\n    <e11-markdown-editor-icon\n      *ngIf=\"button.visible\"\n      [button]=\"button\"\n      (click)=\"action.emit(button)\"\n      [customCss]=\"'e11-p-1'\"\n    ></e11-markdown-editor-icon>\n  </ng-container>\n</div>\n" }]
        }], propDecorators: { fileInput: [{
                type: ViewChild,
                args: ['fileInput', { static: false }]
            }], footer: [{
                type: Input
            }], fileSelected: [{
                type: Output
            }], action: [{
                type: Output
            }] } });

class E11MarkdownEditorHeaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MarkdownEditorHeaderComponent, selector: "e11-markdown-editor-header", inputs: { header: "header" }, ngImport: i0, template: "<div *ngIf=\"header\" class=\"e11-flex e11-justify-between e11-items-center e11-pt-2 e11-px-2\">\n  <markdown-toolbar for=\"editor\">\n    <div class=\"e11-flex e11-flex-wrap e11-items-center e11-divide-gray-200 sm:e11-divide-x\">\n      <div class=\"e11-flex e11-items-center e11-space-x-1 sm:e11-pr-2\">\n        <ng-container *ngFor=\"let button of header.text\">\n          <ng-container *ngIf=\"button.type === 'bold'\">\n            <md-bold>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-bold>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'italic'\">\n            <md-italic>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-italic>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'strikethrough'\">\n            <md-strikethrough>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-strikethrough>\n          </ng-container>\n        </ng-container>\n      </div>\n      <div class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n        <ng-container *ngFor=\"let button of header.link\">\n          <ng-container *ngIf=\"button.type === 'link'\">\n            <md-link>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-link>\n          </ng-container>\n        </ng-container>\n      </div>\n      <div class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n        <ng-container *ngFor=\"let button of header.list\">\n          <ng-container *ngIf=\"button.type === 'ordered-list'\">\n            <md-ordered-list>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-ordered-list>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'unordered-list'\">\n            <md-unordered-list>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-unordered-list>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'task'\">\n            <md-task-list>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-task-list>\n          </ng-container>\n        </ng-container>\n      </div>\n      <div class=\"e11-hidden sm:e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n        <ng-container *ngFor=\"let button of header.code\">\n          <ng-container *ngIf=\"button.type === 'code'\">\n            <md-code>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-code>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'code-block'\">\n            <md-code>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-code>\n          </ng-container>\n        </ng-container>\n      </div>\n    </div>\n  </markdown-toolbar>\n\n  <e11-markdown-editor-icon *ngFor=\"let button of header.full_screen\" [button]=\"button\" [customCss]=\"'e11-p-1'\"></e11-markdown-editor-icon>\n</div>\n<!-- <markdown-toolbar for=\"editor\">\n\n  <md-header>header</md-header>\n  <md-italic>italic</md-italic>\n  <md-quote>quote</md-quote>\n  <md-code>code</md-code>\n  <md-link>link</md-link>\n  <md-image>image</md-image>\n  <md-unordered-list>unordered-list</md-unordered-list>\n  <md-ordered-list>ordered-list</md-ordered-list>\n  <md-task-list>task-list</md-task-list>\n  <md-mention>mention</md-mention>\n  <md-ref>ref</md-ref>\n  <button data-md-button>Custom button</button>\n</markdown-toolbar> -->\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: E11MarkdownEditorIconComponent, selector: "e11-markdown-editor-icon", inputs: ["customCss", "button"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-markdown-editor-header', encapsulation: ViewEncapsulation.None, template: "<div *ngIf=\"header\" class=\"e11-flex e11-justify-between e11-items-center e11-pt-2 e11-px-2\">\n  <markdown-toolbar for=\"editor\">\n    <div class=\"e11-flex e11-flex-wrap e11-items-center e11-divide-gray-200 sm:e11-divide-x\">\n      <div class=\"e11-flex e11-items-center e11-space-x-1 sm:e11-pr-2\">\n        <ng-container *ngFor=\"let button of header.text\">\n          <ng-container *ngIf=\"button.type === 'bold'\">\n            <md-bold>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-bold>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'italic'\">\n            <md-italic>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-italic>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'strikethrough'\">\n            <md-strikethrough>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-strikethrough>\n          </ng-container>\n        </ng-container>\n      </div>\n      <div class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n        <ng-container *ngFor=\"let button of header.link\">\n          <ng-container *ngIf=\"button.type === 'link'\">\n            <md-link>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-link>\n          </ng-container>\n        </ng-container>\n      </div>\n      <div class=\"e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n        <ng-container *ngFor=\"let button of header.list\">\n          <ng-container *ngIf=\"button.type === 'ordered-list'\">\n            <md-ordered-list>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-ordered-list>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'unordered-list'\">\n            <md-unordered-list>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-unordered-list>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'task'\">\n            <md-task-list>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-task-list>\n          </ng-container>\n        </ng-container>\n      </div>\n      <div class=\"e11-hidden sm:e11-flex e11-flex-wrap e11-items-center e11-space-x-1 sm:e11-px-2\">\n        <ng-container *ngFor=\"let button of header.code\">\n          <ng-container *ngIf=\"button.type === 'code'\">\n            <md-code>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-code>\n          </ng-container>\n          <ng-container *ngIf=\"button.type === 'code-block'\">\n            <md-code>\n              <e11-markdown-editor-icon [button]=\"button\"></e11-markdown-editor-icon>\n            </md-code>\n          </ng-container>\n        </ng-container>\n      </div>\n    </div>\n  </markdown-toolbar>\n\n  <e11-markdown-editor-icon *ngFor=\"let button of header.full_screen\" [button]=\"button\" [customCss]=\"'e11-p-1'\"></e11-markdown-editor-icon>\n</div>\n<!-- <markdown-toolbar for=\"editor\">\n\n  <md-header>header</md-header>\n  <md-italic>italic</md-italic>\n  <md-quote>quote</md-quote>\n  <md-code>code</md-code>\n  <md-link>link</md-link>\n  <md-image>image</md-image>\n  <md-unordered-list>unordered-list</md-unordered-list>\n  <md-ordered-list>ordered-list</md-ordered-list>\n  <md-task-list>task-list</md-task-list>\n  <md-mention>mention</md-mention>\n  <md-ref>ref</md-ref>\n  <button data-md-button>Custom button</button>\n</markdown-toolbar> -->\n" }]
        }], propDecorators: { header: [{
                type: Input
            }] } });

var FileMimeTypeIconTypes;
(function (FileMimeTypeIconTypes) {
    FileMimeTypeIconTypes["Audio"] = "Audio";
    FileMimeTypeIconTypes["Video"] = "Video";
    FileMimeTypeIconTypes["Preview"] = "Preview";
    FileMimeTypeIconTypes["Unknown"] = "Unknown";
})(FileMimeTypeIconTypes || (FileMimeTypeIconTypes = {}));
var FileActions;
(function (FileActions) {
    FileActions["View"] = "View";
    FileActions["Remove"] = "Remove";
})(FileActions || (FileActions = {}));
var MarkDownEditorPublicActionType;
(function (MarkDownEditorPublicActionType) {
    MarkDownEditorPublicActionType["Send"] = "send";
    MarkDownEditorPublicActionType["Upload"] = "upload";
    MarkDownEditorPublicActionType["Video_record"] = "video";
    MarkDownEditorPublicActionType["Emoji"] = "emoji";
})(MarkDownEditorPublicActionType || (MarkDownEditorPublicActionType = {}));

const mimeTypeIconMap = {
    // Media
    image: 'image',
    audio: 'audio_file',
    video: 'video_file',
    // Documents
    'application/pdf': 'picture_as_pdf',
    'application/msword': 'description',
    'application/vnd.ms-word': 'description',
    'application/vnd.oasis.opendocument.text': 'description',
    'application/vnd.openxmlformats-officedocument.wordprocessingml': 'description',
    'application/vnd.ms-excel': 'description',
    'application/vnd.openxmlformats-officedocument.spreadsheetml': 'description',
    'application/vnd.oasis.opendocument.spreadsheet': 'description',
    'application/vnd.ms-powerpoint': 'description',
    'application/vnd.openxmlformats-officedocument.presentationml': 'description',
    'application/vnd.oasis.opendocument.presentation': 'description',
    'text/plain': 'description',
    'text/html': 'html',
    'application/json': 'javascript',
    // Archives
    'application/gzip': 'folder_zip',
    'application/zip': 'folder_zip',
    //unknown
    unknown: 'insert_drive_file',
};
const validateFileSize = (file, maxFileSize) => {
    const fileInMB = file.size / (1024 * 1024);
    return fileInMB <= maxFileSize;
};
const checkIfImageIsAllowedType = (type) => {
    return ['image/png', 'image/jpeg', 'image/gif'].some((i) => i === type);
};
const checkIfVideoIsAllowedType = () => { };
const validateFileUploads = async (files) => {
    const newFiles = [];
    for (const file of files) {
        if (file.type.includes('image') && !checkIfImageIsAllowedType(file.type)) {
            // check if heic2any
            if (['image/heic', 'image/heif'].includes(file.type)) {
                const blob = await heic2any({
                    blob: file,
                    toType: 'image/jpeg',
                    quality: 0.7,
                });
                const finalFile = new File([blob], `${file.name.split('.')[0]}.jpeg`, { type: 'image/jpeg' });
                newFiles.push(finalFile);
            }
            else {
                // convert image
                const dataURL = await fileToDataURL(file);
                const blob = await (await fetch(dataURL)).blob();
                const finalFile = new File([blob], `${file.name.split('.')[0]}.jpeg`, { type: 'image/jpeg' });
                newFiles.push(finalFile);
            }
        }
        else {
            // no conversation required
            newFiles.push(file);
        }
    }
    return newFiles;
};
const generateIMarkdownFile = (file) => {
    return {
        id: uuid(),
        file,
    };
};

class E11MarkdownEditorImageComponent {
    constructor() {
        this._files = [];
        this._path = [];
        this.fileMimeTypeIconTypesEnum = FileMimeTypeIconTypes;
        this.fileAction = new EventEmitter();
    }
    set files(values) {
        this._files = Array.from(values).map((value) => ({
            markdownFile: value,
            iconType: this.findFileIcon(value.file.type),
        }));
        this.readAsDataURL(values).then((urls) => {
            this._path = urls;
        });
    }
    ngOnDestroy() {
        this._path = [];
        this._files = [];
    }
    removeImage(index) {
        this.fileAction.emit({
            index,
            action: FileActions.Remove,
        });
    }
    findFileIcon(mimeType) {
        let icon;
        Object.keys(mimeTypeIconMap).forEach((key) => {
            if (mimeType.includes(key)) {
                icon = mimeTypeIconMap[key];
            }
        });
        if (!icon) {
            return mimeTypeIconMap['unknown'];
        }
        if (icon === 'video_file') {
            return FileMimeTypeIconTypes.Video;
        }
        if (icon === 'image') {
            return FileMimeTypeIconTypes.Preview;
        }
        return icon;
    }
    fileToDataURL(markdownFile) {
        var reader = new FileReader();
        return new Promise(function (resolve, _reject) {
            reader.onload = function (event) {
                resolve(event.target.result);
            };
            reader.readAsDataURL(markdownFile.file);
        });
    }
    readAsDataURL(markdownFiles) {
        var filesArray = Array.prototype.slice.call(markdownFiles);
        return Promise.all(filesArray.map(this.fileToDataURL));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorImageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MarkdownEditorImageComponent, selector: "e11-markdown-editor-attachments", inputs: { files: "files" }, outputs: { fileAction: "fileAction" }, ngImport: i0, template: "<div class=\"e11-flex e11-gap-2 e11-items-center e11-flex-wrap\">\n  <ng-container *ngFor=\"let file of _files; let i = index\">\n    <div class=\"e11-relative e11-group\">\n      <ng-container [ngSwitch]=\"file.iconType\">\n        <video\n          *ngSwitchCase=\"fileMimeTypeIconTypesEnum.Video\"\n          class=\"e11-rounded-md e11-ring-1 e11-ring-gray-300 hover:e11-bg-gray-100\"\n          [src]=\"_path[i]\"\n          controls\n          width=\"250\"\n          alt=\"\"\n        >\n          <source [src]=\"_path[i]\" type=\"video/webm\" />\n        </video>\n        <img\n          *ngSwitchCase=\"fileMimeTypeIconTypesEnum.Preview\"\n          class=\"e11-h-14 w-14 e11-rounded-md e11-ring-1 e11-ring-gray-300 hover:e11-bg-gray-100\"\n          [src]=\"_path[i]\"\n          alt=\"\"\n        />\n        <div *ngSwitchDefault>\n          <button\n            *ngIf=\"file.iconType !== fileMimeTypeIconTypesEnum.Preview\"\n            type=\"button\"\n            class=\"e11-flex e11-pr-1 e11-py-1 e11-items-center e11-border-2 e11-text-gray-500 e11-rounded e11-cursor-pointer hover:e11-text-gray-900\"\n          >\n            <span class=\"material-icons e11-text-4xl\"> {{ file.iconType }}</span>\n            <span class=\"e11-sr-only\">{{ file.iconType }}</span>\n            <span class=\"e11-truncate e11-max-w-xs\"> {{ file.markdownFile.file.name }}</span>\n          </button>\n        </div>\n      </ng-container>\n\n      <span\n        class=\"e11-absolute e11-cursor-pointer e11-hidden group-hover:e11-block e11-top-0 e11-right-1 e11-h-5 e11-w-5 e11--translate-y-1/2 e11-translate-x-1/2 e11-transform e11-rounded-full e11-bg-white\"\n        (click)=\"removeImage(i)\"\n      >\n        <span class=\"material-icons e11-text-gray-400 e11-text-[20px]\">cancel</span>\n      </span>\n    </div>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i1.NgSwitchDefault, selector: "[ngSwitchDefault]" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-markdown-editor-attachments', encapsulation: ViewEncapsulation.None, template: "<div class=\"e11-flex e11-gap-2 e11-items-center e11-flex-wrap\">\n  <ng-container *ngFor=\"let file of _files; let i = index\">\n    <div class=\"e11-relative e11-group\">\n      <ng-container [ngSwitch]=\"file.iconType\">\n        <video\n          *ngSwitchCase=\"fileMimeTypeIconTypesEnum.Video\"\n          class=\"e11-rounded-md e11-ring-1 e11-ring-gray-300 hover:e11-bg-gray-100\"\n          [src]=\"_path[i]\"\n          controls\n          width=\"250\"\n          alt=\"\"\n        >\n          <source [src]=\"_path[i]\" type=\"video/webm\" />\n        </video>\n        <img\n          *ngSwitchCase=\"fileMimeTypeIconTypesEnum.Preview\"\n          class=\"e11-h-14 w-14 e11-rounded-md e11-ring-1 e11-ring-gray-300 hover:e11-bg-gray-100\"\n          [src]=\"_path[i]\"\n          alt=\"\"\n        />\n        <div *ngSwitchDefault>\n          <button\n            *ngIf=\"file.iconType !== fileMimeTypeIconTypesEnum.Preview\"\n            type=\"button\"\n            class=\"e11-flex e11-pr-1 e11-py-1 e11-items-center e11-border-2 e11-text-gray-500 e11-rounded e11-cursor-pointer hover:e11-text-gray-900\"\n          >\n            <span class=\"material-icons e11-text-4xl\"> {{ file.iconType }}</span>\n            <span class=\"e11-sr-only\">{{ file.iconType }}</span>\n            <span class=\"e11-truncate e11-max-w-xs\"> {{ file.markdownFile.file.name }}</span>\n          </button>\n        </div>\n      </ng-container>\n\n      <span\n        class=\"e11-absolute e11-cursor-pointer e11-hidden group-hover:e11-block e11-top-0 e11-right-1 e11-h-5 e11-w-5 e11--translate-y-1/2 e11-translate-x-1/2 e11-transform e11-rounded-full e11-bg-white\"\n        (click)=\"removeImage(i)\"\n      >\n        <span class=\"material-icons e11-text-gray-400 e11-text-[20px]\">cancel</span>\n      </span>\n    </div>\n  </ng-container>\n</div>\n" }]
        }], propDecorators: { files: [{
                type: Input
            }], fileAction: [{
                type: Output
            }] } });

/** Curried helper method that wraps a i18n method for menu entries w/ shortcuts */
function shortcut(text) {
    return (args) => `${text} (${args.shortcut})`;
}
/** The default set of localizable strings */
const defaultStrings = {
    commands: {
        blockquote: shortcut('Blockquote'),
        bold: shortcut('Bold'),
        code_block: shortcut('Code block'),
        emphasis: shortcut('Italic'),
        heading: {
            dropdown: shortcut('Heading'),
            entry: ({ level }) => `Heading ${level}`,
        },
        help: 'Help',
        horizontal_rule: shortcut('Horizontal rule'),
        image: shortcut('Image'),
        inline_code: shortcut('Inline code'),
        link: shortcut('Link'),
        ordered_list: shortcut('Numbered list'),
        redo: shortcut('Redo'),
        strikethrough: 'Strikethrough',
        table_edit: 'Edit table',
        table_insert: shortcut('Table'),
        table_column: {
            insert_after: 'Insert column after',
            insert_before: 'Insert column before',
            remove: 'Remove column',
        },
        table_row: {
            insert_after: 'Insert row after',
            insert_before: 'Insert row before',
            remove: 'Remove row',
        },
        undo: shortcut('Undo'),
        unordered_list: shortcut('Bulleted list'),
    },
    link_editor: {
        cancel_button: 'Cancel',
        href_label: 'Link URL',
        save_button: 'Save',
        text_label: 'Link text',
        validation_error: 'The entered URL is invalid.',
    },
    link_tooltip: {
        edit_button_title: 'Edit link',
        remove_button_title: 'Remove link',
    },
    menubar: {
        mode_toggle_label: 'Markdown',
        mode_toggle_title: 'Toggle Markdown mode',
    },
    nodes: {
        codeblock_lang_auto: ({ lang }) => `${lang} (auto)`,
        spoiler_reveal_text: 'Reveal spoiler',
    },
    image_upload: {
        default_image_alt_text: 'enter image description here',
        external_url_validation_error: 'The entered URL is invalid.',
        upload_error_file_too_big: 'Your image is too large to upload (over 2 MiB)',
        upload_error_generic: 'Image upload failed. Please try again.',
        upload_error_unsupported_format: 'Please select an image (jpeg, png, gif) to upload',
        uploaded_image_preview_alt: 'uploaded image preview',
    },
};
/** The set of strings that were overridden by registerLocalizationStrings */
let strings = defaultStrings;
/** Registers new localization strings; any strings that are left unregistered will fall back to the default value */
function registerLocalizationStrings(newStrings) {
    strings = newStrings;
}
/** Resolves a dot-separated key against an object */
function resolve(obj, key) {
    //@ts-ignore
    return key.split('.').reduce((p, n) => p?.[n], obj);
}
/** Caches key lookups to their values so we're not continuously splitting */
const cache = {};
/**
 * Checks the localized strings for a given key and returns the value
 * @param key A dot-separated key to the localized string e.g. "menubar.mode_toggle_label"
 * @param params An object of parameters to pass to the localization function if it exists
 */
function _t(key, params = {}) {
    //@ts-ignore
    if (!(key in cache))
        cache[key] = resolve(strings, key) || resolve(defaultStrings, key);
    const string = cache[key];
    if (!string)
        throw `Missing translation for key: ${key}`;
    if (typeof string === 'string')
        return string;
    else if (typeof string === 'function')
        return string(params);
    throw `Missing translation for key: ${key}`;
}

/**
 * Recursively deep merges two objects into a new object, leaving the original two untouched
 * NOTE: Arrays are only shallow merged
 * @param object1 The object to base the merge off of
 * @param object2 The object to merge into the base object
 */
function deepMerge(object1, object2) {
    return mergeObject(object1, object2);
}
function isPlainObject(obj) {
    // not an object type
    if (!(obj instanceof Object))
        return false;
    if (obj instanceof Function)
        return false;
    if (obj instanceof Array)
        return false;
    // probably an object
    return true;
}
function mergeObject(object1, object2) {
    // if both are arrays, shallow merge (else, non-object merge)
    if (object1 instanceof Array && object2 instanceof Array) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment
        return [...object1, ...object2];
    }
    const obj1IsObject = isPlainObject(object1);
    const obj2IsObject = isPlainObject(object2);
    // if the first item is not an object, return a clone of the second
    if (!obj1IsObject && obj2IsObject)
        return mergeObject({}, object2);
    // if the second is not an object, return a clone of the first
    else if (obj1IsObject && !obj2IsObject)
        return mergeObject({}, object1);
    // if neither are objects, return the second item
    else if (!obj1IsObject && !obj2IsObject)
        return object2;
    const outObject = {};
    // we know these are objects (from above), so retype them now
    const obj1 = object1;
    const obj2 = object2;
    // start merging all the keys from the first object
    let keys = Object.keys(obj1);
    for (const key of keys) {
        if (key in obj2)
            outObject[key] = mergeObject(obj1[key], obj2[key]);
        else
            outObject[key] = obj1[key];
    }
    // set all leftover keys from object2
    keys = Object.keys(obj2);
    for (const key of keys) {
        // we already merged these keys, skip
        if (key in outObject)
            continue;
        outObject[key] = obj2[key];
    }
    return outObject;
}
/**
 * Compares two states and returns true if the doc has changed between them.
 * The doc is considered changed if:
 *      * its content changed
 *      * the stored marks have changed
 * @param prevState The "old" / previous editor state
 * @param newState The "new" / current editor state
 */
function docNodeChanged(prevState, newState) {
    // if either are null, just return "changed"
    if (!prevState || !newState)
        return true;
    return !prevState.doc.eq(newState.doc) || prevState.storedMarks !== newState.storedMarks;
}
/**
 * Compares two states and returns true if the doc has changed between them.
 * The doc is considered changed if:
 *      * the document node changed (@see docNodeChanged)
 *      * the selection has changed
 * @param prevState The "old" / previous editor state
 * @param newState The "new" / current editor state
 */
function docChanged(prevState, newState) {
    return docNodeChanged(prevState, newState) || !prevState.selection.eq(newState.selection);
}
/**
 * Returns the text node the cursor is currently anchored in
 * @param state The current editor state
 * @returns A text node or null if the cursor is not in a text node
 */
function getCurrentTextNode(state) {
    if (!state.selection.$anchor.textOffset)
        return null;
    const $anchor = state.selection.$anchor;
    return $anchor.parent.child($anchor.index());
}
/** The class to attach sticky observers to */
const STICKY_OBSERVER_CLASS = 'js-sticky';
// TODO should this go into Stacks proper?
/**
 * Starts observers watching all .STICKY_OBSERVER_CLASS elements for a change in stuck position
 * @param container The container to search for STICKY_OBSERVER_CLASS elements (usually the closest scrolling parent)
 * @fires sticky-change
 */
function startStickyObservers(container) {
    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            const stuck = !entry.isIntersecting;
            const target = entry.target.nextElementSibling;
            /**
             * sticky-change event
             * @event sticky-change
             * @type {object}
             * @property {boolean} detail.stuck - Indicates whether the target is stuck
             * @property {Element} detail.target - The targeted position:sticky element
             */
            const e = new CustomEvent('sticky-change', {
                detail: { stuck, target },
            });
            document.dispatchEvent(e);
        }
    }, {
        threshold: [0],
        root: container,
    });
    container.querySelectorAll(`.${STICKY_OBSERVER_CLASS}`).forEach((el) => {
        const sentinel = document.createElement('div');
        // not altogether necessary, but let's label the div so others know what this is and where it is coming from
        sentinel.className = 'js-sticky-sentinel';
        // add right before the sticky element; if this element becomes obscured, then the sticky is unstuck
        el.parentNode.insertBefore(sentinel, el);
        observer.observe(sentinel);
    });
}
// rudimentary link validation that's roughly in line with what Stack Overflow's backend uses for validation
const validLinkRegex = /^((https?|ftp):\/\/|\/)[-a-z0-9+&@#/%?=~_|!:,.;()*[\]$]+$/;
const validMailtoRegex = /^mailto:[#-.\w]+@[-a-z0-9]+(\.[-a-z0-9]+)*\.[a-z]+$/;
/**
 * Checks if a url is well-formed and passes Stack Overflow's validation checks
 * @param url The url to validate
 */
function e11flowValidateLink(url) {
    const normalizedUrl = url?.trim().toLowerCase();
    return validLinkRegex.test(normalizedUrl) || validMailtoRegex.test(normalizedUrl);
}
/**
 * Template function to escape all html in substitutions, but not the rest of the template.
 * For instance, escapeHTML`<p>${"<span>user input</span>"}</p>` will escape the inner span, but not the outer p tags.
 * Uses markdown-it's @see escapeHtml in the background
 */
function escapeHTML(strings, ...subs) {
    let output = strings[0];
    for (let i = 0, len = subs.length; i < len; i++)
        output += escapeHtml(subs[i]?.toString() || '') + strings[i + 1];
    return output;
}
/** Gets the modifier key for the current platform; i.e. "Command" on macOS and "Control" elsewhere */
function getPlatformModKey() {
    return /Mac|iP(hone|[oa]d)/.test(navigator.platform) ? 'Cmd' : 'Ctrl';
}
/**
 * Returns a string containing the label and readable keyboard shortcut for button tooltips
 * @param mapping Corresponding command mapping (keyboard shortcut)
 */
function getShortcut(mapping) {
    if (!mapping.startsWith('Mod-'))
        return mapping;
    return getPlatformModKey() + mapping.slice(3);
}
/**
 * Prefixes an event name to scope it to the editor
 * e.g. `view-change` becomes `E11Editor:view-change`
 * @param eventName The event name to prefix
 */
function prefixEventName(eventName) {
    return `E11Editor:${eventName}`;
}
/**
 * Generated a random id that can be used to ensure DOM element ids are unique
 * @returns a random string
 */
function generateRandomId() {
    return (Math.random() * 10000).toFixed(0);
}
/**
 * Prefixes and dispatches a custom event on the target
 * @param target The target to dispatch the event on
 * @param eventName The unprefixed event name
 * @param detail Any custom data to pass on the event
 * @returns true if either event's cancelable attribute value is false or its preventDefault() method was not invoked, and false otherwise
 */
function dispatchEditorEvent(target, eventName, detail) {
    const event = new CustomEvent(prefixEventName(eventName), {
        bubbles: true,
        cancelable: true,
        detail,
    });
    return target.dispatchEvent(event);
}
/**
 * Binds a keymap containing a letter to both the lowercase and uppercase version of that letter;
 * this is done so that when the user has CAPS LOCK on, the keymap will still work
 * @param mapping The keymap string to bind
 * @param command The command to bind to all generated keymaps
 */
function bindLetterKeymap(mapping, command) {
    const letter = mapping.split('-')[1]?.toLowerCase().trim();
    // not a single letter, so just return the mapping
    if (!letter || !letter.match(/^[a-z]{1}$/))
        return { [mapping]: command };
    const prefix = mapping.slice(0, -1);
    return {
        [prefix + letter]: command,
        [prefix + letter.toUpperCase()]: command,
    };
}

/** Describes each distinct editor type the E11Editor handles */
var EditorType;
(function (EditorType) {
    EditorType[EditorType["RichText"] = 0] = "RichText";
    EditorType[EditorType["Commonmark"] = 1] = "Commonmark";
})(EditorType || (EditorType = {}));
const defaultParserFeatures = {
    snippets: true,
    html: true,
    extraEmphasis: true,
    tables: true,
    tagLinks: {
        validate: () => true,
    },
    validateLink: e11flowValidateLink,
};
/** Abstract class that contains shared functionality for implementing View */
class BaseView {
    get document() {
        return this.editorView.state.doc;
    }
    get dom() {
        return this.editorView.dom;
    }
    get readonly() {
        return !this.editorView.editable;
    }
    focus() {
        this.editorView?.focus();
    }
    destroy() {
        this.editorView?.destroy();
    }
    get content() {
        return this.serializeContent();
    }
    set content(value) {
        let tr = this.editorView.state.tr;
        const doc = this.editorView.state.doc;
        const newDoc = this.parseContent(value);
        tr = tr.replaceWith(0, doc.content.size, newDoc);
        this.editorView.dispatch(tr);
    }
}

/* eslint-disable no-useless-call */
// eslint disables needed to ensure we don't spam the console in production/test environments
/* eslint-disable no-console, no-process-env */
// env variables set by webpack (or jest) based on the build mode
const isDevelopment = false;
const isTest = false;
/**
 * Styles text to be used as a "header" in the console
 * @param text The text to stype
 */
function styleHeaderText(text, preface = '') {
    if (!text)
        return [];
    return [`%c${preface}${text}`, 'font-weight: bold;'];
}
/**
 * Logs a message to the console with a styled "header" (development only)
 * @param header The header of the log which will be styled to stand out
 * @param message Any items to log in the message
 */
function log(header, ...message) {
    if (!isDevelopment)
        return;
    console.log.apply(console, [...styleHeaderText(header, '[DEBUG] '), ...message]);
}
/**
 * Logs an error to the console with a styled "header" (both development and prod)
 * @param header The header of the log which will be styled to stand out
 * @param error Any items to log in the error
 */
function error(header, ...error) {
    // don't spam up our test output
    if (isTest)
        return;
    console.error.apply(console, [...styleHeaderText(header), ...error]);
}

/** NoOp to use in place of missing commands */
const commandNoOp = () => false;
class MenuView {
    static { this.disabledClass = 'is-disabled'; }
    static { this.activeClass = 'is-selected'; }
    static { this.invisibleClass = 'd-none'; }
    constructor(blocks, view) {
        // sort the blocks by their priority; lower priority first
        this.blocks = blocks.filter((b) => !!b).sort((a, b) => a.priority - b.priority);
        this.view = view;
        this.dom = document.createElement('div');
        this.dom.className = 'e11-flex e11-flex-wrap e11-items-center e11-divide-gray-200 sm:e11-divide-x js-editor-menu';
        for (const block of this.blocks) {
            // drop all null entries
            block.entries = block.entries.filter((e) => !!e);
            const blockDom = this.makeBlockContainer(block);
            for (const entry of block.entries)
                blockDom.appendChild(entry.dom);
            this.dom.appendChild(blockDom);
            block.dom = blockDom;
        }
        //@ts-ignore
        this.update(view, null);
        // turn all menu commands into a flat list so we can easily look them up later
        const menuCommands = [].concat(...this.blocks
            .map((item) => item.entries)
            .reduce((a, b) => a.concat(b), [])
            .filter((e) => !!e)
            .map((item) => {
            if (item.children) {
                // include the drop-down parent AND all of its children if there are child MenuCommandEntries
                return [item, ...item.children];
            }
            return item;
        }));
        // NOTE: make sure your plugin/menu container calls `e.preventDefault()` on `mousedown` events
        // so the editor doesn't blur on click! E11Editor automatically handles this for us in a typical use-case
        this.dom.addEventListener('click', (e) => {
            // find the closest button parent of the clicked element
            const target = e.target.closest('.js-editor-btn');
            if (!target)
                return;
            // if the button is "disabled", return early
            if (target.classList.contains(MenuView.disabledClass))
                return;
            const key = target.dataset.key;
            e.preventDefault();
            view.focus();
            const found = menuCommands.find((c) => c.key === key);
            if (found)
                found.command(view.state, view.dispatch.bind(view), view);
        });
        this.readonly = !view.editable;
    }
    update(view, prevState) {
        // if the doc/view hasn't changed, there's no work to do
        if (!docChanged(prevState, view.state) && this.readonly !== view.editable)
            return;
        this.readonly = !view.editable;
        // disable *all* clicks if the menu is readonly
        this.dom.classList.toggle('pe-none', this.readonly);
        const viewIsReadonly = this.readonly;
        const isFocused = this.view.hasFocus();
        // check all blocks and their commands for visibility
        for (const block of this.blocks) {
            const visible = block.visible ? block.visible(this.view.state) : true;
            block.dom.classList.toggle(MenuView.invisibleClass, !visible);
            // don't bother checking commands if the block is not visible
            if (!visible)
                continue;
            // check each entry and update the dom to match the current state
            for (const entry of block.entries) {
                this.checkAndUpdateMenuCommandState(entry, viewIsReadonly, isFocused);
            }
        }
    }
    destroy() {
        this.dom.remove();
    }
    /**
     * Checks if the given menu command is visible/active for the current state and updates its dom to match
     * @param entry The menu command entry to check
     * @param isReadonly Whether the editor is readonly
     * @param isFocused Whether the editor currently has focus
     */
    checkAndUpdateMenuCommandState(entry, isReadonly, isFocused) {
        let dom = entry.dom;
        // make sure we really got the button itself, not a wrapper
        if (!dom.classList.contains('js-editor-btn')) {
            const button = dom.querySelector('.js-editor-btn');
            dom = button ?? dom;
        }
        const visible = entry.visible ? entry.visible(this.view.state) : true;
        const active = isFocused && entry.active ? entry.active(this.view.state) : false;
        //@ts-ignore
        const enabled = !isReadonly && entry.command(this.view.state, undefined, this.view);
        dom.classList.remove(MenuView.disabledClass);
        dom.classList.remove(MenuView.activeClass);
        dom.classList.remove(MenuView.invisibleClass);
        dom.dataset.key = entry.key;
        // class priority is active > disabled > default
        if (!visible)
            dom.classList.add(MenuView.invisibleClass);
        else if (active)
            dom.classList.add(MenuView.activeClass);
        else if (!enabled)
            dom.classList.add(MenuView.disabledClass);
        // if this is a dropdown, check all of its children as well
        if (entry.children?.length) {
            for (const child of entry.children) {
                this.checkAndUpdateMenuCommandState(child, isReadonly, isFocused);
            }
        }
    }
    /** Creates the element that a block's child entries' doms are placed into */
    makeBlockContainer(block) {
        const dom = document.createElement('div');
        dom.className = `s-editor-menu-block e11-flex e11-items-center e11-space-x-1 sm:e11-pr-2 ${block.classes?.join(' ') ?? ''}`;
        return dom;
    }
}
/**
 * Simple wrapper function to ensure that conditional menu item adds are consistent
 * @param item The item to add if flag is truthy
 * @param flag Whether to add the item
 * @internal
 */
function addIf(item, flag) {
    return flag ? item : null;
}
/**
 * Creates a menu plugin with the passed in entries
 * @param blocks The entries to use on the generated menu
 * @param containerFn A function that returns the container element for the menu
 * @internal
 */
function createMenuPlugin(blocks, containerFn) {
    return new Plugin({
        view(editorView) {
            const menuView = new MenuView(blocks, editorView);
            containerFn =
                containerFn ||
                    function (v) {
                        return v.dom.parentNode;
                    };
            const container = containerFn(editorView);
            // if the container is the same as the one that has the editor
            // insert the menu before it
            if (container.contains(editorView.dom))
                container.insertBefore(menuView.dom, editorView.dom);
            else
                container.insertBefore(menuView.dom, container.firstChild);
            return menuView;
        },
    });
}
/**
 * Helper function to create consistent menu entry doms
 * @param iconName The html of the svg to use as the icon
 * @param title The text to place in the button's title attribute
 * @param key A unique identifier used for identifying the command to be executed on click
 * @param cssClasses extra CSS classes to be applied to this menu icon (optional)
 * @internal
 */
function makeMenuButton(iconName, title, key, cssClasses) {
    const button = document.createElement('button');
    button.className = `s-editor-btn s-btn js-editor-btn e11-flex e11-p-1 e11-text-gray-500 e11-rounded e11-cursor-pointer hover:e11-text-gray-900 hover:e11-bg-gray-100  js-${key}`;
    if (cssClasses)
        button.classList.add(...cssClasses);
    button.title = title;
    button.setAttribute('aria-label', title);
    button.dataset.controller = 's-tooltip';
    button.dataset.sTooltipPlacement = 'top';
    button.dataset.key = key;
    button.type = 'button';
    // create the svg icon-bg element
    const icon = document.createElement('span');
    icon.className = `icon-bg material-icons e11-text-[18px] icon${iconName}`;
    icon.innerHTML = `${iconName}`;
    button.append(icon);
    return button;
}
/**
 * Create a dropdown menu item that contains all children in its popover
 * @param svg The html of the svg to use as the dropdown icon
 * @param title The text to place in the dropdown button's title attribute
 * @param key A unique identifier used for this dropdown menu
 * @param visible A function that determines wether the dropdown should be visible or hidden
 * @param active A function to determine if the dropdown should be highlighted as active
 * @param children The child MenuCommandEntry items to be placed in the dropdown menu
 * @internal
 */
function makeMenuDropdown(svg, title, key, visible, active, ...children) {
    const randomId = generateRandomId();
    const popoverId = `${key}-popover-${randomId}`;
    const buttonId = `${key}-btn-${randomId}`;
    const button = makeMenuButton(svg, title, key);
    button.classList.add('s-btn', 's-btn__dropdown');
    button.setAttribute('aria-controls', popoverId);
    button.setAttribute('data-action', 's-popover#toggle');
    button.setAttribute('data-controller', 's-tooltip');
    button.id = buttonId;
    button.dataset.key = key;
    const popover = document.createElement('div');
    popover.className = 's-popover wmn-initial w-auto px0 pt0 pb8';
    popover.id = popoverId;
    popover.setAttribute('role', 'menu');
    const arrow = document.createElement('div');
    arrow.className = 's-popover--arrow';
    popover.appendChild(arrow);
    const content = document.createElement('div');
    content.className = 'd-flex fd-column';
    content.append(...children.map((c) => c.dom));
    popover.appendChild(content);
    const wrapper = document.createElement('div');
    wrapper.dataset.controller = 's-popover';
    wrapper.setAttribute('data-s-popover-toggle-class', 'is-selected');
    wrapper.setAttribute('data-s-popover-placement', 'bottom');
    wrapper.setAttribute('data-s-popover-reference-selector', `#${buttonId}`);
    wrapper.appendChild(button);
    wrapper.appendChild(popover);
    return {
        key,
        dom: wrapper,
        children,
        command: () => true,
        visible,
        active,
    };
}
/**
 * Creates a dropdown menu item that can be embedded in a dropdown menu's popover
 * @param title The text to be displayed for this item
 * @param command The command to be executed when this item is clicked
 * @param key A unique identifier used for identifying the command to be executed on click
 * @param active A function to determine whether this item should be rendered as "active"
 * @param cssClasses Additional css classes to be applied to this dropdown item
 * @internal
 */
function dropdownItem(title, command, key, active, cssClasses) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.key = key;
    button.textContent = title;
    button.dataset.action = 's-popover#hide';
    button.className = 's-block-link s-editor--dropdown-item js-editor-btn';
    if (cssClasses)
        button.classList.add(...cssClasses);
    return {
        key,
        command,
        dom: button,
        active,
    };
}
/**
 * Creates a section with a heading usable for dropdown menus. This is just a visual element with no
 * interaction and no action being triggered on click
 * @param title The text to be displayed for this item
 * @param key A unique identifier used for identifying the command to be executed on click
 * @internal
 */
function dropdownSection(title, key) {
    const section = document.createElement('span');
    section.className = 'flex--item ta-left fs-fine tt-uppercase mx12 mb6 mt12 fc-black-400';
    section.dataset.key = key;
    section.textContent = title;
    return {
        key,
        command: () => true,
        visible: () => true,
        active: () => false,
        dom: section,
    };
}
/**
 * Creates a link entry that opens a _blank to href when clicked
 * @param iconName The html of the svg to use as the icon
 * @param title The text to place in the link's title attribute
 * @param href The href to open when clicked
 * @param key A unique identifier used for this link
 * @internal
 */
function makeMenuLinkEntry(iconName, title, href, key) {
    const dom = document.createElement('a');
    dom.className = `s-editor-btn s-btn flex--item js-editor-btn js-${key}`;
    dom.href = href;
    dom.target = '_blank';
    dom.title = title;
    dom.setAttribute('aria-label', title);
    dom.dataset.controller = 's-tooltip';
    dom.dataset.sTooltipPlacement = 'top';
    // create the svg icon-bg element
    const icon = document.createElement('span');
    icon.className = `icon-bg icon${iconName}`;
    dom.append(icon);
    return {
        key,
        command: (_, dispatch) => {
            if (dispatch)
                window.open(dom.href, dom.target);
            return !!href;
        },
        active: commandNoOp,
        dom,
    };
}

// TODO this relies on Stacks classes, should we abstract?
/** Base rich-text schema; heavily inspired by prosemirror-markdown's schema */
const nodes = {
    doc: {
        content: 'block+',
    },
    paragraph: {
        content: 'inline*',
        group: 'block',
        parseDOM: [{ tag: 'p' }],
        toDOM() {
            return ['p', 0];
        },
    },
    /** IMPORTANT: This needs be be set _before_ blockquote below, since they share the same tag in parseDOM */
    spoiler: {
        content: 'block+',
        group: 'block',
        attrs: { revealed: { default: false } },
        parseDOM: [
            {
                tag: 'blockquote.spoiler',
                getAttrs(node) {
                    if (typeof node === 'string')
                        return false;
                    return {
                        revealed: node.classList.contains('is-visible'),
                    };
                },
            },
        ],
        toDOM(node) {
            return [
                'blockquote',
                {
                    class: `spoiler${node.attrs.revealed ? ' is-visible' : ''}`,
                    'data-spoiler': _t('nodes.spoiler_reveal_text'),
                },
                0,
            ];
        },
    },
    blockquote: {
        content: 'block+',
        group: 'block',
        parseDOM: [{ tag: 'blockquote' }],
        toDOM() {
            return ['blockquote', 0];
        },
    },
    horizontal_rule: {
        group: 'block',
        parseDOM: [{ tag: 'hr' }],
        toDOM() {
            return ['div', ['hr']];
        },
    },
    heading: {
        attrs: { level: { default: 1 } },
        content: 'inline*',
        group: 'block',
        defining: true,
        parseDOM: [
            { tag: 'h1', attrs: { level: 1 } },
            { tag: 'h2', attrs: { level: 2 } },
            { tag: 'h3', attrs: { level: 3 } },
            { tag: 'h4', attrs: { level: 4 } },
            { tag: 'h5', attrs: { level: 5 } },
            { tag: 'h6', attrs: { level: 6 } },
        ],
        toDOM(node) {
            return [`h${node.attrs.level}`, 0];
        },
    },
    code_block: {
        content: 'text*',
        group: 'block',
        code: true,
        defining: true,
        marks: '',
        attrs: {
            params: { default: '' },
            detectedHighlightLanguage: { default: '' },
            isEditingProcessor: { default: false },
        },
        parseDOM: [
            {
                tag: 'pre',
                preserveWhitespace: 'full',
                getAttrs: (node) => {
                    if (typeof node === 'string')
                        return false;
                    return {
                        params: node.getAttribute('data-params') || '',
                    };
                },
            },
        ],
        toDOM(node) {
            return ['pre', node.attrs.params ? { 'data-params': node.attrs.params } : {}, ['code', 0]];
        },
    },
    ordered_list: {
        content: 'list_item+',
        group: 'block',
        attrs: { order: { default: 1 }, tight: { default: false } },
        parseDOM: [
            {
                tag: 'ol',
                getAttrs(dom) {
                    if (typeof dom === 'string')
                        return false;
                    return {
                        order: dom.hasAttribute('start') ? +dom.getAttribute('start') : 1,
                        tight: dom.hasAttribute('data-tight'),
                    };
                },
            },
        ],
        toDOM(node) {
            return [
                'ol',
                {
                    start: node.attrs.order === 1 ? null : String(node.attrs.order),
                    'data-tight': node.attrs.tight ? 'true' : null,
                    class: 'e11-list-decimal e11-ml-5',
                },
                0,
            ];
        },
    },
    bullet_list: {
        content: 'list_item+',
        group: 'block',
        attrs: { tight: { default: false } },
        parseDOM: [
            {
                tag: 'ul',
                getAttrs: (dom) => {
                    if (typeof dom === 'string')
                        return false;
                    return { tight: dom.hasAttribute('data-tight') };
                },
            },
        ],
        toDOM(node) {
            return ['ul', { 'data-tight': node.attrs.tight ? 'true' : null, class: 'e11-list-disc e11-ml-5' }, 0];
        },
    },
    list_item: {
        content: 'block+',
        defining: true,
        parseDOM: [{ tag: 'li' }],
        toDOM() {
            return ['li', 0];
        },
    },
    text: {
        group: 'inline',
    },
    image: {
        inline: true,
        group: 'inline',
        draggable: true,
        attrs: {
            src: {},
            alt: { default: null },
            title: { default: null },
            width: { default: null },
            height: { default: null },
        },
        parseDOM: [
            {
                tag: 'img[src]',
                getAttrs(dom) {
                    if (typeof dom === 'string')
                        return false;
                    return {
                        src: dom.getAttribute('src'),
                        title: dom.getAttribute('title'),
                        alt: dom.getAttribute('alt'),
                        height: dom.getAttribute('height'),
                        width: dom.getAttribute('width'),
                    };
                },
            },
        ],
        toDOM(node) {
            return ['img', node.attrs];
        },
    },
    hard_break: {
        inline: true,
        group: 'inline',
        selectable: false,
        parseDOM: [{ tag: 'br' }],
        toDOM() {
            return ['br'];
        },
    },
    pre: genHtmlBlockNodeSpec('pre'),
    /**
     * Defines an uneditable html_block node; Only appears when a user has written a "complicated" html_block
     * i.e. anything not resembling `<tag>content</tag>` or `<tag />`
     */
    html_block: {
        content: 'text*',
        attrs: { content: { default: '' } },
        marks: '_',
        group: 'block',
        atom: true,
        inline: false,
        selectable: true,
        // IMPORTANT! Removing this will cause inline content to be "collapsed" upwards, removing the metadata
        defining: true,
        // isolating set true so we don't allow outside content to accidentally enter this node (preserves html inside)
        isolating: true,
        parseDOM: [{ tag: 'div.html_block' }],
        toDOM(node) {
            return [
                'div',
                {
                    class: 'html_block',
                },
                node.attrs.content,
            ];
        },
    },
    /**
     * Defines an uneditable html_inline node; These should very rarely appear in cases where
     * a user has a "valid", but unpaired html_inline tag (e.g. `test</em>`)
     */
    html_inline: {
        content: 'text*',
        attrs: { content: { default: '' } },
        marks: '_',
        group: 'inline',
        atom: true,
        inline: true,
        selectable: true,
        // IMPORTANT! Removing this will cause inline content to be "collapsed" upwards, removing the metadata
        defining: true,
        // isolating set true so we don't allow outside content to accidentally enter this node (preserves html inside)
        isolating: true,
        parseDOM: [{ tag: 'span.html_inline' }],
        toDOM(node) {
            return [
                'span',
                {
                    class: 'html_inline',
                },
                node.attrs.content,
            ];
        },
    },
    /**
     * Represents an `html_block` node that was split by a newline, then put back together post-tokenization.
     * The "content" of the container is editable, but the leading/trailing html is not.
     * e.g `<blockquote>**Test**\n\n_test_</blockquote>` will have the `_test_` editable, but not the `**Test**`
     */
    html_block_container: {
        content: 'block*',
        attrs: { contentOpen: { default: '' }, contentClose: { default: '' } },
        marks: '_',
        group: 'block',
        inline: false,
        selectable: true,
        // IMPORTANT! Removing this will cause inline content to be "collapsed" upwards, removing the metadata
        defining: true,
        // isolating set true so we don't allow outside content to accidentally enter this node (preserves html inside)
        isolating: true,
    },
    // manually render softbreaks, making sure to mark them
    // so we when parse them back out we can convert back to \n for markdown
    softbreak: {
        content: 'inline+',
        attrs: {},
        marks: '_',
        inline: true,
        group: 'inline',
        // TODO accurate? necessary?
        parseDOM: [
            {
                tag: 'span[softbreak]',
                getAttrs(node) {
                    if (typeof node === 'string')
                        return false;
                    return {
                        content: node.innerHTML,
                    };
                },
            },
        ],
        toDOM() {
            return [
                'span',
                {
                    softbreak: '',
                },
                0,
            ];
        },
    },
    table: {
        content: 'table_head table_body*',
        isolating: true,
        group: 'block',
        selectable: false,
        parseDOM: [{ tag: 'table' }],
        toDOM() {
            return ['div', { class: 's-table-container' }, ['table', { class: 's-table' }, 0]];
        },
    },
    table_head: {
        content: 'table_row',
        isolating: true,
        group: 'table_block',
        selectable: false,
        parseDOM: [{ tag: 'thead' }],
        toDOM() {
            return ['thead', 0];
        },
    },
    table_body: {
        content: 'table_row+',
        isolating: true,
        group: 'table_block',
        selectable: false,
        parseDOM: [{ tag: 'tbody' }],
        toDOM() {
            return ['tbody', 0];
        },
    },
    table_row: {
        content: '(table_cell | table_header)+',
        isolating: true,
        group: 'table_block',
        selectable: false,
        parseDOM: [{ tag: 'tr' }],
        toDOM() {
            return ['tr', 0];
        },
    },
    table_cell: {
        content: 'inline*',
        isolating: true,
        group: 'table_block',
        selectable: false,
        attrs: {
            style: { default: null },
        },
        parseDOM: [
            {
                tag: 'td',
                getAttrs(dom) {
                    if (typeof dom === 'string')
                        return false;
                    const textAlign = dom.style.textAlign;
                    return textAlign ? { style: `text-align: ${textAlign}` } : null;
                },
            },
        ],
        toDOM(node) {
            return ['td', node.attrs, 0];
        },
    },
    table_header: {
        content: 'inline*',
        isolating: true,
        group: 'table_block',
        selectable: false,
        attrs: {
            style: { default: null },
        },
        parseDOM: [
            {
                tag: 'th',
                getAttrs(dom) {
                    if (typeof dom === 'string')
                        return false;
                    const textAlign = dom.style.textAlign;
                    return textAlign ? { style: `text-align: ${textAlign}` } : null;
                },
            },
        ],
        toDOM(node) {
            return ['th', node.attrs, 0];
        },
    },
    // TODO should this be a mark instead?
    tagLink: {
        content: 'text*',
        marks: '', // TODO should it accept marks?
        atom: true, // TODO allow this to be editable
        inline: true,
        group: 'inline',
        attrs: {
            tagName: { default: null },
            tagType: { default: 'tag' },
        },
    },
};
const marks = {
    em: {
        parseDOM: [
            { tag: 'i' },
            { tag: 'em' },
            {
                style: 'font-style',
                getAttrs: (value) => value === 'italic' && null,
            },
        ],
        toDOM() {
            return ['em'];
        },
    },
    strong: {
        parseDOM: [
            { tag: 'b' },
            { tag: 'strong' },
            {
                style: 'font-weight',
                //@ts-ignore
                getAttrs: (value) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null,
            },
        ],
        toDOM() {
            return ['strong'];
        },
    },
    link: {
        inclusive: false,
        attrs: {
            href: {},
            title: { default: null },
            referenceType: { default: '' },
            referenceLabel: { default: '' },
        },
        parseDOM: [
            {
                tag: 'a[href]',
                getAttrs(dom) {
                    if (typeof dom === 'string')
                        return false;
                    return {
                        href: dom.getAttribute('href'),
                        title: dom.getAttribute('title'),
                    };
                },
            },
        ],
        toDOM(node) {
            return [
                'a',
                {
                    href: node.attrs.href,
                    title: node.attrs.title,
                    class: 'e11-underline e11-text-blue-600 hover:e11-text-blue-800 visited:e11-text-purple-600',
                },
            ];
        },
    },
    code: {
        exitable: true,
        inclusive: true,
        parseDOM: [{ tag: 'code' }],
        toDOM() {
            return [
                'code',
                {
                    class: 'e11-prose e11-prose-gray e11-bg-gray-200 e11-p-1 e11-rounded-sm',
                },
            ];
        },
    },
    strike: genHtmlInlineMarkSpec({}, 'del', 's', 'strike'),
    kbd: genHtmlInlineMarkSpec({ exitable: true, inclusive: true }, 'kbd'),
    sup: genHtmlInlineMarkSpec({}, 'sup'),
    sub: genHtmlInlineMarkSpec({}, 'sub'),
};
// for *every* mark, add in support for the `markup` attribute
// we use this to save the "original" html tag used to create the mark when converting from html markdown
// this is important because a user could use either `<b>` or `<strong>` to create bold, and we want to preserve this when converting back
Object.values(marks).forEach((node) => {
    const attrs = node.attrs || {};
    attrs.markup = { default: '' };
    node.attrs = attrs;
});
// ditto for nodes
Object.entries(nodes).forEach(([k, node]) => {
    if (k === 'text')
        return;
    const attrs = node.attrs || {};
    attrs.markup = { default: '' };
    node.attrs = attrs;
});
/** The complete schema spec used by the rich-text editor */
const richTextSchemaSpec = {
    nodes,
    marks,
};
/**
 * Creates a generic html NodeSpec for a block html tag
 * @param tag The name of the tag to use in the Prosemirror dom
 */
function genHtmlBlockNodeSpec(tag) {
    return {
        content: 'block+',
        marks: '',
        group: 'block',
        inline: false,
        selectable: true,
        toDOM() {
            return [tag, 0];
        },
        parseDOM: [{ tag }],
    };
}
/**
 * Creates a generic html MarkSpec for an inline html tag
 * @param tag The name of the tag to use in the Prosemirror dom
 */
function genHtmlInlineMarkSpec(attributes, ...tags) {
    return {
        ...attributes,
        toDOM() {
            return [tags[0], 0];
        },
        parseDOM: tags.map((tag) => ({ tag })),
    };
}

// create a static, mini schema for detecting code blocks in clipboard content
const miniSchema = new Schema({
    nodes: {
        doc: richTextSchemaSpec.nodes.doc,
        text: richTextSchemaSpec.nodes.text,
        paragraph: richTextSchemaSpec.nodes.paragraph,
        code_block: richTextSchemaSpec.nodes.code_block,
    },
});
function getHtmlClipboardContent(clipboardData) {
    if (!clipboardData.types.includes('text/html'))
        return null;
    return new globalThis.DOMParser().parseFromString(clipboardData.getData('text/html'), 'text/html');
}
/**
 * Detects if code was pasted into the document and returns the text if true
 * @param clipboardData The clipboardData from the ClipboardEvent
 */
function getDetectedCode(clipboardData, htmlDoc) {
    // if we're loading a whole document, don't false positive if there's more than just code
    const codeEl = htmlDoc?.querySelector('code');
    if (htmlDoc && codeEl) {
        return htmlDoc.body.textContent.trim() !== codeEl.textContent ? null : codeEl.textContent;
    }
    const textContent = clipboardData.getData('text/plain');
    if (!textContent)
        return null;
    // TODO how to reliably detect if a string is code?
    // TODO add more support?
    // check if there's ide specific paste data present
    if (clipboardData.getData('vscode-editor-data')) {
        // TODO parse data for language?
        return textContent;
    }
    // no ide detected, try detecting leading indentation
    // true if any line starts with: 2+ space characters, 1 tab character
    if (/^([ ]{2,}|\t)/m.test(textContent))
        return textContent;
    return null;
}
/**
 * Parses a code string from pasted text, based on multiple heuristics
 * @param clipboardData The ClipboardEvent.clipboardData from the clipboard paste event
 * @param doc Pre-parsed slice, if already available; otherwise the slice will be parsed from the clipboard's html data
 * @internal
 */
function parseCodeFromPasteData(clipboardData, doc) {
    let codeData;
    let htmlContent = null;
    if (!doc) {
        htmlContent = getHtmlClipboardContent(clipboardData);
        if (htmlContent)
            doc = DOMParser.fromSchema(miniSchema).parse(htmlContent);
    }
    // if the schema parser already detected a code block, just use that
    if (doc && doc.content.childCount === 1 && doc.content.child(0).type.name === 'code_block') {
        codeData = doc.content.child(0).textContent;
    }
    else {
        // if not parsed above, parse here - this allows us to only run the parse when it is necessary
        htmlContent ??= getHtmlClipboardContent(clipboardData);
        codeData = getDetectedCode(clipboardData, htmlContent);
    }
    if (!codeData)
        return null;
    // TODO can we do some basic formatting?
    return codeData;
}
/** Plugin for the rich-text editor that auto-detects if code was pasted and handles it specifically */
const richTextCodePasteHandler = new Plugin({
    props: {
        handlePaste(view, event, slice) {
            // if we're pasting into an existing code block, don't bother checking for code
            const schema = view.state.schema;
            const codeblockType = schema.nodes.code_block;
            const currNodeType = view.state.selection.$from.node().type;
            if (currNodeType === codeblockType)
                return false;
            const codeData = parseCodeFromPasteData(event.clipboardData, slice);
            if (!codeData)
                return false;
            const node = codeblockType.createChecked({}, schema.text(codeData));
            view.dispatch(view.state.tr.replaceSelectionWith(node));
            return true;
        },
    },
});
/** Plugin for the commonmark editor that auto-detects if code was pasted and handles it specifically */
const commonmarkCodePasteHandler = new Plugin({
    props: {
        handlePaste(view, event) {
            // unlike the rich-text schema, the commonmark schema doesn't allow code_blocks in the root node
            // so pass in a null slice so the code manually parses instead
            let codeData = parseCodeFromPasteData(event.clipboardData, undefined);
            if (!codeData)
                return false;
            const { $from } = view.state.selection;
            // wrap the code in a markdown code fence
            codeData = `\`\`\`\n${codeData}\n\`\`\`\n`;
            // add a newline if we're not at the beginning of the document
            codeData = ($from.pos === 1 ? '' : '\n') + codeData;
            view.dispatch(view.state.tr.insertText(codeData));
            return true;
        },
    },
});

class StatefulPluginKey extends PluginKey {
    constructor(name) {
        super(name);
    }
    /** @inheritdoc */
    get(state) {
        return super.get(state);
    }
    setMeta(tr, data) {
        return tr.setMeta(this, data);
    }
}
class StatefulPlugin extends Plugin {
    constructor(spec) {
        super(spec);
    }
    get transactionKey() {
        return this.spec.key;
    }
    getMeta(tr) {
        return tr.getMeta(this.transactionKey);
    }
}
class AsyncPluginKey extends StatefulPluginKey {
    constructor(name) {
        super(name);
    }
    /** @inheritdoc */
    setMeta(tr, data) {
        const wrappedData = {
            callbackData: null,
            state: data,
        };
        return tr.setMeta(this, wrappedData);
    }
    setCallbackData(tr, data) {
        const wrappedData = {
            callbackData: data,
            state: null,
        };
        return tr.setMeta(this, wrappedData);
    }
    dispatchCallbackData(view, data) {
        const tr = this.setCallbackData(view.state.tr, data);
        view.updateState(view.state.apply(tr));
        return tr;
    }
}
class AsyncViewHandler {
    constructor(view, transactionKey, callback) {
        this.callback = callback;
        this.transactionKey = transactionKey;
        // go ahead and call this first to initialize the plugin
        this.attachCallback(view, null);
    }
    // on document update, call the callback again
    update(view, prevState) {
        // if the doc didn't change, don't update
        if (view.state.doc.eq(prevState.doc))
            return;
        // attach a new callback to this instance
        this.attachCallback(view, prevState);
    }
    destroy() {
        // do nothing, let the plugin clean itself up
    }
    attachCallback(view, prevState) {
        const promiseId = (this.inProgressPromise = Math.random());
        this.callback(view, prevState)
            .then((data) => {
            // if another promise has been initialized before this one finished, cancel
            if (promiseId !== this.inProgressPromise) {
                log('AsyncViewHandler attachCallback', 'cancelling promise update due to another callback taking its place');
                return;
            }
            this.inProgressPromise = null;
            // let the document know this callback has finished
            this.transactionKey.dispatchCallbackData(view, data);
        })
            // on error, don't dispatch, just clear
            .catch(() => {
            this.inProgressPromise = null;
        });
    }
}
/**
 * Shortcut wrapper for a plugin with async functionality;
 * Overrides the spec's `view` property to manually handle async functionality
 */
class AsyncPlugin extends StatefulPlugin {
    constructor(spec) {
        spec.view = (view) => {
            return new AsyncViewHandler(view, spec.key, spec.asyncCallback);
        };
        // @ts-ignore
        super(spec);
    }
    /** @inheritdoc */
    getMeta(tr) {
        const metadata = tr.getMeta(this.transactionKey);
        return metadata?.state;
    }
    getCallbackData(tr) {
        const metadata = tr.getMeta(this.transactionKey);
        return metadata?.callbackData;
    }
}

/**
 * The central plugin key where all show/hideInterface requests come into.
 * This should *never* be exported. Any functionality that wants to use this must inherit from
 * @see {@link ManagedInterfaceKey} and use its show/hideInterface methods.
 */
class MainInterfaceManagerKey extends StatefulPluginKey {
    constructor() {
        super('interface-manager');
    }
    /**
     * Shows the interface for the plugin referenced by the passed key
     * @param view The current editor view
     * @param toShow The key of the plugin to show
     * @param data Any data to attach to the transaction's metadata
     * @returns true if the interface was shown, false if there was no state change
     */
    showInterfaceTr(viewState, toShow, data) {
        // even though the TS types forbid this, it could still be passed in
        if (data && 'shouldShow' in data)
            delete data.shouldShow;
        const state = {
            ...toShow.getState(viewState),
            ...data,
        };
        // check validity
        if (!this.checkIfValid(state, true))
            return null;
        // hide existing
        let tr = this.hideCurrentInterfaceTr(viewState) || viewState.tr;
        // dispatch cancelable event and return early if canceled
        if (this.dispatchCancelableEvent(viewState, `${toShow.name}-show`, state))
            return null;
        // dispatch transaction for managed plugin
        tr = toShow.setMeta(tr, {
            ...state,
            shouldShow: true,
        });
        // set metadata for this plugin
        const { containerGetter, dom } = this.getState(viewState);
        tr = this.setMeta(tr, {
            dom,
            currentlyShown: toShow,
            containerGetter,
        });
        return tr;
    }
    /**
     * Hides the interface for the plugin referenced by the passed key
     * @param view The current editor view
     * @param toHide The key of the plugin to show
     * @param data Any data to attach to the transaction's metadata
     * @returns true if the interface was hidden, false if there was no state change
     */
    hideInterfaceTr(viewState, toHide, data) {
        // even though the TS types forbid this, it could still be passed in
        if (data && 'shouldShow' in data) {
            delete data.shouldShow;
        }
        const state = {
            ...toHide.getState(viewState),
            ...data,
        };
        // check validity
        if (!this.checkIfValid(state, false))
            return null;
        // dispatch cancelable event and return early if canceled
        if (this.dispatchCancelableEvent(viewState, `${toHide.name}-hide`, state))
            return null;
        // dispatch transaction for managed plugin
        let tr = viewState.tr;
        tr = toHide.setMeta(tr, {
            ...state,
            shouldShow: false,
        });
        // set metadata for this plugin
        const { containerGetter, dom } = this.getState(viewState);
        tr = this.setMeta(tr, {
            dom,
            currentlyShown: null,
            containerGetter,
        });
        return tr;
    }
    hideCurrentInterfaceTr(viewState) {
        const { currentlyShown } = this.getState(viewState);
        if (!currentlyShown)
            return null;
        return this.hideInterfaceTr(viewState, currentlyShown, {
            shouldShow: false,
        });
    }
    /**
     * Checks if the the requested state change is valid
     * @param state The current plugin state
     * @param checkingIsShown Whether the state is being checked for being shown
     */
    checkIfValid(state, checkingIsShown) {
        // check if the state we're expecting is set
        if (checkingIsShown) {
            // already visible, don't dispatch the event
            return !('shouldShow' in state) || !state.shouldShow;
        }
        else {
            // already hidden, don't dispatch the event
            return state.shouldShow;
        }
    }
    /**
     * Dispatches an event to the editor view's DOM that can be canceled
     * @param state The current editor state
     * @param eventName The unprefixed name of the event to dispatch
     * @param data The current plugin state
     */
    dispatchCancelableEvent(state, eventName, data) {
        const dom = this.getState(state).dom;
        return !dispatchEditorEvent(dom, eventName, data);
    }
}
/** Singleton instance of @see {@link MainInterfaceManagerKey} */
const MAIN_INTERFACE_MANAGER_KEY = new MainInterfaceManagerKey();
/**
 * Public PluginKey implementation for plugins that want to expose an interface.
 * Contains helper methods for showing/hiding the interface that ensure that the manager
 * handles the state changes across all interface-enabled plugins.
 */
class ManagedInterfaceKey extends StatefulPluginKey {
    constructor(name) {
        super(name);
        this.name = name;
    }
    /**
     * Gets the container element that the plugin's interface should be rendered into
     * @param view The current editor view
     */
    getContainer(view) {
        return MAIN_INTERFACE_MANAGER_KEY.getState(view.state).containerGetter(view);
    }
    /**
     * Shows the interface for this plugin, optionally overriding the metadata passed to the transaction
     * @param view The current editor view
     * @param data Optional data to attach to the transaction's metadata
     * @returns True if the interface was shown, false if there was no state change
     */
    showInterfaceTr(state, data) {
        return MAIN_INTERFACE_MANAGER_KEY.showInterfaceTr(state, this, data);
    }
    /**
     * Hides the interface for this plugin, optionally overriding the metadata passed to the transaction
     * @param view The current editor view
     * @param data Optional data to attach to the transaction's metadata
     * @returns True if the interface was hidden, false if there was no state change
     */
    hideInterfaceTr(state, data) {
        return MAIN_INTERFACE_MANAGER_KEY.hideInterfaceTr(state, this, data);
    }
}
/**
 * Main plugin for coordinating the use of the interface container for all interface-enabled plugins.
 * This plugin is *required* for any interface-enabled plugin to work. This plugin also adds a listener to hide the interface
 * if the ESC key is pressed or if the text editor gains focus.
 * @param containerGetter The method for getting the container element for the interface; falls back to the editor view's DOM's parentElement if not provided
 */
function interfaceManagerPlugin(containerGetter) {
    containerGetter =
        containerGetter ||
            function (view) {
                return view.dom.parentElement;
            };
    return new StatefulPlugin({
        key: MAIN_INTERFACE_MANAGER_KEY,
        state: {
            init: () => ({
                dom: null,
                currentlyShown: null,
                containerGetter,
            }),
            apply(tr, value) {
                return {
                    ...value,
                    ...this.getMeta(tr),
                };
            },
        },
        props: {
            handleKeyDown: (view, event) => {
                // if the ESC key is pressed, then hide the interface
                if (event.key === 'Escape') {
                    const tr = MAIN_INTERFACE_MANAGER_KEY.hideCurrentInterfaceTr(view.state);
                    if (tr)
                        view.dispatch(tr);
                }
                // don't stop the event from propagating
                return false;
            },
            handleClick(view) {
                // if the editor is clicked, then hide the interface
                const tr = MAIN_INTERFACE_MANAGER_KEY.hideCurrentInterfaceTr(view.state);
                if (tr)
                    view.dispatch(tr);
                return false;
            },
        },
        view: (editorView) => {
            editorView.dispatch(MAIN_INTERFACE_MANAGER_KEY.setMeta(editorView.state.tr, {
                dom: editorView.dom,
                currentlyShown: null,
                containerGetter,
            }));
            return {};
        },
    });
}
/**
 * PluginView that ensures that the build/destroyInterface methods are called only at the appropriate times.
 * **WARNING**: The plugin that uses this must also ensure that it forwards the `shouldShow` value from any
 * apply transaction's metadata to the plugin's state. Failing to do so will result in the interface never being shown.
 */
class PluginInterfaceView {
    constructor(key) {
        this.key = key;
        this.isShown = false;
    }
    /**
     * Pre-implemented update override that ensures that the interface is shown/hidden appropriately.
     * If you need to do additional work, you should override this method, making sure to call `super.update(view)`
     * in the overridden method _after_ the additional setup has been done.
     * @param view The current editor view
     */
    update(view) {
        const { shouldShow } = this.key.getState(view.state);
        // only show/hide if the state has changed
        if (this.isShown && !shouldShow) {
            // hide the interface
            this.destroyInterface(this.key.getContainer(view));
            this.isShown = false;
        }
        else if (!this.isShown && shouldShow) {
            // show the interface
            this.buildInterface(this.key.getContainer(view));
            this.isShown = true;
        }
    }
    /** Helper wrapper around this key's @see {@link ManagedInterfaceKey.showInterface} */
    tryShowInterfaceTr(state, data) {
        return this.key.showInterfaceTr(state, data);
    }
    /** Helper wrapper around this key's @see {@link ManagedInterfaceKey.hideInterface} */
    tryHideInterfaceTr(state, data) {
        return this.key.hideInterfaceTr(state, data);
    }
}

/**
 * Default image upload callback that posts to `/image/upload`,
 * expecting a json response like `{ UploadedImage: "https://www.example.com/path/to/file" }`
 * and returns `UploadedImage`'s value
 * @param file The file to upload
 */
async function defaultImageUploadHandler(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('/image/upload', {
        method: 'POST',
        cache: 'no-cache',
        body: formData,
    });
    if (!response.ok) {
        throw new Error(`Failed to upload image: ${response.status} - ${response.statusText}`);
    }
    const json = (await response.json());
    return json.UploadedImage;
}
var ValidationResult$1;
(function (ValidationResult) {
    ValidationResult[ValidationResult["Ok"] = 0] = "Ok";
    ValidationResult[ValidationResult["FileTooLarge"] = 1] = "FileTooLarge";
    ValidationResult[ValidationResult["InvalidFileType"] = 2] = "InvalidFileType";
})(ValidationResult$1 || (ValidationResult$1 = {}));
class ImageUploader extends PluginInterfaceView {
    constructor(view, uploadOptions, validateLink, addTransactionDispatcher) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        super(INTERFACE_KEY);
        this.image = null;
        const randomId = generateRandomId();
        this.isVisible = false;
        this.uploadOptions = uploadOptions;
        this.validateLink = validateLink;
        this.addTransactionDispatcher = addTransactionDispatcher;
        this.uploadContainer = document.createElement('div');
        this.uploadContainer.className = 'mt6 bt bb bc-black-400 js-image-uploader';
        this.uploadField = document.createElement('input');
        this.uploadField.type = 'file';
        this.uploadField.className = 'js-image-uploader-input v-visible-sr';
        this.uploadField.accept = 'image/*';
        this.uploadField.multiple = false;
        this.uploadField.id = `fileUpload${randomId}`;
        // TODO i18n
        this.uploadContainer.innerHTML = escapeHTML `
            <div class="fs-body2 p12 pb0 js-cta-container">
                <label for="${this.uploadField.id}" class="d-inline-flex f:outline-ring s-link js-browse-button" role="button" aria-controls="image-preview-${randomId}">
                    Browse
                </label>, drag & drop<span class="js-external-url-trigger-container d-none">, <button type="button" class="s-btn s-btn__link js-external-url-trigger">enter a link</button></span>, or paste an image <span class="fc-light fs-caption">Max size 2 MiB</span>
            </div>

            <div class="js-external-url-input-container p12 d-none">
                <div class="d-flex fd-row ai-center sm:fd-column sm:ai-start">
                    <label class="d-block s-label ws-nowrap mr4" for="external-url-input-${randomId}">
                        External url
                    </label>
                    <input id="external-url-input-${randomId}" type="text" class="s-input js-external-url-input" placeholder="https://example.com/img.png" />
                </div>
            </div>

            <div id="image-preview-${randomId}" class="js-image-preview wmx100 pt12 px12 d-none"></div>
            <aside class="s-notice s-notice__warning d-none m8 js-validation-message" role="status" aria-hidden="true"></aside>

            <div class="d-flex jc-space-between ai-center p12 sm:fd-column sm:ai-start sm:g16">
                <div>
                    <button class="s-btn s-btn__primary ws-nowrap mr8 js-add-image" type="button" disabled>Add image</button>
                    <button class="s-btn ws-nowrap js-cancel-button" type="button">Cancel</button>
                </div>
                <div class="d-flex fd-column fs-caption fc-black-300 s-anchors s-anchors__muted">
                    <div class="js-branding-html"></div>
                    <div class="js-content-policy-html"></div>
                </div>
            </div>
        `;
        // add in the uploadField right after the first child element
        this.uploadContainer.querySelector('.js-browse-button').appendChild(this.uploadField);
        // XSS "safe": this html is passed in via the editor options; it is not our job to sanitize it
        this.uploadContainer.querySelector('.js-branding-html').innerHTML = this.uploadOptions?.brandingHtml;
        // XSS "safe": this html is passed in via the editor options; it is not our job to sanitize it
        this.uploadContainer.querySelector('.js-content-policy-html').innerHTML = this.uploadOptions?.contentPolicyHtml;
        this.uploadField.addEventListener('change', () => {
            this.handleFileSelection(view);
        });
        this.uploadContainer.addEventListener('dragenter', this.highlightDropArea.bind(this));
        this.uploadContainer.addEventListener('dragover', this.highlightDropArea.bind(this));
        // we need this handler on top of the plugin's handleDrop() to make
        // sure we're handling drop events on the upload container itself properly
        this.uploadContainer.addEventListener('drop', (event) => {
            this.unhighlightDropArea(event);
            this.handleDrop(event, view);
        });
        // we need this handler on top of the plugin's handlePaste() to make
        // sure we're handling paste events on the upload container itself properly
        this.uploadContainer.addEventListener('paste', (event) => {
            this.handlePaste(event, view);
        });
        this.uploadContainer.addEventListener('dragleave', this.unhighlightDropArea.bind(this));
        this.uploadContainer.querySelector('.js-cancel-button').addEventListener('click', () => {
            const tr = this.tryHideInterfaceTr(view.state);
            if (tr)
                view.dispatch(tr);
        });
        this.uploadContainer.querySelector('.js-add-image').addEventListener('click', (e) => this.handleUploadTrigger(e, this.image, view));
        if (this.uploadOptions.allowExternalUrls) {
            this.uploadContainer.querySelector('.js-external-url-trigger-container').classList.remove('d-none');
            this.uploadContainer.querySelector('.js-external-url-trigger').addEventListener('click', () => {
                this.toggleExternalUrlInput(true);
            });
            this.uploadContainer.querySelector('.js-external-url-input').addEventListener('input', (e) => {
                this.validateExternalUrl(e.target.value);
            });
        }
    }
    highlightDropArea(event) {
        this.uploadContainer.classList.add('bs-ring');
        this.uploadContainer.classList.add('bc-blue-300');
        event.preventDefault();
        event.stopPropagation();
    }
    unhighlightDropArea(event) {
        this.uploadContainer.classList.remove('bs-ring');
        this.uploadContainer.classList.remove('bc-blue-300');
        event.preventDefault();
        event.stopPropagation();
    }
    handleFileSelection(view) {
        this.resetImagePreview();
        const files = this.uploadField.files;
        if (view.state.selection.$from.parent.inlineContent && files?.length)
            void this.showImagePreview(files[0]);
    }
    handleDrop(event, view) {
        this.resetImagePreview();
        const files = event.dataTransfer.files;
        if (view.state.selection.$from.parent.inlineContent && files.length)
            void this.showImagePreview(files[0]);
    }
    handlePaste(event, view) {
        this.resetImagePreview();
        const files = event.clipboardData.files;
        if (view.state.selection.$from.parent.inlineContent && files.length)
            void this.showImagePreview(files[0]);
    }
    validateImage(image) {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        const sizeLimit = 0x200000; // 2 MiB
        if (!validTypes.includes(image.type))
            return ValidationResult$1.InvalidFileType;
        if (image.size >= sizeLimit)
            return ValidationResult$1.FileTooLarge;
        return ValidationResult$1.Ok;
    }
    showValidationError(errorMessage, level = 'warning') {
        this.uploadField.value = '';
        const validationElement = this.uploadContainer.querySelector('.js-validation-message');
        if (level === 'warning') {
            validationElement.classList.remove('s-notice__danger');
            validationElement.classList.add('s-notice__warning');
        }
        else {
            validationElement.classList.remove('s-notice__warning');
            validationElement.classList.add('s-notice__danger');
        }
        validationElement.classList.remove('d-none');
        validationElement.textContent = errorMessage;
    }
    hideValidationError() {
        const validationElement = this.uploadContainer.querySelector('.js-validation-message');
        validationElement.classList.add('d-none');
        validationElement.classList.remove('s-notice__warning');
        validationElement.classList.remove('s-notice__danger');
        validationElement.innerHTML = '';
    }
    showImagePreview(file) {
        const promise = new Promise((resolve, reject) => this.showImagePreviewAsync(file, resolve, reject));
        return promise;
    }
    showImagePreviewAsync(file, resolve, reject) {
        const previewElement = this.uploadContainer.querySelector('.js-image-preview');
        const addImageButton = this.uploadContainer.querySelector('.js-add-image');
        this.hideValidationError();
        const validationResult = this.validateImage(file);
        switch (validationResult) {
            case ValidationResult$1.FileTooLarge:
                this.showValidationError(_t('image_upload.upload_error_file_too_big'));
                reject('file too large');
                return;
            case ValidationResult$1.InvalidFileType:
                this.showValidationError(_t('image_upload.upload_error_unsupported_format'));
                reject('invalid filetype');
                return;
        }
        this.resetImagePreview();
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const image = new Image();
            image.className = 'hmx1 w-auto';
            image.title = file.name;
            image.src = reader.result;
            image.alt = _t('image_upload.uploaded_image_preview_alt');
            previewElement.appendChild(image);
            previewElement.classList.remove('d-none');
            this.image = file;
            addImageButton.disabled = false;
            resolve();
        }, false);
        reader.readAsDataURL(file);
    }
    toggleExternalUrlInput(show) {
        const cta = this.uploadContainer.querySelector('.js-cta-container');
        const container = this.uploadContainer.querySelector('.js-external-url-input-container');
        cta.classList.toggle('d-none', show);
        container.classList.toggle('d-none', !show);
        container.querySelector('.js-external-url-input').value = '';
    }
    validateExternalUrl(url) {
        this.resetImagePreview();
        const addImageButton = this.uploadContainer.querySelector('.js-add-image');
        if (!this.validateLink(url)) {
            this.showValidationError(_t('image_upload.external_url_validation_error'), 'danger');
            addImageButton.disabled = true;
        }
        else {
            this.hideValidationError();
            addImageButton.disabled = false;
        }
    }
    resetImagePreview() {
        this.uploadContainer.querySelector('.js-image-preview').innerHTML = '';
        this.image = null;
        this.uploadContainer.querySelector('.js-add-image').disabled = true;
    }
    resetUploader() {
        this.resetImagePreview();
        this.toggleExternalUrlInput(false);
        this.hideValidationError();
        this.uploadField.value = '';
    }
    handleUploadTrigger(_event, file, view) {
        const externalUrl = this.uploadContainer.querySelector('.js-external-url-input').value;
        const urlIsValue = externalUrl && this.validateLink(externalUrl);
        if (!file && !urlIsValue)
            return;
        void this.startImageUpload(view, file || externalUrl);
        this.resetUploader();
        const tr = this.tryHideInterfaceTr(view.state);
        if (tr)
            view.dispatch(tr);
        view.focus();
    }
    startImageUpload(view, file) {
        // A fresh object to act as the ID for this upload
        const id = {};
        // Replace the selection with a placeholder
        const tr = view.state.tr;
        if (!tr.selection.empty)
            tr.deleteSelection();
        this.key.setMeta(tr, {
            add: { id, pos: tr.selection.from },
            // explicitly clear out any pasted/dropped file on upload
            file: null,
            shouldShow: false,
        });
        view.dispatch(tr);
        if (!this.uploadOptions?.handler) {
            // purposefully log an error to the dev console
            // don't use our internal `log` implementation, it only logs on dev builds
            // eslint-disable-next-line no-console
            console.error("No upload handler registered. Ensure you set a proper handler on the editor's options.imageUploadHandler");
            return new Promise(() => { });
        }
        return this.uploadOptions.handler(file).then((url) => {
            // ON SUCCESS
            // find where we inserted our placeholder so the content insert knows where to go
            const decos = this.key.getState(view.state).decorations;
            const found = decos.find(undefined, undefined, 
            // eslint-disable-next-line eqeqeq
            (spec) => spec.id == id);
            const pos = found.length ? found[0].from : null;
            // If the content around the placeholder has been deleted, drop the image
            if (pos === null)
                return;
            // get the transaction from the dispatcher
            let tr = this.addTransactionDispatcher(view.state, url, pos);
            // let the plugin know it can remove the upload decoration
            tr = this.key.setMeta(tr, {
                remove: { id },
                shouldShow: false,
                file: null,
            });
            view.dispatch(tr);
        }, () => {
            // ON ERROR
            // reshow the image uploader along with an error message
            let tr = this.tryShowInterfaceTr(view.state) || view.state.tr;
            // let the plugin know it can remove the upload decoration
            tr = this.key.setMeta(tr, {
                remove: { id },
                shouldShow: false,
                file: null,
            });
            view.dispatch(tr);
            this.showValidationError(_t('image_upload.upload_error_generic'), 'error');
        });
    }
    update(view) {
        const state = this.key.getState(view.state);
        this.image = state?.file || this.image;
        super.update(view);
    }
    destroy() {
        this.uploadField.remove();
        this.uploadContainer.remove();
        this.image = null;
    }
    buildInterface(container) {
        if (this.image)
            void this.showImagePreview(this.image);
        // add the upload container to the menu area
        container.appendChild(this.uploadContainer);
        this.uploadContainer.querySelector('.js-image-uploader-input').focus();
    }
    destroyInterface(container) {
        this.resetUploader();
        this.uploadContainer.classList.remove('outline-ring');
        container.removeChild(this.uploadContainer);
    }
}
/**
 * Hides the image uploader
 * @param view The current editor view
 */
function hideImageUploader(view) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const tr = INTERFACE_KEY.hideInterfaceTr(view.state, {
        file: null,
    });
    if (tr)
        view.dispatch(tr);
}
/** Shows the image uploader
 * @param view The current editor view
 * @param file The file to upload
 */
function showImageUploader(view, file) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const tr = INTERFACE_KEY.showInterfaceTr(view.state, {
        file: file || null,
    });
    if (tr)
        view.dispatch(tr);
}
/**
 * Checks if the image-upload functionality is enabled
 * @param state The current editor state
 */
function imageUploaderEnabled(state) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const pluginState = INTERFACE_KEY.getState(state);
    return !!pluginState;
}
/**
 * Creates a placeholder decoration to indicate to the user that the image is currently uploading;
 * Gets replaced with the actual image markup on upload completion
 */
function createPlaceholder() {
    const placeholder = document.createElement('div');
    placeholder.className = 'ws-normal d-block m8';
    placeholder.innerHTML = `
<div class="py6 px6 bg-black-050 bar-sm gsx gs8 d-inline-flex ai-center fw-normal fs-body1">
    <span class="s-spinner s-spinner__sm flex--item">
        <span class="v-visible-sr">Loading…</span>
    </span>
    <span class="flex--item">Uploading image…</span>
</div>
`;
    return placeholder;
}
/**
 * Adds image uploading capabilities to the editor.
 * With this plugin, you'll be able to show a popover that allows you to
 * browse for files on your file system, or use drag & drop to select images
 * to upload.
 *
 * On upload, this plugin will call the provided uploadHandler function .
 * @see defaultImageUploadHandler for an example
 *
 * @param uploadHandler A function handling file uploads. Will receive the file to upload
 * as the `file` parameter and needs to return a resolved promise with the URL of the uploaded file
 * @param containerFn A function that returns the container to insert the plugin's UI into
 * @param addTransactionDispatcher Dispatcher function that generates a transaction to dispatch to the view on image add
 */
function imageUploaderPlaceholderPlugin(uploadOptions, validateLink, addTransactionDispatcher) {
    // if the required image upload options are missing, don't enable the plugin at all
    if (!uploadOptions?.handler)
        return new Plugin({});
    return new StatefulPlugin({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        key: INTERFACE_KEY,
        state: {
            init() {
                return {
                    decorations: DecorationSet.empty,
                    file: null,
                    shouldShow: false,
                };
            },
            apply(tr, state) {
                let set = state.decorations || DecorationSet.empty;
                // Adjust decoration positions to changes made by the transaction
                set = set.map(tr.mapping, tr.doc);
                const metadata = this.getMeta(tr);
                const returnValue = {
                    file: state.file,
                    decorations: set,
                    shouldShow: state.shouldShow,
                };
                // if no metadata was set, do not alter this state further
                if (!metadata)
                    return returnValue;
                if ('file' in metadata)
                    returnValue.file = metadata.file;
                else
                    returnValue.file = null;
                if ('shouldShow' in metadata)
                    returnValue.shouldShow = metadata.shouldShow;
                // See if the transaction adds or removes any placeholders
                if (metadata.add) {
                    const deco = Decoration.widget(metadata.add.pos, createPlaceholder(), {
                        id: metadata.add.id,
                    });
                    returnValue.decorations = set.add(tr.doc, [deco]);
                }
                else if (metadata.remove) {
                    returnValue.decorations = set.remove(set.find(undefined, undefined, 
                    // eslint-disable-next-line eqeqeq
                    (spec) => spec.id == metadata.remove.id));
                }
                return returnValue;
            },
        },
        props: {
            decorations(state) {
                return this.getState(state).decorations;
            },
            handleDrop(view, event) {
                const files = event.dataTransfer.files;
                if (view.state.selection.$from.parent.inlineContent && files.length) {
                    showImageUploader(view, files[0]);
                    return true;
                }
                return false;
            },
            handlePaste(view, event) {
                const files = event.clipboardData.files;
                if (view.state.selection.$from.parent.inlineContent && files.length) {
                    showImageUploader(view, files[0]);
                    return true;
                }
                return false;
            },
        },
        view(editorView) {
            return new ImageUploader(editorView, uploadOptions, validateLink, addTransactionDispatcher);
        },
    });
}
/** Singleton instance of the plugin key for exported show/hide methods to reference */
const INTERFACE_KEY = new ManagedInterfaceKey('image-uploader');
/**
 * Adds image uploading capabilities to the editor.
 * With this plugin, you'll be able to show a popover that allows you to
 * browse for files on your file system, or use drag & drop to select images
 * to upload.
 *
 * On upload, this plugin will call the provided uploadOptions.handler function
 * @see defaultImageUploadHandler for an example
 *
 * @param uploadOptions The imageUpload options
 * @param containerFn A function that returns the container to insert the plugin's UI into
 */
function richTextImageUpload(uploadOptions, validateLink, schema) {
    return imageUploaderPlaceholderPlugin(uploadOptions, validateLink, (state, url, pos) => {
        const defaultAltText = _t('image_upload.default_image_alt_text');
        const marks = uploadOptions.wrapImagesInLinks || uploadOptions.embedImagesAsLinks ? [schema.marks.link.create({ href: url })] : null;
        const imgNode = uploadOptions.embedImagesAsLinks
            ? schema.text(defaultAltText, marks)
            : schema.nodes.image.create({ src: url, alt: defaultAltText }, null, marks);
        return state.tr.replaceWith(pos, pos, imgNode);
    });
}
// TODO markdown upload decoration doesn't really fit in visually, make it more... ascii art-ish?
/**
 * Adds image uploading capabilities to the editor.
 * With this plugin, you'll be able to show a popover that allows you to
 * browse for files on your file system, or use drag & drop to select images
 * to upload.
 *
 * On upload, this plugin will call the provided uploadOptions.handler function
 * @see defaultImageUploadHandler for an example
 *
 * @param uploadHandler The imageUpload options
 * @param containerFn A function that returns the container to insert the plugin's UI into
 */
function commonmarkImageUpload(uploadOptions, validateLink) {
    return imageUploaderPlaceholderPlugin(uploadOptions, validateLink, (state, url, pos) => {
        const defaultAltText = _t('image_upload.default_image_alt_text');
        // construct the raw markdown
        let mdString = `![${defaultAltText}](${url})`;
        let selectionStart = pos + 2;
        let selectionEnd = selectionStart + defaultAltText.length;
        if (uploadOptions.embedImagesAsLinks) {
            // strip off the leading `!`
            mdString = mdString.slice(1);
            selectionStart -= 1;
            selectionEnd -= 1;
        }
        else if (uploadOptions.wrapImagesInLinks) {
            mdString = `[${mdString}](${url})`;
            selectionStart += 1;
            selectionEnd += 1;
        }
        // insert into the document
        const tr = state.tr.insertText(mdString, pos);
        // pre-select the alt text so the user can just start typing after insert
        // NOTE: these are not magic numbers, just hardcoded indexes for the above string
        tr.setSelection(TextSelection.create(state.apply(tr).doc, selectionStart, selectionEnd));
        return tr;
    });
}

/**
 * Creates a placeholder decoration on the document's first child
 * @param doc The root document node
 * @param placeholder The placeholder text
 */
function createPlaceholderDecoration(doc, placeholder) {
    // TODO check for image upload placeholder
    const showPlaceholder = !doc.textContent && doc.childCount === 1 && doc.firstChild?.childCount === 0;
    if (!showPlaceholder)
        return DecorationSet.empty;
    const $pos = doc.resolve(1);
    return DecorationSet.create(doc, [
        Decoration.node($pos.before(), $pos.after(), {
            'data-placeholder': placeholder,
        }),
    ]);
}
/** Plugin that adds placeholder text to the editor when it's empty */
function placeholderPlugin(placeholder) {
    if (!placeholder?.trim())
        return new Plugin({});
    return new Plugin({
        key: new PluginKey('placeholder'),
        state: {
            init: (_, state) => createPlaceholderDecoration(state.doc, placeholder),
            apply: (tr, value) => {
                if (!tr.docChanged)
                    return value.map(tr.mapping, tr.doc);
                return createPlaceholderDecoration(tr.doc, placeholder);
            },
        },
        props: {
            decorations(state) {
                return this.getState(state);
            },
        },
        view(view) {
            view.dom.setAttribute('aria-placeholder', placeholder);
            return {
                destroy() {
                    view.dom.removeAttribute('aria-placeholder');
                },
            };
        },
    });
}

// TODO document
const READ_ONLY_KEY = new PluginKey(readonlyPlugin.name);
// TODO document
function editableCheck(state) {
    return !READ_ONLY_KEY.getState(state);
}
/**
 * Toggles the stored `readonly` plugin state
 * @param isReadonly Whether to toggle readonly on or not
 */
function toggleReadonly(isReadonly, state, dispatch) {
    const isCurrentlyRO = READ_ONLY_KEY.getState(state);
    // if the state already matches the expected result, return
    if (isCurrentlyRO === isReadonly)
        return false;
    let tr = state.tr.setMeta(READ_ONLY_KEY, isReadonly);
    tr = tr.setMeta('addToHistory', false);
    if (dispatch)
        dispatch(tr);
    return true;
}
// TODO document
function readonlyPlugin() {
    return new Plugin({
        key: READ_ONLY_KEY,
        state: {
            init() {
                return false;
            },
            apply(tr, value) {
                const meta = tr.getMeta(READ_ONLY_KEY);
                if (typeof meta === 'undefined')
                    return value;
                return meta;
            },
        },
    });
}

/** Parses out a Prosemirror document from a code (markdown) string */
class CodeStringParser extends DOMParser {
    parseCode(content, options) {
        const node = document.createElement('div');
        node.innerHTML = escapeHTML `<pre>${content}</pre>`;
        return super.parse(node, options);
    }
    static fromSchema(schema) {
        return (schema.cached.domParser || (schema.cached.domParser = new CodeStringParser(schema, CodeStringParser.schemaRules(schema))));
    }
    static toString(node) {
        return node.textBetween(0, node.content.size, '\n\n');
    }
}

/** Plugin that restores browser default triple-click handling behavior */
const tripleClickHandler = new Plugin({
    props: {
        handleDOMEvents: {
            mousedown(view, event) {
                const { $from, $to } = view.state.selection;
                // triple clicks only (detail is set to number of clicks for mousedown events)
                const isTripleClick = event.detail === 3;
                // only clicks where the selection doesn't span across nodes
                const notSpanningNodes = $from.sameParent($to);
                return isTripleClick && notSpanningNodes;
            },
        },
    },
});

function addHardbreakMarkup(tokens, parent = null) {
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.children)
            addHardbreakMarkup(token.children, token);
        if (token.type !== 'hardbreak')
            continue;
        if (/\s\s\n/.test(parent?.content))
            token.markup = '  \n';
        else if (/\\\n/.test(parent?.content))
            token.markup = '\\\n';
    }
}
/**
 * Adds markup to differentiate between doublespace/backslash hardbreaks
 * TODO UPSTREAM
 */
function hardbreak_markup(md) {
    md.core.ruler.push('hardbreak-markup', (state) => {
        addHardbreakMarkup(state.tokens);
        return false;
    });
}

/**
 * Describes the supported html tags
 * @see {@link https://meta.stackexchange.com/questions/1777/what-html-tags-are-allowed-on-stack-exchange-sites|Supported tags}
 */
var TagType;
(function (TagType) {
    // Uncategorized
    TagType[TagType["unknown"] = 0] = "unknown";
    TagType[TagType["comment"] = 1] = "comment";
    // Inline items
    TagType[TagType["strike"] = 2] = "strike";
    TagType[TagType["strong"] = 3] = "strong";
    TagType[TagType["emphasis"] = 4] = "emphasis";
    TagType[TagType["hardbreak"] = 5] = "hardbreak";
    TagType[TagType["code"] = 6] = "code";
    TagType[TagType["link"] = 7] = "link";
    TagType[TagType["image"] = 8] = "image";
    TagType[TagType["keyboard"] = 9] = "keyboard";
    TagType[TagType["pre"] = 10] = "pre";
    TagType[TagType["sup"] = 11] = "sup";
    TagType[TagType["sub"] = 12] = "sub";
    // Block items
    TagType[TagType["heading"] = 13] = "heading";
    TagType[TagType["paragraph"] = 14] = "paragraph";
    TagType[TagType["horizontal_rule"] = 15] = "horizontal_rule";
    TagType[TagType["blockquote"] = 16] = "blockquote";
    TagType[TagType["list_item"] = 17] = "list_item";
    TagType[TagType["ordered_list"] = 18] = "ordered_list";
    TagType[TagType["unordered_list"] = 19] = "unordered_list";
    // Description lists
    TagType[TagType["description_details"] = 20] = "description_details";
    TagType[TagType["description_list"] = 21] = "description_list";
    TagType[TagType["description_term"] = 22] = "description_term";
})(TagType || (TagType = {}));
/**
 * Describes the supported attributes for each html tag
 * @see {@link https://meta.stackexchange.com/questions/1777/what-html-tags-are-allowed-on-stack-exchange-sites|Supported tags}
 */
const supportedTagAttributes = {
    [TagType.link]: ['href', 'title'],
    [TagType.image]: ['alt', 'height', 'src', 'title', 'width'],
};
/**
 * Collection of elements that are counted as "block" level elements
 * TODO change to a map for fast lookup?
 */
const blockElements = [
    TagType.blockquote,
    TagType.heading,
    TagType.list_item,
    TagType.ordered_list,
    TagType.unordered_list,
    TagType.description_details,
    TagType.description_list,
    TagType.description_term,
    TagType.paragraph,
    TagType.horizontal_rule,
    TagType.pre,
];
/**
 * Collection of elements that are self-closing (e.g. <br/>)
 * TODO change to a map for fast lookup?
 */
const selfClosingElements = [TagType.hardbreak, TagType.image, TagType.horizontal_rule];

/**
 * Detects what supported type this tag is
 * @param tag The raw html tag to categorize eg <strong>, </strong>, <br>, <br />
 * @see {@link https://meta.stackexchange.com/questions/1777/what-html-tags-are-allowed-on-stack-exchange-sites|Supported tags}
 */
function getTagInfo(tag) {
    if (!tag) {
        return {
            type: TagType.unknown,
            isSelfClosing: false,
            isClosing: false,
            isBlock: false,
            tagName: null,
            attributes: {},
            markup: null,
        };
    }
    let tagType = TagType.unknown;
    // check if this tag looks like `</div>` or `</div malformed>`
    const isClosingTag = /^<\/\S+?.*?>$/.test(tag);
    // strip away all html characters and potential attributes
    const tagName = tag.replace(/[<>/]/g, '').trim().split(/\s/)[0];
    if (['del', 'strike', 's'].includes(tagName)) {
        tagType = TagType.strike;
    }
    else if (['b', 'strong'].includes(tagName)) {
        tagType = TagType.strong;
    }
    else if (['em', 'i'].includes(tagName)) {
        tagType = TagType.emphasis;
    }
    else if (tagName === 'code') {
        tagType = TagType.code;
    }
    else if (tagName === 'br') {
        tagType = TagType.hardbreak;
    }
    else if (tagName === 'blockquote') {
        tagType = TagType.blockquote;
    }
    else if (tagName === 'a') {
        tagType = TagType.link;
    }
    else if (tagName === 'img') {
        tagType = TagType.image;
    }
    else if (/h[1,2,3,4,5,6]/.test(tagName)) {
        // NOTE: no need to set the level, the default `heading` generates this from the `tag` property
        tagType = TagType.heading;
    }
    else if (tagName === 'kbd') {
        tagType = TagType.keyboard;
    }
    else if (tagName === 'pre') {
        tagType = TagType.pre;
    }
    else if (tagName === 'sup') {
        tagType = TagType.sup;
    }
    else if (tagName === 'sub') {
        tagType = TagType.sub;
    }
    else if (tagName === 'ul') {
        tagType = TagType.unordered_list;
    }
    else if (tagName === 'ol') {
        tagType = TagType.ordered_list;
    }
    else if (tagName === 'li') {
        tagType = TagType.list_item;
    }
    else if (tagName === 'p') {
        tagType = TagType.paragraph;
    }
    else if (tagName === 'hr') {
        tagType = TagType.horizontal_rule;
    }
    else if (tagName === 'dd') {
        tagType = TagType.description_details;
    }
    else if (tagName === 'dl') {
        tagType = TagType.description_list;
    }
    else if (tagName === 'dt') {
        tagType = TagType.description_term;
    }
    else {
        tagType = TagType.unknown;
    }
    let markup = tagName ? `<${isClosingTag ? '/' : ''}${tagName}>` : '';
    const isSelfClosing = selfClosingElements.includes(tagType);
    if (isSelfClosing) {
        // sanitize the original markup for output
        // <img title="asdfas" src="asdfasdf" /> becomes <img />
        markup = tag.replace(/^(<[a-z]+).*?(\s?\/?>)$/i, '$1$2');
    }
    const attributes = {};
    const supportedAttrs = supportedTagAttributes[tagType];
    if (supportedAttrs?.length) {
        for (const attr of supportedAttrs) {
            attributes[attr] = new RegExp(`${attr}=["'](.+?)["']`).exec(tag)?.[1] || '';
        }
    }
    return {
        type: tagType,
        isSelfClosing,
        isClosing: isClosingTag,
        isBlock: blockElements.includes(tagType),
        tagName,
        attributes,
        markup,
    };
}
/**
 * Converts a TagInfo entry into a markdown-it Token, altering an existing one if given
 * @param tagInfo The tagInfo to use
 * @param existing The token to alter; creates a new token if this is empty
 */
function tagInfoToToken(tagInfo, existing) {
    // if a token was not passed in, create a new empty one
    const token = existing || new Token('', '', 0);
    // determine the markdown-it Token type for this tag
    const postfix = tagInfo.isSelfClosing ? '' : tagInfo.isClosing ? '_close' : '_open';
    let tokenType = '';
    switch (tagInfo.type) {
        case TagType.unknown:
            tokenType = 'text';
            break;
        case TagType.strike:
            tokenType = `s${postfix}`;
            break;
        case TagType.emphasis:
            tokenType = `em${postfix}`;
            break;
        case TagType.code:
            tokenType = `code_inline_split${postfix}`;
            break;
        case TagType.horizontal_rule:
            tokenType = 'hr';
            break;
        case TagType.link:
            tokenType = `link${postfix}`;
            token.attrSet('href', tagInfo.attributes.href);
            token.attrSet('title', tagInfo.attributes.title);
            break;
        case TagType.image:
            tokenType = 'image';
            token.attrSet('src', tagInfo.attributes.src);
            token.attrSet('height', tagInfo.attributes.height);
            token.attrSet('width', tagInfo.attributes.width);
            token.attrSet('alt', tagInfo.attributes.alt);
            token.attrSet('title', tagInfo.attributes.title);
            break;
        case TagType.keyboard:
            tokenType = `kbd${postfix}`;
            break;
        default:
            // e.g. TagType.pre becomes "pre" + postfix
            tokenType = TagType[tagInfo.type] + postfix;
            break;
    }
    token.type = tokenType;
    token.markup = tagInfo.markup;
    token.nesting = tagInfo.isClosing ? -1 : 1;
    if (tagInfo.isSelfClosing)
        token.nesting = 0;
    // make sure to set the original tag name back to the token so we can convert back
    token.tag = tagInfo.tagName || '';
    return token;
}
/**
 * Determines if an html_block is able to be simply parsed
 * @param token The html_block token to parse
 * @returns The parsed info if able, null if unable
 */
function isParseableHtmlBlockToken(token) {
    const content = token.content;
    // checks if a token matches `<open>content</close>` OR `<br />`
    const matches = /^(?:(<[a-z0-9]+.*?>)([^<>\n]+?)(<\/[a-z0-9]+>))$|^(<[a-z0-9]+(?:\s.+?)?\s?\/?>)$/i.exec(content);
    if (!matches)
        return null;
    // there will always be four matches, only the last will be filled when the second regex kicks in
    const isSelfClosed = !!matches[4];
    const tagInfo = [];
    let isBlock = false;
    if (isSelfClosed) {
        // self closed, just get the info for the tag as a whole
        const info = getTagInfo(content);
        if (info.type !== TagType.unknown) {
            isBlock = info.isBlock;
            tagInfo.push(info);
        }
    }
    else {
        // block is in the format <tag>text</tag>
        // get the tag info for the open/close tags
        const openTag = getTagInfo(matches[1]);
        const text = matches[2];
        const closeTag = getTagInfo(matches[3]);
        // the tag is only valid if both tags are known and match each other
        if (openTag.type !== TagType.unknown && closeTag.type !== TagType.unknown && openTag.type === closeTag.type) {
            isBlock = openTag.isBlock;
            // blockquotes are different...
            tagInfo.push(openTag);
            tagInfo.push(text);
            tagInfo.push(closeTag);
        }
    }
    // we were able to parse the tag if *any* info was returned
    return tagInfo.length > 0 ? { isBlock, tagInfo } : null;
}
/**
 * Wraps an inline token with another token of type `inline`.
 * Optionally, wraps in `paragraph` tokens if the parent requires a block child
 * @param token The token to wrap
 * @param parentType The type of the token's parent
 */
function wrapBareInlineToken(token, parentType) {
    // don't wrap block tokens
    if (token.block)
        return [token];
    /** Array of types that take block children (vs inline children only) */
    const takesBlockChildren = [TagType.blockquote, TagType.pre];
    const inlineToken = new Token('inline', '', 0);
    inlineToken.children = [token];
    // if the parent type only takes block children, wrap in a `p` tag
    if (!parentType || takesBlockChildren.includes(parentType)) {
        return [new Token('paragraph_open', 'p', 1), inlineToken, new Token('paragraph_close', 'p', -1)];
    }
    return [inlineToken];
}
/**
 * Sanitizes a single token if it is of type `html_inline`; otherwise, its children are sanitized
 * @param token The token to sanitize `html_inline` tokens in
 */
function sanitizeHtmlInlineToken(token) {
    let tagInfo = null;
    if (token.type === 'html_inline') {
        // all html_inline tokens are valid, get their info
        tagInfo = getTagInfo(token.content);
    }
    else {
        // non-block/inline, just sanitize their children
        if (token.children && token.children.length)
            token.children = sanitizeInlineHtmlTokens(token.children);
    }
    // if the tagInfo was not populated, then this isn't sanitizable
    if (!tagInfo)
        return token;
    // this shouldn't happen, but guard against it anyways
    if (tagInfo.isBlock)
        return token;
    // set all the tagInfo onto the token
    token = tagInfoToToken(tagInfo, token);
    // set the `inline_html` attribute for when we're checking for well formed tokens later
    token.attrSet('inline_html', 'true');
    return token;
}
/**
 * Recursively sanitizes all `html_inline` tokens in a Token array, along with making sure
 * that all sanitized tokens are well formed (both an opening and closing pair exists);
 * Can return `html_inline` tokens that were unable to be sanitized or have a missing pair
 * @param tokens The tokens to sanitize
 */
function sanitizeInlineHtmlTokens(tokens) {
    tokens = tokens.map(sanitizeHtmlInlineToken).filter((t) => !!t);
    for (let i = 0, len = tokens.length; i < len; i++) {
        const openToken = tokens[i];
        if (!openToken.attrGet('inline_html'))
            continue;
        // doesn't have an open tag... change back to html_inline and let the renderer deal with it
        if (openToken.type.includes('_close') && !openToken.attrGet('paired')) {
            openToken.type = 'html_inline';
            continue;
        }
        if (!openToken.type.includes('_open'))
            continue;
        let hasClosingTag = false;
        // look for the closing tag to this opening tag
        for (let j = i + 1, len2 = tokens.length; j < len2; j++) {
            const closeToken = tokens[j];
            // not inline or already paired, skip
            if (!closeToken || !closeToken.attrGet('inline_html') || closeToken.attrGet('paired'))
                continue;
            if (openToken.tag === closeToken.tag) {
                hasClosingTag = true;
                closeToken.attrSet('paired', 'true');
                break;
            }
        }
        // doesn't have a close tag... change back to html_inline and let the renderer deal with it
        if (!hasClosingTag)
            openToken.type = 'html_inline';
    }
    return tokens;
}
/**
 * Strips a string of all unknown html tags
 * @param content The string to sanitize
 */
function sanitizeHtmlString(content) {
    const tags = content.match(/(<\/?[a-z]+.*?>)/gi)?.map((t) => ({
        match: t,
        tagInfo: getTagInfo(t),
    }));
    // no tags in the content... probably should never happen, but check anyways
    if (!tags || !tags.length)
        return content;
    let newContent = content;
    // replace each invalid tag with a sanitized version
    tags.forEach((t) => {
        let toReplaceWith;
        // TODO move logic into its own function?
        if (t.tagInfo.type === TagType.unknown) {
            // unknown tags are removed completely
            toReplaceWith = '';
        }
        else if (t.tagInfo.type === TagType.image) {
            // image tags have specific allowed attributes
            const attrs = t.match.match(/((width|height|src|alt|title)=["'].+?["'])/g);
            let insert = t.tagInfo.markup;
            if (attrs?.length)
                insert = insert.replace('<img', `<img ${attrs.join(' ')}`);
            toReplaceWith = insert;
        }
        else if (t.tagInfo.type === TagType.link) {
            // link tags have specific allowed attributes
            const attrs = t.match.match(/((href|title)=["'].+?["'])/g);
            let insert = t.tagInfo.markup;
            if (attrs?.length)
                insert = insert.replace('<a', `<a ${attrs.join(' ')}`);
            toReplaceWith = insert;
        }
        else {
            // known tags have their attributes wiped out completely
            toReplaceWith = t.tagInfo.markup;
        }
        // replace our matched tag with the updated version
        newContent = newContent.replace(t.match, toReplaceWith);
    });
    return newContent;
}
/**
 * Recursively sanitizes all `html_block` tokens by parsing the ones that are able to be simply parsed
 * @param tokens The tokens to sanitize
 */
function sanitizeSimpleHtmlBlockTokens(tokens) {
    const retTokens = [];
    tokens.forEach((token) => {
        let parsedInfo = null;
        if (token.type === 'html_block') {
            // we *do* allow parsing some tokens detected as html_block under specific circumstances (br, image)
            parsedInfo = isParseableHtmlBlockToken(token);
        }
        else if (token.children?.length) {
            // sanitize any children as well
            token.children = sanitizeSimpleHtmlBlockTokens(token.children);
        }
        if (!parsedInfo || !parsedInfo.tagInfo.length) {
            retTokens.push(token);
            return;
        }
        const newTokens = [];
        parsedInfo.tagInfo.forEach((tag, i, arr) => {
            const lastInfo = arr[i - 1];
            const isInline = typeof tag === 'string' || !tag.isBlock;
            let tok;
            if (typeof tag === 'string') {
                const t = new Token('text', '', 0);
                t.content = tag;
                tok = t;
            }
            else {
                tok = tagInfoToToken(tag);
            }
            let wrappedTokens = [tok];
            if (isInline)
                wrappedTokens = wrapBareInlineToken(tok, lastInfo?.type);
            newTokens.push(...wrappedTokens);
        });
        retTokens.push(...newTokens);
    });
    return retTokens;
}
/**
 * Sanitize the content of `html_block` tokens by stripping out all unknown tags
 * @param tokens The tokens to sanitize
 */
function sanitizeBlockHtmlTokens(tokens) {
    const retTokens = [];
    tokens.forEach((token) => {
        if (token.children?.length)
            token.children = sanitizeBlockHtmlTokens(token.children);
        // don't sanitize non-`html_block*` tokens
        if (token.type.indexOf('html_block') !== 0) {
            retTokens.push(token);
            return;
        }
        if (token.type === 'html_block') {
            token.content = sanitizeHtmlString(token.content);
            // if the content is completely empty, don't even add the block
            if (!token.content.trim())
                return;
            // TODO naive check? try appending to a div and getting innerText instead?
            // there's no more html left after sanitization, return a text node instead
            if (!token.content.includes('<')) {
                const textToken = new Token('text', '', 0);
                textToken.content = token.content;
                const wrappedToken = wrapBareInlineToken(textToken, null);
                retTokens.push(...wrappedToken);
            }
            else {
                retTokens.push(token);
            }
        }
        else if (token.type === 'html_block_container_open') {
            const contentOpen = token.attrGet('contentOpen');
            const contentClose = token.attrGet('contentClose');
            token.attrSet('contentOpen', sanitizeHtmlString(contentOpen));
            token.attrSet('contentClose', sanitizeHtmlString(contentClose));
            // TODO if both contentOpen and contentClose are empty, remove the container entirely
            retTokens.push(token);
        }
        else {
            retTokens.push(token);
        }
    });
    return retTokens;
}
/**
 * Attempts to merge `html_block` tokens that were split by a newline character
 * @param tokens The tokens to sanitize
 */
function mergeSplitBlockHtmlTokens(tokens) {
    const returnTokens = [];
    let splitCount = 0;
    // get all split html_block indexes
    let blockIndexes = tokens
        .map((t, i) => {
        if (t.type !== 'html_block')
            return null;
        // extremely naive check that assumes open/close tags are balanced
        const openTags = t.content.match(/<[a-z]+(\s[a-z0-9\-"'=\s])?>/gi)?.length ?? 0;
        const closeTags = t.content.match(/<\/[a-z]+>/gi)?.length ?? 0;
        // TODO accurate?
        if (openTags > closeTags) {
            splitCount += 1;
            return i;
        }
        // TODO yeah, this is probably not accurate...
        if (openTags < closeTags && splitCount % 2 === 1) {
            splitCount += 1;
            return i;
        }
        return null;
    })
        .filter((t) => t !== null);
    // if there is an odd number of indexes, drop the last one (we only work in pairs)
    if (blockIndexes.length % 2 === 1)
        blockIndexes = blockIndexes.slice(0, -1);
    let lastOpenToken = null;
    tokens.forEach((t, i) => {
        if (t.children && t.children.length)
            t.children = mergeSplitBlockHtmlTokens(t.children);
        // if the current token is an html_block (and assigned to a pair)
        const isPairedHtmlBlock = t.type === 'html_block' && blockIndexes.includes(i);
        if (!isPairedHtmlBlock) {
            returnTokens.push(t);
            return;
        }
        const containerToken = new Token(`html_block_container_${lastOpenToken ? 'close' : 'open'}`, '', lastOpenToken ? -1 : 1);
        if (!lastOpenToken) {
            containerToken.attrSet('contentOpen', t.content);
            lastOpenToken = containerToken;
            returnTokens.push(containerToken);
        }
        else {
            lastOpenToken.attrSet('contentClose', t.content);
            lastOpenToken = null;
            returnTokens.push(containerToken);
        }
    });
    return returnTokens;
}
/**
 * Parses and sanitizes all supported html_block and html_inline tokens
 * @param md
 */
function html(md) {
    md.core.ruler.push('so-sanitize-html', (state) => {
        // TODO there are a lot of loops here. Can we combine a few?
        state.tokens = sanitizeInlineHtmlTokens(state.tokens);
        state.tokens = sanitizeSimpleHtmlBlockTokens(state.tokens);
        state.tokens = mergeSplitBlockHtmlTokens(state.tokens);
        state.tokens = sanitizeBlockHtmlTokens(state.tokens);
        return false;
    });
}

const directMentionContentSelectRegExp = /<@(.*?)>/; // <@ selects the id in the middle >
const parseMentionDetail = (mentions, id) => {
    if (!mentions || !mentions.mentionDetail || !mentions.mentionDetail[id]) {
        return {
            id: id,
            name: 'Not set',
            email: 'Not set',
        };
    }
    return mentions.mentionDetail[id];
};
const extractIdFromMention = (mentionStr) => {
    return mentionStr.match(directMentionContentSelectRegExp)[1];
};
const getTokenObject = (type, tag, content, mentions) => {
    const token = new Token(type, tag, 0);
    token.content = content;
    if (type === 'mention') {
        token.content = '';
        token.attrs = parseMentionDetail(mentions, extractIdFromMention(content));
    }
    return token;
};
const parseMentions = (mentions) => (state) => {
    state.tokens.forEach((blockToken) => {
        if (blockToken.type !== 'inline')
            return;
        const newChildren = [];
        blockToken.children.forEach((token) => {
            const mentionMatcherRegExp = /<@([a-zA-Z0-9]+)>/g;
            if (token.type !== 'text' || !mentionMatcherRegExp.test(token.content)) {
                newChildren.push(token);
                return;
            }
            const directMentionMatchRegExp = /(<@.*?>)/g; // selects all <@*...*>
            const parts = token.content.split(directMentionMatchRegExp).filter(Boolean);
            parts.forEach((part) => {
                if (directMentionContentSelectRegExp.test(part)) {
                    newChildren.push(getTokenObject('mention', 'span', part, mentions));
                }
                else {
                    newChildren.push(getTokenObject('text', '', part));
                }
            });
        });
        blockToken.children = newChildren;
    });
};
const mentionMarkdown = (mentions) => (md, opts) => {
    md.core.ruler.after('inline', 'mention', parseMentions(mentions));
};

// TODO rather than checking all this, it'd be nice if we upstreamed a PR to set a reference attribute at parse time
/** Checks if a single link_open/link_close token is a link reference and sets metadata on it */
function setLinkReference(references, token, parent) {
    if (!parent) {
        // this should never happen, but check anyways
        throw new Error('link-reference: parent token is undefined');
    }
    // TODO HACK this is super fragile and definitely doesn't work for anything except the most basic cases
    // try to find any links in the parent's content
    // they'll look like [label], [label][], or [label][id]
    const regex = /\[(.+?)\](?!\()(?:\[\]|\[(.+?)\]|)/g;
    let reference;
    let label = '';
    // TODO I'd much rather use String.matchAll (supported by all supported browsers), but that'd require a change to tsconfig
    let match;
    //@ts-ignore
    while ((match = regex.exec(parent.content)) !== null) {
        if (!match?.length)
            continue;
        // [label], [label][]
        label = match[1];
        // [label][id]
        if (match[2])
            label = match[2];
        const normalizedLabel = normalizeReference(label);
        reference = references[normalizedLabel];
        // if the reference doesn't exist, or if we found the wrong reference, bail
        if (!reference || reference.href !== token.attrGet('href')) {
            reference = undefined;
            continue;
        }
        break;
    }
    if (!reference)
        return false;
    const refLinkType = match[2] ? 'full' : match[0].endsWith('[]') ? 'collapsed' : 'shortcut';
    token.markup = 'reference';
    token.meta = token.meta || {};
    token.meta.reference = {
        ...reference,
        label,
        type: refLinkType,
        contentMatch: match[0], // TODO used?
    };
    return true;
}
/** Sets link reference metadata on all applicable link_open/link_close tokens */
function setLinkReferences(references, tokens, parent) {
    let foundLinkOpen = false;
    // go through the tokens and try to find applicable links
    tokens.forEach((token) => {
        // TODO this runs for every link token_close, which could likely be matched when we have a valid link_open
        // if this is a standard link_open (no html, autolink, etc), try to set the reference
        if (token.type === 'link_open' && !token.markup)
            foundLinkOpen = setLinkReference(references, token, parent);
        // if we found a link_open, set the reference on the matching link_close
        // NOTE: links cannot be embedded inside links, so we don't have to worry about mismatched open/close tokens
        if (token.type === 'link_close' && foundLinkOpen) {
            token.markup = 'reference';
            foundLinkOpen = false;
        }
        if (token.children)
            setLinkReferences(references, token.children, token);
    });
}
/**
 * Searches for and marks links that point to a reference with a "reference" meta data
 */
function reference_link(md) {
    md.core.ruler.push('link-reference', (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const references = state.env?.references;
        // if there are no references, do nothing
        if (!references || !Object.keys(references).length)
            return false;
        setLinkReferences(references, state.tokens);
        return false;
    });
}

// TODO unfortunately, we cannot reliably extend blockquote since it is hardcoded to search for `>` characters
// In addition, we cannot just call "blockquote" inside spoiler, because it does a lookahead for `>` characters and leaves our `!`s behind, potentially causing parsing issues
// The official advice is to just "copy paste then edit" "extended" rules...
// see https://github.com/markdown-it/markdown-it/blob/master/docs/development.md#general-considerations-for-plugins
// see also https://github.com/markdown-it/markdown-it/issues/46#issuecomment-73125248
function blockquoteExt(options, state, startLine, endLine, silent) {
    // NOTE: we're keeping the source as close to upstream as possible, so ignore errors like this
    // eslint-disable-next-line no-var
    var adjustTab;
    let ch;
    let i;
    let initial;
    let l;
    let lastLineEmpty;
    let lines;
    let nextLine;
    let offset;
    let oldBMarks;
    let oldBSCount;
    let oldIndent;
    let oldParentType;
    let oldSCount;
    let oldTShift;
    let spaceAfterMarker;
    let terminate;
    let terminatorRules;
    let token;
    let wasOutdented;
    const oldLineMax = state.lineMax;
    let pos = state.bMarks[startLine] + state.tShift[startLine];
    let max = state.eMarks[startLine];
    // if it's indented more than 3 spaces, it should be a code block
    if (state.sCount[startLine] - state.blkIndent >= 4)
        return false;
    // check the block quote marker
    if (state.src.charCodeAt(pos) !== 0x3e || /* > */ !options.followingCharRegex.test(state.src[pos + 1]))
        return false;
    pos += options.markup.length;
    // we know that it's going to be a valid blockquote,
    // so no point trying to find the end of it in silent mode
    if (silent)
        return true;
    // skip spaces after ">" and re-calculate offset
    initial = offset = state.sCount[startLine] + pos - (state.bMarks[startLine] + state.tShift[startLine]);
    // skip one optional space after '>'
    if (state.src.charCodeAt(pos) === 0x20 /* space */) {
        // ' >   test '
        //     ^ -- position start of line here:
        pos++;
        initial++;
        offset++;
        adjustTab = false;
        spaceAfterMarker = true;
    }
    else if (state.src.charCodeAt(pos) === 0x09 /* tab */) {
        spaceAfterMarker = true;
        if ((state.bsCount[startLine] + offset) % 4 === 3) {
            // '  >\t  test '
            //       ^ -- position start of line here (tab has width===1)
            pos++;
            initial++;
            offset++;
            adjustTab = false;
        }
        else {
            // ' >\t  test '
            //    ^ -- position start of line here + shift bsCount slightly
            //         to make extra space appear
            adjustTab = true;
        }
    }
    else {
        spaceAfterMarker = false;
    }
    oldBMarks = [state.bMarks[startLine]];
    state.bMarks[startLine] = pos;
    while (pos < max) {
        ch = state.src.charCodeAt(pos);
        if (isSpace(ch)) {
            if (ch === 0x09) {
                offset += 4 - ((offset + state.bsCount[startLine] + (adjustTab ? 1 : 0)) % 4);
            }
            else {
                offset++;
            }
        }
        else {
            break;
        }
        pos++;
    }
    oldBSCount = [state.bsCount[startLine]];
    state.bsCount[startLine] = state.sCount[startLine] + 1 + (spaceAfterMarker ? 1 : 0);
    lastLineEmpty = pos >= max;
    oldSCount = [state.sCount[startLine]];
    state.sCount[startLine] = offset - initial;
    oldTShift = [state.tShift[startLine]];
    state.tShift[startLine] = pos - state.bMarks[startLine];
    terminatorRules = state.md.block.ruler.getRules('spoiler');
    oldParentType = state.parentType;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error TODO adding a new parent type here...
    state.parentType = 'spoiler';
    wasOutdented = false;
    // Search the end of the block
    //
    // Block ends with either:
    //  1. an empty line outside:
    //     ```
    //     > test
    //
    //     ```
    //  2. an empty line inside:
    //     ```
    //     >
    //     test
    //     ```
    //  3. another tag:
    //     ```
    //     > test
    //      - - -
    //     ```
    for (nextLine = startLine + 1; nextLine < endLine; nextLine++) {
        // check if it's outdented, i.e. it's inside list item and indented
        // less than said list item:
        //
        // ```
        // 1. anything
        //    > current blockquote
        // 2. checking this line
        // ```
        if (state.sCount[nextLine] < state.blkIndent)
            wasOutdented = true;
        pos = state.bMarks[nextLine] + state.tShift[nextLine];
        max = state.eMarks[nextLine];
        if (pos >= max) {
            // Case 1: line is not inside the blockquote, and this line is empty.
            break;
        }
        pos += options.markup.length;
        if (state.src.charCodeAt(pos - options.markup.length) === 0x3e &&
            /* > */ options.followingCharRegex.test(state.src[pos - options.markup.length + 1]) &&
            !wasOutdented) {
            // This line is inside the blockquote.
            // skip spaces after ">" and re-calculate offset
            initial = offset = state.sCount[nextLine] + pos - (state.bMarks[nextLine] + state.tShift[nextLine]);
            // skip one optional space after '>'
            if (state.src.charCodeAt(pos) === 0x20 /* space */) {
                // ' >   test '
                //     ^ -- position start of line here:
                pos++;
                initial++;
                offset++;
                adjustTab = false;
                spaceAfterMarker = true;
            }
            else if (state.src.charCodeAt(pos) === 0x09 /* tab */) {
                spaceAfterMarker = true;
                if ((state.bsCount[nextLine] + offset) % 4 === 3) {
                    // '  >\t  test '
                    //       ^ -- position start of line here (tab has width===1)
                    pos++;
                    initial++;
                    offset++;
                    adjustTab = false;
                }
                else {
                    // ' >\t  test '
                    //    ^ -- position start of line here + shift bsCount slightly
                    //         to make extra space appear
                    adjustTab = true;
                }
            }
            else {
                spaceAfterMarker = false;
            }
            oldBMarks.push(state.bMarks[nextLine]);
            state.bMarks[nextLine] = pos;
            while (pos < max) {
                ch = state.src.charCodeAt(pos);
                if (isSpace(ch)) {
                    if (ch === 0x09) {
                        offset += 4 - ((offset + state.bsCount[nextLine] + (adjustTab ? 1 : 0)) % 4);
                    }
                    else {
                        offset++;
                    }
                }
                else {
                    break;
                }
                pos++;
            }
            lastLineEmpty = pos >= max;
            oldBSCount.push(state.bsCount[nextLine]);
            state.bsCount[nextLine] = state.sCount[nextLine] + 1 + (spaceAfterMarker ? 1 : 0);
            oldSCount.push(state.sCount[nextLine]);
            state.sCount[nextLine] = offset - initial;
            oldTShift.push(state.tShift[nextLine]);
            state.tShift[nextLine] = pos - state.bMarks[nextLine];
            continue;
        }
        // Case 2: line is not inside the blockquote, and the last line was empty.
        if (lastLineEmpty)
            break;
        // Case 3: another tag found.
        terminate = false;
        for (i = 0, l = terminatorRules.length; i < l; i++) {
            if (terminatorRules[i](state, nextLine, endLine, true)) {
                terminate = true;
                break;
            }
        }
        if (terminate) {
            // Quirk to enforce "hard termination mode" for paragraphs;
            // normally if you call `tokenize(state, startLine, nextLine)`,
            // paragraphs will look below nextLine for paragraph continuation,
            // but if blockquote is terminated by another tag, they shouldn't
            state.lineMax = nextLine;
            if (state.blkIndent !== 0) {
                // state.blkIndent was non-zero, we now set it to zero,
                // so we need to re-calculate all offsets to appear as
                // if indent wasn't changed
                oldBMarks.push(state.bMarks[nextLine]);
                oldBSCount.push(state.bsCount[nextLine]);
                oldTShift.push(state.tShift[nextLine]);
                oldSCount.push(state.sCount[nextLine]);
                state.sCount[nextLine] -= state.blkIndent;
            }
            break;
        }
        oldBMarks.push(state.bMarks[nextLine]);
        oldBSCount.push(state.bsCount[nextLine]);
        oldTShift.push(state.tShift[nextLine]);
        oldSCount.push(state.sCount[nextLine]);
        // A negative indentation means that this is a paragraph continuation
        //
        state.sCount[nextLine] = -1;
    }
    oldIndent = state.blkIndent;
    state.blkIndent = 0;
    token = state.push(`${options.name}_open`, options.name, 1);
    token.markup = options.markup;
    token.map = lines = [startLine, 0];
    state.md.block.tokenize(state, startLine, nextLine);
    token = state.push(`${options.name}_close`, options.name, -1);
    token.markup = options.markup;
    state.lineMax = oldLineMax;
    state.parentType = oldParentType;
    lines[1] = state.line;
    // Restore original tShift; this might not be necessary since the parser
    // has already been here, but just to make sure we can do that.
    for (i = 0; i < oldTShift.length; i++) {
        state.bMarks[i + startLine] = oldBMarks[i];
        state.tShift[i + startLine] = oldTShift[i];
        state.sCount[i + startLine] = oldSCount[i];
        state.bsCount[i + startLine] = oldBSCount[i];
    }
    state.blkIndent = oldIndent;
    return true;
}
function spoilerFn(state, startLine, endLine, silent) {
    return blockquoteExt({
        followingCharRegex: /!/,
        markup: '>!',
        name: 'spoiler',
    }, state, startLine, endLine, silent);
}
function blockquoteFn(state, startLine, endLine, silent) {
    return blockquoteExt({
        followingCharRegex: /[^!]/,
        markup: '>',
        name: 'blockquote',
    }, state, startLine, endLine, silent);
}
/**
 * Parses out spoiler `>!` blocks
 * @param md
 */
function spoiler(md) {
    // TODO necessary?
    // find all blockquote chain rules and update to be part of the spoiler chain as well
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error TODO no public way to iterate over all rules; maybe there's a better way?
    ;
    md.block.ruler.__rules__.forEach((r) => {
        const bqIndex = r.alt.indexOf('blockquote');
        if (bqIndex > -1) {
            // add in "spoiler" right before the "blockquote" entry
            r.alt.splice(bqIndex, 0, 'spoiler');
        }
    });
    md.block.ruler.before('blockquote', 'spoiler', spoilerFn, {
        // TODO stole this from blockquote, dunno what it does...
        alt: ['paragraph', 'reference', 'spoiler', 'blockquote', 'list'],
    });
    md.block.ruler.at('blockquote', blockquoteFn, {
        alt: ['paragraph', 'reference', 'spoiler', 'blockquote', 'list'],
    });
}

function langCommentParser(matcher, tokenType, state, startLine, endLine, silent) {
    const pos = state.bMarks[startLine] + state.tShift[startLine];
    const max = state.eMarks[startLine];
    // Check start
    if (state.src.charCodeAt(pos) !== 0x3c /* < */ || pos + 2 >= max)
        return false;
    // Quick fail on second char
    const ch = state.src.charCodeAt(pos + 1);
    if (ch !== 0x21 /* ! */)
        return false;
    // match the opener
    const lineText = state.src.slice(pos, max);
    const matches = matcher.exec(lineText);
    if (!matches?.length)
        return false;
    if (silent)
        return true;
    const nextLine = startLine + 1;
    state.line = nextLine;
    const newToken = state.push(tokenType, '', 0);
    newToken.map = [startLine, nextLine];
    newToken.content = state.getLines(startLine, nextLine, state.blkIndent, true);
    newToken.attrSet('language', matches[1]);
    return true;
}
/**
 * Parser rule for stack_snippet lang, partial based / piecemealed from other markdown-it block rules
 * @param state
 * @param startLine
 * @param endLine
 * @param silent
 */
function stack_language_comment(state, startLine, endLine, silent) {
    return langCommentParser(/<!-- language: lang-(.+?) -->/, 'stack_language_comment', state, startLine, endLine, silent);
}
function stack_lang_all(state, startLine, endLine, silent) {
    return langCommentParser(/<!-- language-all: lang-(.+?) -->/, 'stack_language_all_comment', state, startLine, endLine, silent);
}
// TODO document what the language-all rules are
function sanitizeCodeBlockLangs(state) {
    // keep track of which "language-all" has been set
    let currentLanguageAll = null;
    // first, do a pass to detect the first stack_language_all_comment for use in unmarked code_blocks preceding a `language-all` comment
    // TODO we need to support a default language at either parse or render time, will revisit this later in a separate plugin
    for (const token of state.tokens) {
        if (token.type === 'stack_language_all_comment') {
            currentLanguageAll = token.attrGet('language');
            break;
        }
    }
    // second, check for stack_language_comment and apply languages (including language-all) to all code blocks
    for (let i = 0, len = state.tokens.length; i < len; i++) {
        const currentBlock = state.tokens[i];
        const nextBlock = state.tokens[i + 1];
        // if this is a language comment and the next block is a code block, set the value
        if (currentBlock.type === 'stack_language_comment' && nextBlock?.type === 'code_block') {
            const language = currentBlock.attrGet('language');
            // the "info string" for code fences gets set into Token.info, so reuse that
            nextBlock.info = language;
        }
        // later language-all blocks reset the "default" language for all following code_blocks
        else if (currentBlock.type === 'stack_language_all_comment') {
            currentLanguageAll = currentBlock.attrGet('language');
        }
        // set the language value on unmarked code blocks if we have a default language set
        else if (currentBlock.type === 'code_block' && !currentBlock.info && currentLanguageAll) {
            // the "info string" for code fences gets set into Token.info, so reuse that
            currentBlock.info = currentLanguageAll;
        }
    }
    // last, strip out all the stack_language_comment* tokens entirely
    state.tokens = state.tokens.filter((t) => !t.type.startsWith('stack_language'));
    return false;
}
/**
 * Parses out `<!-- language: lang-* -->` and `<!-- language-all -->` comments and applies to the targeted code_block tokens
 * @param md
 */
function stackLanguageComments(md) {
    md.block.ruler.before('html_block', 'stack_language_comment', stack_language_comment);
    md.block.ruler.before('html_block', 'stack_language_all_comment', stack_lang_all);
    // strip out the stack_snippet_lang and directly modify the code blocks themselves
    md.core.ruler.push('so-sanitize-code-lang', sanitizeCodeBlockLangs);
}

function parse_tag_link(state, silent, options) {
    // quick fail on first character
    if (state.src.charCodeAt(state.pos) !== 0x5b /* [ */)
        return false;
    if (state.src.slice(state.pos, state.pos + 5) !== '[tag:' && state.src.slice(state.pos, state.pos + 10) !== '[meta-tag:')
        return false;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const labelEnd = state.md.helpers.parseLinkLabel(state, state.pos + 1, false);
    // could not find the label end
    if (labelEnd < 0)
        return false;
    const totalContent = state.src.slice(state.pos, labelEnd + 1);
    const isMeta = totalContent.slice(0, 10) === '[meta-tag:';
    const tagName = totalContent.slice(isMeta ? 10 : 5, -1);
    if (options.validate && !options.validate(tagName, isMeta, totalContent))
        return false;
    if (!silent) {
        let token = state.push('tag_link_open', 'a', 1);
        token.attrSet('tagName', tagName);
        token.attrSet('tagType', isMeta ? 'meta-tag' : 'tag');
        token.content = totalContent;
        token = state.push('text', '', 0);
        token.content = tagName;
        token = state.push('tag_link_close', 'a', -1);
    }
    state.pos = labelEnd + 1;
    return true;
}
/**
 * Parses [tag:FOO] and [meta-tag:FOO] links
 * @param md
 */
function tagLinks(md, options) {
    // MarkdownIt appears to take the name of the rule-defining function as the rule name
    // and ignores the ruleName parameter. We are relying on the rule name in tests to
    // verify that it was appropriately defined.
    md.inline.ruler.push('tag_link', (state, silent) => {
        return parse_tag_link(state, silent, options);
    });
    // TODO make sure tag links are not in links
}

function tightenList(tokens) {
    let iteratedElements = 0;
    let insideListItem = false;
    let isTight = false;
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open') {
            const result = tightenList(tokens.slice(i + 1));
            if (result.isTight)
                token.attrSet('tight', result.isTight.toString());
            // all the content from the current index to the close tag is taken care of
            // skip to the next node
            i += result.iteratedElements + 1;
            continue;
        }
        else if (token.type.startsWith('list_item_')) {
            insideListItem = token.type.endsWith('open');
        }
        else if (token.type.startsWith('paragraph_') && insideListItem && token.hidden) {
            isTight = true;
        }
        else if (token.type === 'bullet_list_close' || token.type === 'ordered_list_close') {
            // we found the end of this list, stop here
            iteratedElements = i;
            break;
        }
    }
    return {
        iteratedElements,
        isTight,
    };
}
/**
 * Searches for and marks tight lists with a "tight" attribute
 */
function tight_list(md) {
    md.core.ruler.push('tight-list', (state) => {
        tightenList(state.tokens);
        return false;
    });
}

// extend the default markdown parser's tokens and add our own
const customMarkdownParserTokens = {
    ...defaultMarkdownParser.tokens,
    pre: { block: 'pre' },
    kbd: { mark: 'kbd' },
    sup: { mark: 'sup' },
    sub: { mark: 'sub' },
    html_inline: {
        node: 'html_inline',
        getAttrs: (token) => ({
            content: token.content,
        }),
    },
    mention: {
        node: 'mention',
        getAttrs: (token) => ({
            ...(token.attrs || {}),
            content: token.content,
        }),
    },
    html_block: {
        node: 'html_block',
        getAttrs: (token) => ({
            content: token.content,
        }),
    },
    html_block_container: {
        block: 'html_block_container',
        getAttrs: (token) => ({
            contentOpen: token.attrGet('contentOpen'),
            contentClose: token.attrGet('contentClose'),
        }),
    },
    // don't map our intermediary "stack_language*_comment" tokens. These are stripped from the stream, so they shouldn't be coming back anyways
    stack_language_comment: { ignore: true },
    stack_language_all_comment: { ignore: true },
    bullet_list: {
        block: 'bullet_list',
        getAttrs: (tok) => ({
            tight: tok.attrGet('tight') === 'true',
        }),
    },
    ordered_list: {
        block: 'ordered_list',
        getAttrs: (tok) => ({
            order: +(tok.attrGet('start') || 1),
            tight: tok.attrGet('tight') === 'true',
        }),
    },
    code_block: {
        block: 'code_block',
        noCloseToken: true,
        getAttrs: (tok) => ({
            params: tok.info || '',
            markup: tok.markup || 'indented',
        }),
    },
    fence: {
        block: 'code_block',
        getAttrs: (tok) => ({
            params: tok.info || '',
        }),
        noCloseToken: true,
    },
    // add support for the strike mark
    s: {
        mark: 'strike',
    },
    table: {
        block: 'table',
    },
    thead: {
        block: 'table_head',
    },
    tbody: {
        block: 'table_body',
    },
    th: {
        block: 'table_header',
        getAttrs: (tok) => ({
            style: tok.attrGet('style'),
        }),
    },
    tr: {
        block: 'table_row',
    },
    td: {
        block: 'table_cell',
        getAttrs: (tok) => ({
            style: tok.attrGet('style'),
        }),
    },
    // override the default image parser so we can add our own extended attributes
    image: {
        node: 'image',
        getAttrs: (tok) => ({
            src: tok.attrGet('src'),
            width: tok.attrGet('width'),
            height: tok.attrGet('height'),
            alt: tok.attrGet('alt') || tok.children?.[0]?.content || null,
            title: tok.attrGet('title'),
        }),
    },
    tag_link: {
        block: 'tagLink',
        getAttrs: (tok) => ({
            tagName: tok.attrGet('tagName'),
            tagType: tok.attrGet('tagType'),
        }),
    },
    spoiler: {
        block: 'spoiler',
    },
    // support <code>foo</code> which parses differently from `bar`
    code_inline_split: {
        mark: 'code',
    },
};
// add tag attribute support to all the marks like we did in schema
// this allows us to map the original tags to / from markdown
Object.keys(customMarkdownParserTokens).forEach((k) => {
    const token = customMarkdownParserTokens[k];
    // if an existing getAttrs function exists, make sure we wrap it and add our attributes in
    if (token.getAttrs) {
        const origGetAttrs = token.getAttrs.bind(token);
        // reference links require special handling
        if (k === 'link') {
            token.getAttrs = (tok, stream, index) => {
                const attrs = { ...origGetAttrs(tok, stream, index) };
                attrs['markup'] = tok.markup;
                if (tok.markup === 'reference') {
                    const meta = tok.meta;
                    attrs['referenceType'] = meta?.reference?.type;
                    attrs['referenceLabel'] = meta?.reference?.label;
                }
                return attrs;
            };
        }
        else {
            token.getAttrs = (tok, stream, index) => ({
                markup: tok.markup,
                ...origGetAttrs(tok, stream, index),
            });
        }
        return;
    }
    // set a getAttrs function that returns the tag attribute
    token.getAttrs = (tok) => ({
        markup: tok.markup,
    });
});
// TODO can we do this more cleanly?
/**
 * Custom MarkdownParser that manually adds a low-level handler for `html_inline`.
 * We do this because we need some special functionality that is not exposed by default with the existing
 * handler generation code (from adding tokens)
 */
class SOMarkdownParser extends MarkdownParser {
    constructor(schema, tokenizer, tokens) {
        super(schema, tokenizer, tokens);
        // prosemirror visually preserves the \n from a softbreak (whitespace: pre-wrap),
        // so replace with " " (space) to be commonmark compliant
        // prosemirror visually preserves the \n from a softbreak (whitespace: pre-wrap),
        // so replace with " " (space) to be commonmark compliant
        this.tokenHandlers['softbreak'] = (state) => {
            const nodeType = this.schema.nodes['softbreak'];
            state.openNode(nodeType, {});
            state.addText(' ');
            state.closeNode();
        };
    }
}
/**
 * Extended MarkdownIt so we can peek into the tokens during parse
 * TODO we can likely remove this extended version now that we've moved internal items to plugins
 */
class SOMarkdownIt extends MarkdownIt {
    constructor(presetName, options) {
        super(presetName, options);
    }
    parse(src, env) {
        const parsed = super.parse(src, env);
        log('Sanitized markdown token tree', parsed);
        return parsed;
    }
}
/**
 * Creates a MarkdownIt instance with default properties
 * @param features The features to toggle on/off
 * @param externalPluginProvider The external plugin provider TODO should not be optional
 */
function createDefaultMarkdownItInstance(features, externalPluginProvider, mentions) {
    const defaultMarkdownItInstance = new SOMarkdownIt('default', {
        html: features.html, // we can allow the markdown parser to send through arbitrary HTML, but only because we're gonna whitelist it later
        linkify: true, // automatically link plain URLs
    });
    if (!features.tables)
        defaultMarkdownItInstance.disable('table');
    // match features to Markdig's extra emphasis plugin
    // https://github.com/lunet-io/markdig/blob/master/src/Markdig.Tests/Specs/EmphasisExtraSpecs.md
    if (!features.extraEmphasis)
        defaultMarkdownItInstance.disable('strikethrough');
    defaultMarkdownItInstance.linkify.set({
        fuzzyLink: false, // disable autolinking of anything that comes without protocol prefix (e.g. https://)
        fuzzyEmail: false, // disable email address (without mailto:) autolinking
    });
    // use a custom link validator that's closer to Stack Overflow's backend validation
    defaultMarkdownItInstance.validateLink = features.validateLink;
    // start adding in the parser plugins, NOTE: order matters!
    // parse/sanitize html
    if (features.html)
        defaultMarkdownItInstance.use((md) => html(md));
    // map language html comments to code blocks like code fences
    defaultMarkdownItInstance.use(stackLanguageComments);
    // parse tag links
    if (features.tagLinks)
        defaultMarkdownItInstance.use(tagLinks, features.tagLinks);
    // parse spoilers
    defaultMarkdownItInstance.use(spoiler);
    // ensure lists are tightened up for parsing into the doc
    defaultMarkdownItInstance.use(tight_list);
    // ensure links are have their references properly referenced
    defaultMarkdownItInstance.use(reference_link);
    // ensure we can tell the difference between the different types of hardbreaks
    defaultMarkdownItInstance.use(hardbreak_markup);
    defaultMarkdownItInstance.use(mentionMarkdown(mentions));
    // TODO should always exist, so remove the check once the param is made non-optional
    externalPluginProvider?.alterMarkdownIt(defaultMarkdownItInstance);
    return defaultMarkdownItInstance;
}
/**
 * Builds a custom markdown parser with the passed features toggled
 * @param features The features to toggle on/off
 * @param schema The finalized schema to use
 * @param externalPluginProvider The external plugin provider to use
 */
function buildMarkdownParser(features, schema, externalPluginProvider, mentions) {
    const defaultMarkdownItInstance = createDefaultMarkdownItInstance(features, externalPluginProvider, mentions);
    return new SOMarkdownParser(schema, defaultMarkdownItInstance, {
        ...customMarkdownParserTokens,
        ...externalPluginProvider?.markdownProps.parser,
    });
}

/**
 * The amount of time to delay rendering since the last render;
 * Setting to a high value will result in less frequent renders as users type
 */
const DEFAULT_RENDER_DELAY_MS = 1000;
class PreviewView {
    constructor(view, parserFeatures, previewOptions) {
        this.renderTimeoutId = null;
        this.dom = document.createElement('div');
        this.dom.classList.add('s-prose', 'js-md-preview');
        // TODO pass down the ExternalPluginProvider as well
        this.renderer =
            previewOptions?.renderer ||
                createDefaultMarkdownItInstance({
                    ...parserFeatures,
                    // TODO until we handle proper html sanitizing in the renderer,
                    // we need to disable html entirely...
                    html: false,
                });
        this.renderDelayMs = previewOptions?.renderDelayMs || DEFAULT_RENDER_DELAY_MS;
        this.renderPreview(view.state.doc.textContent);
    }
    update(view, prevState) {
        // if the doc/view hasn't changed, there's no work to do
        if (!docNodeChanged(prevState, view.state))
            return;
        // if there is a render timeout already, clear it (essentially resetting the timeout)
        if (this.renderTimeoutId)
            window.clearTimeout(this.renderTimeoutId);
        const text = view.state.doc.textContent;
        if (this.renderDelayMs) {
            // only render the preview after a delay
            // this is to prevent too many renders while the user is typing
            this.renderTimeoutId = window.setTimeout(() => {
                this.renderPreview(text);
                this.renderTimeoutId = null;
            }, this.renderDelayMs);
        }
        else {
            // if there is no delay, just render
            // there's typically no harm in setting a timeout with 0ms delay,
            // but running immediately without deferring execution is easier to unit test
            this.renderPreview(text);
        }
    }
    destroy() {
        this.dom.remove();
    }
    /** Renders the preview using the passed markdown text */
    renderPreview(text) {
        log('PreviewView.update', 'Updated preview');
        // NOTE: This assumes that the renderer is properly sanitizing html;
        // this is specified in the option docs @see CommonmarkOptions["preview"]
        this.dom.innerHTML = this.renderer.render(text);
    }
}
/**
 * Plugin that renders the editor's markdown content directly and displays it in a preview element
 * @param previewOptions The preview options passed to the commonmark editor
 * @param parserFeatures The features to enable/disable on the commonmark parser
 */
function createPreviewPlugin(previewOptions, parserFeatures) {
    if (!previewOptions?.enabled)
        return new Plugin({});
    return new Plugin({
        view(editorView) {
            const previewView = new PreviewView(editorView, parserFeatures, previewOptions);
            const containerFn = previewOptions?.parentContainer ||
                function (v) {
                    return v.dom.parentNode;
                };
            const container = containerFn(editorView);
            if (!container.contains(editorView.dom))
                container.insertBefore(previewView.dom, container.firstChild);
            // eslint-disable-next-line no-throw-literal
            else
                throw 'Preview parentContainer must not contain the editor view';
            return previewView;
        },
    });
}

/**
 * Shortcut binding that takes in a formatting string and returns a matching setBlockType command
 * @param formattingText
 */
const setBlockTypeCommand = (formattingText, options) => setBlockType.bind(null, formattingText, options);
/**
 * Shortcut binding that takes in a formatting string and returns a matching wrapIn command
 * @param formattingText
 */
const wrapInCommand = (formattingText) => toggleWrapIn$1.bind(null, formattingText);
const blockWrapInCommand = (formattingText) => toggleBlockWrap.bind(null, formattingText);
const insertRawTextCommand = (text, selectFrom, selectTo) => insertRawText.bind(null, text, selectFrom, selectTo);
const newTextNode = (schema, content) => schema.text(content);
/**
 * Toggles wrapping selected text in the formatting string; adds newly wrapped text if nothing is selected
 * @param formattingText The text to wrap the currently selected text in
 * @param state The current editor state
 * @param dispatch The dispatch function used to trigger the transaction, set to "null" if you don't want to dispatch
 */
function toggleWrapIn$1(formattingText, state, dispatch) {
    // check if we're unwrapping first
    if (unwrapIn(formattingText, state, dispatch))
        return true;
    return wrapIn(formattingText, state, dispatch);
}
/**
 * Wraps the currently selected text with the passed text, creating new text if nothing is selected
 * @param formattingText The text to wrap the currently selected text in
 * @param state The current editor state
 * @param dispatch The dispatch function used to trigger the transaction, set to "null" if you don't want to dispatch
 */
function wrapIn(formattingText, state, dispatch) {
    const textToInsertOnEmptySelection = 'your text';
    const { from, to } = state.selection;
    const tr = state.tr.insertText(formattingText, to);
    if (state.selection.empty)
        tr.insertText(textToInsertOnEmptySelection, to);
    tr.insertText(formattingText, from).scrollIntoView();
    if (dispatch) {
        let selectionStart = from;
        // add the format length twice to adjust for the characters added before *and* after the text
        let selectionEnd = to + formattingText.length * 2;
        // if the selection was empty, just select the newly added text
        // and *not* the formatting so the user can start typing over it immediately
        if (state.selection.empty) {
            selectionStart = from + formattingText.length;
            selectionEnd = selectionStart + textToInsertOnEmptySelection.length;
        }
        // set the selection to include our newly added characters
        tr.setSelection(TextSelection.create(state.apply(tr).doc, selectionStart, selectionEnd));
        dispatch(tr);
    }
    return true;
}
/**
 * Unwraps the currently selected text if it is already wrapped in the passed text
 * @param formattingText The text to wrap the currently selected text in
 * @param state The current editor state
 * @param dispatch The dispatch function used to trigger the transaction, set to "null" if you don't want to dispatch
 */
function unwrapIn(formattingText, state, dispatch) {
    // if the selection is empty, then there is nothing to unwrap
    if (state.selection.empty)
        return false;
    const { from, to } = state.selection;
    const selectedText = state.doc.textBetween(from, to);
    const precedingString = selectedText.slice(0, formattingText.length);
    const postcedingString = selectedText.slice(formattingText.length * -1);
    if (precedingString !== formattingText || postcedingString !== formattingText)
        return false;
    if (dispatch) {
        const tr = state.tr;
        // unwrap the text and set into the document
        const unwrappedText = selectedText.slice(formattingText.length, formattingText.length * -1);
        tr.replaceSelectionWith(newTextNode(tr.doc.type.schema, unwrappedText));
        // set the selected text to the unwrapped text
        tr.setSelection(TextSelection.create(state.apply(tr).doc, from, to - formattingText.length * 2));
        dispatch(tr);
    }
    return true;
}
/**
 * Sets/unsets the block type of either selected (set for just the selection) or unselected (set at the beginning of the line) text
 * @param formattingText The text to prepend to the currently selected block
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function setBlockType(formattingText, options, state, dispatch) {
    // check first if we are toggling this entire block or toggling just the selected content
    if (setMultilineSelectedBlockType(formattingText, options, state, dispatch))
        return true;
    return setSingleLineBlockType(formattingText, state, dispatch);
}
/**
 * Returns any block formatting characters (plus trailing space) at the very start of the passed text
 * @param text The text to check for leading block characters
 * @internal
 */
function matchLeadingBlockCharacters(text) {
    // TODO this might be too aggressive... remove based on a whitelist instead?
    // Match ordered list markers; see https://spec.commonmark.org/0.30/#ordered-list-marker
    let match = /^(\d+)(?:\.|\))\s/.exec(text)?.[0];
    // If text is not an ordered list block, check for other block types
    if (!match) {
        // TODO HACK assumes all non-ordered list block types are non-letter characters followed by a single space
        match = /^[^a-zA-Z0-9]+\s{1}(?=[a-zA-Z0-9_*[!]|$)+/.exec(text)?.[0];
    }
    return match || '';
}
/**
 * Places/removes the passed text at the very beginning of the text block the selection exists in,
 * potentially removing other block text to do so
 * @param formattingText The text to prepend to the currently selected block
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function setSingleLineBlockType(formattingText, state, dispatch) {
    // get the "from" position of the cursor/selection only
    const { from } = state.selection;
    // get all text from the start of the doc to our cursor
    const textToCursor = state.doc.cut(0, from).textContent;
    // doc position is index differently (0 vs 1 indexed), so offset
    const stateOffset = 1;
    // look backwards for the most recent newline char
    const prevNewlineIndex = textToCursor.lastIndexOf('\n');
    // store where we're inserting our text; this will be our working point from now on
    let textInsertPos;
    // if there is no newline, set to beginning of doc
    if (prevNewlineIndex === -1) {
        textInsertPos = stateOffset;
    }
    else {
        // otherwise, set based on the index
        textInsertPos = prevNewlineIndex + stateOffset + '\n'.length;
    }
    // always trail the formatting text with an empty space
    const trailingText = ' ';
    // get all text starting from our insert point to check if we're toggling on/off
    const textFromInsert = state.doc.cut(textInsertPos).textContent;
    // check if *any* block type is already set
    const leadingBlockChars = matchLeadingBlockCharacters(textFromInsert);
    let tr = state.tr;
    if (leadingBlockChars.length) {
        // remove all leading block chars
        tr = tr.delete(textInsertPos, textInsertPos + leadingBlockChars.length);
    }
    let isTogglingOff = false;
    // check if the text at that index is already set to our formatting text
    if (leadingBlockChars === formattingText + trailingText)
        isTogglingOff = true;
    if (!isTogglingOff) {
        // add the formatting text
        tr = tr.insertText(formattingText + trailingText, textInsertPos);
    }
    if (dispatch) {
        tr = tr.scrollIntoView();
        dispatch(tr);
    }
    return true;
}
/**
 * Places/removes the passed text at the very beginning of each selected text line, creating a preceding newline if necessary
 * @param formattingText The text to prepend to the currently selected block
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function setMultilineSelectedBlockType(formattingText, options, state, dispatch) {
    // if the selection is empty, then this command is not valid
    if (state.selection.empty)
        return false;
    let { from } = state.selection;
    const selectedText = state.doc.cut(from, state.selection.to).textContent;
    // if there are no line breaks inside this text, then treat it as if we're editing the whole block
    if (!selectedText.includes('\n'))
        return false;
    // always trail the formatting text with a space
    const trailingText = ' ';
    let tr = state.tr;
    // check the very first character on each line
    // if even a single line is missing, toggle all missing ON, else toggle all OFF
    const lines = selectedText.split('\n');
    const isTogglingOn = lines.some((text) => !text.startsWith(formattingText + trailingText));
    // doc position is index differently (0 vs 1 indexed), so offset
    const stateOffset = 1;
    let rangeFrom = from;
    lines.forEach((l) => {
        let formattedLine;
        const leadingBlockChars = matchLeadingBlockCharacters(l);
        const beginsWithText = leadingBlockChars === formattingText + trailingText;
        if (isTogglingOn && beginsWithText) {
            // toggling on and already begins with text... leave it
            formattedLine = l;
        }
        else if (isTogglingOn && leadingBlockChars.length) {
            // toggling on and another block is there... replace
            formattedLine = formattingText + trailingText + l.slice(leadingBlockChars.length);
        }
        else if (isTogglingOn) {
            // toggling on and nothing is there... just add
            formattedLine = formattingText + trailingText + l;
        }
        else {
            // toggling off... remove whatever leading block chars are there
            formattedLine = l.slice(leadingBlockChars.length);
        }
        const rangeTo = rangeFrom + l.length;
        // we can't set an empty text node, so if the line is empty, just delete the text instead
        if (formattedLine.length) {
            tr = tr.replaceRangeWith(rangeFrom, rangeTo, newTextNode(tr.doc.type.schema, formattedLine));
        }
        else {
            tr = tr.deleteRange(rangeFrom, rangeTo);
        }
        // set the start of the next line to the altered line's length + 1 character for the removed (by .split) newline char
        rangeFrom += formattedLine.length + '\n'.length;
        // Increment formattingText if specified in options
        if (options?.incrementLeadingChar) {
            formattingText = incrementCharacter(formattingText) + formattingText.substring(1);
        }
    });
    // if the character immediately preceding the selection isn't a newline, add one
    if (from > stateOffset && state.doc.textBetween(from - stateOffset, from) !== '\n') {
        tr = tr.insertText('\n', from, from);
        from += '\n'.length;
        rangeFrom += '\n'.length;
    }
    if (dispatch) {
        // the end of the selection is the calculated "rangeFrom", which includes all our added/removed chars
        // subtract a single \n's length from the end that is overadjusted in the calculation (for splitting on \n)
        const selectionEnd = rangeFrom - '\n'.length;
        tr.setSelection(TextSelection.create(state.apply(tr).doc, from, selectionEnd));
        tr.scrollIntoView();
        dispatch(tr);
    }
    return true;
}
// TODO document
function toggleBlockWrap(formattingText, state, dispatch) {
    // check if we're unwrapping first
    if (blockUnwrapIn(formattingText, state, dispatch))
        return true;
    return blockWrapIn(formattingText, state, dispatch);
}
/**
 * Wraps/unwraps a multiline block in the given formatting text, adding newlines before and after the selection if necessary
 * @param formattingText The text to wrap/unwrap the currently selected block in
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function blockWrapIn(formattingText, state, dispatch) {
    // empty selection, not valid
    if (state.selection.empty) {
        const placeholderText = 'type here';
        const insertedBlock = `\n${formattingText}\n${placeholderText}\n${formattingText}\n`;
        const newlineOffset = 2; // account for  two inserted newlines
        return insertRawText(insertedBlock, formattingText.length + newlineOffset, formattingText.length + placeholderText.length + newlineOffset, state, dispatch);
    }
    let { from, to } = state.selection;
    // check if we need to unwrap
    if (blockUnwrapIn(formattingText, state, dispatch))
        return true;
    // wrap the selected block in code fences, prepending/appending newlines if necessary
    let tr = state.tr;
    // if the character immediately preceding the selection isn't a newline, add one
    if (from > 0 && state.doc.textBetween(from - 1, from) !== '\n') {
        tr = tr.insertText('\n', from, from);
        from += 1;
        to += 1;
    }
    // if the character immediately postceding the selection isn't a newline, add one
    if (to + 1 < state.doc.content.size && state.doc.textBetween(to, to + 1) !== '\n') {
        tr = tr.insertText('\n', to + 1, to + 1);
        to += 1;
    }
    // add this char before and after the selection along with the formatting text
    const surroundingChar = '\n';
    // insert the code fences from the end first so we don't mess up our from index
    tr.insertText(surroundingChar + formattingText, to);
    tr.insertText(formattingText + surroundingChar, from);
    if (dispatch) {
        // adjust our new text selection based on how many characters we added
        const addedTextModifier = surroundingChar.length + formattingText.length;
        tr.setSelection(TextSelection.create(state.apply(tr).doc, from, 
        // add modifier twice, once for added leading text, once for added trailing text
        to + addedTextModifier * 2));
        tr.scrollIntoView();
        dispatch(tr);
    }
    return true;
}
/**
 * Unwraps a multiline block in the given formatting text if able
 * @param formattingText The text to unwrap from currently selected block
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function blockUnwrapIn(formattingText, state, dispatch) {
    // no selection, not valid
    if (state.selection.empty)
        return false;
    const { from, to } = state.selection;
    const selectedText = state.doc.textBetween(from, to);
    const surroundingChar = '\n';
    const totalFormattedLength = formattingText.length + surroundingChar.length;
    const precedingString = selectedText.slice(0, totalFormattedLength);
    const postcedingString = selectedText.slice(totalFormattedLength * -1);
    if (precedingString !== formattingText + surroundingChar || postcedingString !== surroundingChar + formattingText)
        return false;
    let tr = state.tr;
    // remove our wrapping chars, starting with the trailing text so we don't disturb the from index
    tr = tr.delete(to - totalFormattedLength, to);
    tr = tr.delete(from, from + totalFormattedLength);
    if (dispatch) {
        tr.setSelection(TextSelection.create(state.apply(tr).doc, from, 
        // add modifier twice, once for added leading text, once for added trailing text
        to - totalFormattedLength * 2));
        tr.scrollIntoView();
        dispatch(tr);
    }
    return true;
}
/**
 * Inserts the given text at the cursor, replacing selected text if applicable
 * @param text The text to insert
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function insertRawText(text, selectFrom, selectTo, state, dispatch) {
    let tr = state.tr;
    const { from } = state.selection;
    if (state.selection.empty)
        tr = tr.insertText(text, from);
    else
        tr = tr.replaceSelectionWith(newTextNode(tr.doc.type.schema, text));
    if (dispatch) {
        // if the select range is declared, select the specified range in the added text
        if (typeof selectFrom !== 'undefined' && typeof selectTo !== 'undefined') {
            tr = tr.setSelection(TextSelection.create(state.apply(tr).doc, from + selectFrom, from + selectTo));
        }
        else {
            // if the range is NOT declared, set the cursor to before the inserted text
            tr = tr.setSelection(TextSelection.create(state.apply(tr).doc, from));
        }
        tr = tr.scrollIntoView();
        dispatch(tr);
    }
    return true;
}
/**
 * Inserts a link at the cursor, optionally placing it around the currently selected text if able
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function insertLinkCommand$1(state, dispatch) {
    // TODO what dummy link to use?
    const dummyLink = 'https://cnect.jobs/';
    // TODO what should we select - text or link?
    if (state.selection.empty) {
        return insertRawText(`[text](${dummyLink})`, 1, 5, state, dispatch);
    }
    const { from, to } = state.selection;
    const selectedText = state.doc.textBetween(from, to);
    const insertedText = `[${selectedText}](${dummyLink})`;
    // TODO magic numbers!
    const selectFrom = 3 + selectedText.length;
    const selectTo = selectFrom + dummyLink.length;
    // insert the link with the link selected for easy typeover
    return insertRawText(insertedText, selectFrom, selectTo, state, dispatch);
}
/**
 * Inserts a basic table at the cursor
 * @param state The current editor state
 * @param dispatch the dispatch function used to dispatch the transaction, set to "null" if you don't want to dispatch
 */
function insertTableCommand$1(state, dispatch) {
    const tableMarkdown = `
| Column A | Column B |
| -------- | -------- |
| Cell 1   | Cell 2   |
| Cell 3   | Cell 4   |
`;
    if (state.selection.empty)
        return insertRawText(tableMarkdown, 1, 1, state, dispatch);
}
// TODO
function indentBlockCommand() {
    return false;
}
// TODO
function unIndentBlockCommand() {
    return false;
}
/**
 * Selects all text in the document's root node, rather than the node itself
 */
function selectAllTextCommand(state, dispatch) {
    if (dispatch) {
        let rootNodePos = 0;
        let rootNodeLength = 0;
        // find the root text node's position so we can highlight just it
        state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
            if (node.type.name !== 'text')
                return true;
            rootNodePos = pos;
            rootNodeLength = node.nodeSize;
            return false;
        });
        dispatch(state.tr.setSelection(TextSelection.create(state.doc, rootNodePos, rootNodePos + rootNodeLength)));
    }
    return true;
}
function incrementCharacter(text) {
    return String.fromCharCode(text.charCodeAt(0) + 1);
}
const boldCommand = wrapInCommand('**');
const emphasisCommand = wrapInCommand('*');
const inlineCodeCommand = wrapInCommand('`');
const indentCommand = indentBlockCommand;
const unindentBlock = unIndentBlockCommand;
const headerCommand = setBlockTypeCommand('#');
const strikethroughCommand = wrapInCommand('~~');
const blockquoteCommand = setBlockTypeCommand('>');
const orderedListCommand = setBlockTypeCommand('1.', { incrementLeadingChar: true });
const unorderedListCommand = setBlockTypeCommand('-');
const insertHorizontalRuleCommand$1 = insertRawTextCommand('\n---\n');
const insertCodeblockCommand = blockWrapInCommand('```');
function insertImageCommand$1(state, dispatch, view) {
    if (!imageUploaderEnabled(view.state))
        return false;
    if (!dispatch)
        return true;
    showImageUploader(view);
    return true;
}
// TODO ensure that all names match those found in the rich-text editor
/**
 * Creates all menu entries for the commonmark editor
 * @param options The options for the editor
 * @internal
 */
const createMenuEntries$1 = (options) => [
    {
        name: 'formatting1', // TODO better name?
        priority: 0,
        entries: [
            // {
            //   key: 'toggleHeading',
            //   command: headerCommand,
            //   dom: makeMenuButton(
            //     'Header',
            //     _t('commands.heading.dropdown', {
            //       shortcut: getShortcut('Mod-H'),
            //     }),
            //     'heading-btn',
            //   ),
            // },
            {
                key: 'toggleBold',
                command: boldCommand,
                dom: makeMenuButton('format_bold', _t('commands.bold', { shortcut: getShortcut('Mod-B') }), 'bold-btn'),
            },
            {
                key: 'toggleEmphasis',
                command: emphasisCommand,
                dom: makeMenuButton('format_italic', _t('commands.emphasis', { shortcut: getShortcut('Mod-I') }), 'italic-btn'),
            },
            {
                key: 'toggleCode',
                command: inlineCodeCommand,
                dom: makeMenuButton('code', _t('commands.inline_code', {
                    shortcut: getShortcut('Mod-K'),
                }), 'code-btn'),
            },
            addIf({
                key: 'toggleStrikethrough',
                command: strikethroughCommand,
                dom: makeMenuButton('strikethrough_s', _t('commands.strikethrough'), 'strike-btn'),
            }, options.parserFeatures.extraEmphasis),
        ],
    },
    {
        name: 'formatting2', // TODO better name?
        priority: 10,
        entries: [
            {
                key: 'toggleLink',
                command: insertLinkCommand$1,
                dom: makeMenuButton('link', _t('commands.link', { shortcut: getShortcut('Mod-L') }), 'insert-link-btn'),
            },
            // {
            //   key: 'toggleBlockquote',
            //   command: blockquoteCommand,
            //   dom: makeMenuButton(
            //     'Quote',
            //     _t('commands.blockquote', {
            //       shortcut: getShortcut('Ctrl-Q'),
            //     }),
            //     'blockquote-btn',
            //   ),
            // },
            {
                key: 'insertCodeblock',
                command: insertCodeblockCommand,
                dom: makeMenuButton('developer_mode', _t('commands.code_block', {
                    shortcut: getShortcut('Mod-M'),
                }), 'code-block-btn'),
            },
            // addIf(
            //   {
            //     key: 'insertImage',
            //     command: insertImageCommand,
            //     dom: makeMenuButton(
            //       'Image',
            //       _t('commands.image', {
            //         shortcut: getShortcut('Mod-G'),
            //       }),
            //       'insert-image-btn',
            //     ),
            //   },
            //   !!options.imageUpload?.handler,
            // ),
            // addIf(
            //   {
            //     key: 'insertTable',
            //     command: insertTableCommand,
            //     dom: makeMenuButton(
            //       'Table',
            //       _t('commands.table_insert', {
            //         shortcut: getShortcut('Mod-E'),
            //       }),
            //       'insert-table-btn',
            //     ),
            //   },
            //   options.parserFeatures.tables,
            // ),
        ],
    },
    {
        name: 'formatting3', // TODO better name?
        priority: 20,
        entries: [
            {
                key: 'toggleOrderedList',
                command: orderedListCommand,
                dom: makeMenuButton('format_list_numbered', _t('commands.ordered_list', {
                    shortcut: getShortcut('Mod-O'),
                }), 'numbered-list-btn'),
            },
            {
                key: 'toggleUnorderedList',
                command: unorderedListCommand,
                dom: makeMenuButton('format_list_bulleted', _t('commands.unordered_list', {
                    shortcut: getShortcut('Mod-U'),
                }), 'bullet-list-btn'),
            },
            // {
            //   key: 'insertRule',
            //   command: insertHorizontalRuleCommand,
            //   dom: makeMenuButton(
            //     'HorizontalRule',
            //     _t('commands.horizontal_rule', {
            //       shortcut: getShortcut('Mod-R'),
            //     }),
            //     'horizontal-rule-btn',
            //   ),
            // },
        ],
    },
    {
        name: 'history',
        priority: 30,
        entries: [
            {
                key: 'undo',
                command: undo,
                dom: makeMenuButton('undo', _t('commands.undo', { shortcut: getShortcut('Mod-Z') }), 'undo-btn', ['sm:d-inline-block']),
                visible: () => false,
            },
            {
                key: 'redo',
                command: redo,
                dom: makeMenuButton('redo', _t('commands.redo', { shortcut: getShortcut('Mod-Y') }), 'redo-btn', ['sm:d-inline-block']),
                visible: () => false,
            },
        ],
        visible: () => false,
        classes: ['sm:d-inline-block'],
    },
    {
        name: 'other',
        priority: 40,
        entries: [
            // TODO eventually this will mimic the "help" dropdown in the prod editor
            makeMenuLinkEntry('Help', _t('commands.help'), options.editorHelpLink, 'help-link'),
        ],
    },
];

function allKeymaps$1(parserFeatures) {
    const commonmarkKeymap = keymap({
        ...bindLetterKeymap('Mod-z', undo),
        ...bindLetterKeymap('Mod-y', redo),
        ...bindLetterKeymap('Mod-Shift-z', redo),
        Tab: indentCommand,
        'Shift-Tab': indentCommand,
        ...bindLetterKeymap('Mod-b', boldCommand),
        ...bindLetterKeymap('Mod-i', emphasisCommand),
        ...bindLetterKeymap('Mod-l', insertLinkCommand$1),
        ...bindLetterKeymap('Ctrl-q', blockquoteCommand),
        ...bindLetterKeymap('Mod-k', inlineCodeCommand),
        ...bindLetterKeymap('Mod-g', insertImageCommand$1),
        ...bindLetterKeymap('Ctrl-g', insertImageCommand$1),
        ...bindLetterKeymap('Mod-o', orderedListCommand),
        ...bindLetterKeymap('Mod-u', unorderedListCommand),
        ...bindLetterKeymap('Mod-h', headerCommand),
        ...bindLetterKeymap('Mod-r', insertHorizontalRuleCommand$1),
        ...bindLetterKeymap('Mod-m', insertCodeblockCommand),
        // selectAll selects the outermost node and messes up our other commands
        ...bindLetterKeymap('Mod-a', selectAllTextCommand),
    });
    const tableKeymap = keymap({
        ...bindLetterKeymap('Mod-e', insertTableCommand$1),
    });
    const keymaps = [commonmarkKeymap, keymap(baseKeymap)];
    if (parserFeatures.tables)
        keymaps.unshift(tableKeymap);
    return keymaps;
}

// create a modified schema for commonmark
const commonmarkSchema = new Schema({
    nodes: {
        doc: {
            content: 'code_block+',
        },
        text: {
            group: 'inline',
        },
        code_block: {
            content: 'text*',
            group: 'block',
            marks: '',
            code: true,
            defining: true,
            isolating: true,
            // don't let the user select / delete
            selectable: false,
            // force the block language to always be markdown
            attrs: { params: { default: 'markdown' } },
            parseDOM: [
                {
                    tag: 'pre',
                    preserveWhitespace: 'full',
                },
            ],
            toDOM() {
                return ['pre', { class: 's-code-block e11-prose e11-prose-stone markdown' }, ['code', 0]];
            },
        },
    },
    marks: {},
});

/** Plugin that ensures that all commonmark editor contents are copied as plain text, not as code */
const textCopyHandlerPlugin = new Plugin({
    props: {
        clipboardSerializer: new DOMSerializer({
            code_block: (node) => node.textContent,
        }, {}),
    },
});

/**
 * Plugin that highlights markdown syntax using {@link @lezer/highlight}.
 * @param parserFeatures The additional parser features to add syntax highlighting for
 */
function markdownHighlightPlugin(parserFeatures) {
    /**
     * Custom classes for rendered tokens to match the hljs classes;
     * supports all tokens added by @lezer/highlight along with the ones we add in parser.configure below
     * @see {@link https://github.com/lezer-parser/markdown/blob/3c5f5dc3b5be08e19c32f0f7fac6f3619a58c911/src/markdown.ts#L1833}
     * Original class names can be found in {@link @lezer/highlight.classHighlighter}
     */
    const highlighter = tagHighlighter([
        // commonmark standard
        { tag: tags.quote, class: 'hljs-quote' },
        { tag: tags.contentSeparator, class: 'hljs-built_in' },
        { tag: tags.heading1, class: 'hljs-section' },
        { tag: tags.heading2, class: 'hljs-section' },
        { tag: tags.heading3, class: 'hljs-section' },
        { tag: tags.heading4, class: 'hljs-section' },
        { tag: tags.heading5, class: 'hljs-section' },
        { tag: tags.heading6, class: 'hljs-section' },
        { tag: tags.comment, class: 'hljs-comment' },
        { tag: tags.escape, class: 'hljs-literal' },
        { tag: tags.character, class: 'hljs-symbol' },
        { tag: tags.emphasis, class: 'hljs-emphasis' },
        { tag: tags.strong, class: 'hljs-strong' },
        { tag: tags.link, class: 'hljs-string' },
        { tag: tags.monospace, class: 'hljs-code' },
        { tag: tags.url, class: 'hljs-link' },
        { tag: tags.processingInstruction, class: 'hljs-symbol' },
        { tag: tags.labelName, class: 'hljs-string' },
        { tag: tags.string, class: 'hljs-string' },
        { tag: tags.tagName, class: 'hljs-tag' },
        // commonmark extensions
        { tag: tags.strikethrough, class: 'tok-strike' },
        { tag: tags.heading, class: 'hljs-strong' },
        // no highlighting
        { tag: tags.content, class: '' },
        { tag: tags.list, class: '' },
    ]);
    // extensions to the default commonmark parser/highlighter
    const extensions = [];
    const extensionProps = {};
    if (parserFeatures.extraEmphasis)
        extensions.push(Strikethrough);
    if (parserFeatures.tables)
        extensions.push(Table);
    if (parserFeatures.html)
        extensionProps['HTMLBlock HTMLTag'] = tags.tagName;
    return highlightPlugin({
        '*': parser.configure([
            {
                props: [styleTags(extensionProps)],
            },
            ...extensions,
        ]),
    }, ['code_block'], null, highlighter);
}

var ValidationResult;
(function (ValidationResult) {
    ValidationResult[ValidationResult["Ok"] = 0] = "Ok";
    ValidationResult[ValidationResult["FileTooLarge"] = 1] = "FileTooLarge";
    ValidationResult[ValidationResult["InvalidFileType"] = 2] = "InvalidFileType";
})(ValidationResult || (ValidationResult = {}));
async function defaultFileUploadHandler(file) { }
const fileUploader = (options) => new Plugin({
    props: {
        handlePaste: (view, event) => {
            const files = event.clipboardData?.files;
            if (files && files.length && options.handler) {
                options.handler(Array.from(files));
                return true;
            }
            return false;
        },
        handleDrop: (view, event) => {
            const files = event.dataTransfer?.files;
            if (files && files.length && options.handler) {
                options.handler(Array.from(files));
                return true;
            }
            return false;
        },
    },
});

function dragPlugin() {
    let leaving;
    function clear() {
        if (leaving != null) {
            clearTimeout(leaving);
            leaving = null;
        }
    }
    function start(view) {
        clear();
        if (!plugin.getState(view.state))
            view.dispatch(view.state.tr.setMeta(plugin, true));
    }
    let plugin = new Plugin({
        state: {
            init() {
                return false;
            },
            apply(tr, value) {
                let updated = tr.getMeta(plugin);
                return updated == null ? value : updated;
            },
        },
        props: {
            handleDOMEvents: {
                dragover: start,
                dragenter: start,
                drop(view) {
                    clear();
                    if (plugin.getState(view.state))
                        view.dispatch(view.state.tr.setMeta(plugin, false));
                },
                dragleave(view, e) {
                    if (e.target == view.dom && plugin.getState(view.state)) {
                        clear();
                        // @important: Can't be synchronous
                        // this will fire when the drag goes onto a child element,
                        // and we can't tell from the event whether that's the case.
                        leaving = setTimeout(() => {
                            view.dispatch(view.state.tr.setMeta(plugin, false));
                            leaving = null;
                        }, 50);
                    }
                },
            },
            attributes(state) {
                return plugin.getState(state) ? { class: 'ProseMirror-dragging e11-relative' } : { class: '' };
            },
        },
    });
    return plugin;
}

class CommonmarkEditor extends BaseView {
    constructor(target, content, pluginProvider, options = {}) {
        super();
        this.options = deepMerge(CommonmarkEditor.defaultOptions, options);
        const menuEntries = pluginProvider.getFinalizedMenu(createMenuEntries$1(this.options), EditorType.Commonmark, commonmarkSchema);
        const menu = createMenuPlugin(menuEntries, this.options.menuParentContainer);
        this.editorView = new EditorView((node) => {
            node.classList.add(...(this.options.classList || []));
            target.appendChild(node);
        }, {
            editable: editableCheck,
            state: EditorState.create({
                doc: this.parseContent(content),
                plugins: [
                    history(),
                    ...allKeymaps$1(this.options.parserFeatures),
                    menu,
                    createPreviewPlugin(this.options.preview, this.options.parserFeatures),
                    markdownHighlightPlugin(this.options.parserFeatures),
                    interfaceManagerPlugin(this.options.pluginParentContainer),
                    dragPlugin(),
                    fileUploader(this.options.fileUpload),
                    // commonmarkImageUpload(this.options.imageUpload, this.options.parserFeatures.validateLink),
                    placeholderPlugin(this.options.placeholderText),
                    readonlyPlugin(),
                    tripleClickHandler,
                    commonmarkCodePasteHandler,
                    textCopyHandlerPlugin,
                    ...pluginProvider.plugins.commonmark,
                ],
            }),
            plugins: [],
        });
        log('prosemirror commonmark document', this.editorView.state.doc.toJSON());
    }
    static get defaultOptions() {
        return {
            // set to null to disable by default
            editorHelpLink: null,
            menuParentContainer: null,
            parserFeatures: defaultParserFeatures,
            placeholderText: null,
            imageUpload: {
                //@ts-ignore
                handler: defaultImageUploadHandler,
            },
            preview: {
                enabled: false,
                parentContainer: null,
                renderer: null,
            },
        };
    }
    parseContent(content) {
        return CodeStringParser.fromSchema(commonmarkSchema).parseCode(content);
    }
    serializeContent() {
        return CodeStringParser.toString(this.editorView.state.doc);
    }
}

/**
 *
 * @param {String} mentionTrigger
 * @param {String} hashtagTrigger
 * @param {bool} allowSpace
 * @returns {Object}
 */
function getRegexp(mentionTrigger, hashtagTrigger, allowSpace) {
    var mention = allowSpace
        ? new RegExp('(^|\\s)' + mentionTrigger + '([\\w-\\+]+\\s?[\\w-\\+]*)$')
        : new RegExp('(^|\\s)' + mentionTrigger + '([\\w-\\+]+)$');
    // hashtags should never allow spaces. I mean, what's the point of allowing spaces in hashtags?
    var tag = new RegExp('(^|\\s)' + hashtagTrigger + '([\\w-]+)$');
    return {
        mention: mention,
        tag: tag,
    };
}
/**
 *
 * @param {ResolvedPosition} $position https://prosemirror.net/docs/ref/#model.Resolved_Positions
 * @param {JSONObject} opts
 * @returns {JSONObject}
 */
function getMatch($position, opts) {
    // take current para text content upto cursor start.
    // this makes the regex simpler and parsing the matches easier.
    var parastart = $position.before();
    const text = $position.doc.textBetween(parastart, $position.pos, '\n', '\0');
    var regex = getRegexp(opts.mentionTrigger, opts.hashtagTrigger, opts.allowSpace);
    // only one of the below matches will be true.
    var mentionMatch = text.match(regex.mention);
    var tagMatch = text.match(regex.tag);
    var match = mentionMatch || tagMatch;
    // set type of match
    var type;
    if (mentionMatch) {
        type = 'mention';
    }
    else if (tagMatch) {
        type = 'tag';
    }
    // if match found, return match with useful information.
    if (match) {
        // adjust match.index to remove the matched extra space
        match.index = match[0].startsWith(' ') ? match.index + 1 : match.index;
        match[0] = match[0].startsWith(' ') ? match[0].substring(1, match[0].length) : match[0];
        // The absolute position of the match in the document
        var from = $position.start() + match.index;
        var to = from + match[0].length;
        var queryText = match[2];
        return {
            range: { from: from, to: to },
            queryText: queryText,
            type: type,
        };
    }
    // else if no match don't return anything.
}
/**
 * Util to debounce call to a function.
 * >>> debounce(function(){}, 1000, this)
 */
const debounce = (function () {
    let timeoutId = null;
    return function (func, timeout, context) {
        //@ts-ignore
        context = context || this;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(function () {
            func.apply(context, arguments);
        }, timeout);
        return timeoutId;
    };
})();
let getNewState = function () {
    return {
        active: false,
        range: {
            from: 0,
            to: 0,
        },
        type: '', //mention or tag
        text: '',
        //@ts-ignore
        suggestions: [],
        index: 0, // current active suggestion index
    };
};
/**
 * @param {JSONObject} opts
 * @returns {Plugin}
 */
function getMentionsPlugin(opts) {
    // default options
    let defaultOpts = {
        mentionTrigger: '@',
        hashtagTrigger: '#',
        allowSpace: true,
        getSuggestions: (type, text, cb) => {
            cb([]);
        },
        getSuggestionsHTML: (items) => '<div class="suggestion-item-list">' + items.map((i) => '<div class="suggestion-item">' + i.name + '</div>').join('') + '</div>',
        activeClass: 'suggestion-item-active',
        suggestionTextClass: 'prosemirror-suggestion',
        maxNoOfSuggestions: 10,
        delay: 500,
    };
    var opts = Object.assign({}, defaultOpts, opts);
    // timeoutId for clearing debounced calls
    var showListTimeoutId = null;
    // dropdown element
    var el = document.createElement('div');
    // current Idx
    var index = 0;
    // ----- methods operating on above properties -----
    var showList = function (view, state, suggestions, opts) {
        el.innerHTML = opts.getSuggestionsHTML(suggestions, state.type);
        // attach new item event handlers
        el.querySelectorAll('.suggestion-item').forEach(function (itemNode, index) {
            itemNode.addEventListener('click', function () {
                select(view, state, opts);
                view.focus();
            });
            // TODO: setIndex() needlessly queries.
            // We already have the itemNode. SHOULD OPTIMIZE.
            itemNode.addEventListener('mouseover', function () {
                setIndex(index, state, opts);
            });
            itemNode.addEventListener('mouseout', function () {
                setIndex(index, state, opts);
            });
        });
        // highlight first element by default - like Facebook.
        addClassAtIndex(state.index, opts.activeClass);
        // get current @mention span left and top.
        // TODO: knock off domAtPos usage. It's not documented and is not officially a public API.
        // It's used currently, only to optimize the the query for textDOM
        var node = view.domAtPos(view.state.selection.$from.pos);
        var paraDOM = node.node;
        var textDOM = paraDOM.querySelector('.' + opts.suggestionTextClass);
        // TODO: should add null check case for textDOM
        var offset = textDOM.getBoundingClientRect();
        // TODO: think about outsourcing this positioning logic as options
        document.body.appendChild(el);
        el.classList.add('suggestion-item-container');
        el.style.position = 'fixed';
        el.style.left = offset.left + 'px';
        var top = textDOM.offsetHeight + offset.top;
        el.style.top = top + 'px';
        el.style.display = 'block';
        el.style.zIndex = '999999';
    };
    var hideList = function () {
        el.style.display = 'none';
    };
    var removeClassAtIndex = function (index, className) {
        var itemList = el.querySelector('.suggestion-item-list').childNodes;
        var prevItem = itemList[index];
        prevItem.classList.remove(className);
    };
    var addClassAtIndex = function (index, className) {
        var itemList = el.querySelector('.suggestion-item-list').childNodes;
        var prevItem = itemList[index];
        prevItem.classList.add(className);
    };
    var setIndex = function (index, state, opts) {
        removeClassAtIndex(state.index, opts.activeClass);
        state.index = index;
        addClassAtIndex(state.index, opts.activeClass);
    };
    var goNext = function (view, state, opts) {
        removeClassAtIndex(state.index, opts.activeClass);
        state.index++;
        state.index = state.index === state.suggestions.length ? 0 : state.index;
        addClassAtIndex(state.index, opts.activeClass);
    };
    var goPrev = function (view, state, opts) {
        removeClassAtIndex(state.index, opts.activeClass);
        state.index--;
        state.index = state.index === -1 ? state.suggestions.length - 1 : state.index;
        addClassAtIndex(state.index, opts.activeClass);
    };
    var select = function (view, state, opts) {
        var item = state.suggestions[state.index];
        var attrs;
        if (state.type === 'mention') {
            attrs = {
                name: item.name,
                id: item.id,
                email: item.email,
            };
        }
        else {
            attrs = {
                tag: item.tag,
            };
        }
        var node = view.state.schema.nodes[state.type].create(attrs);
        var tr = view.state.tr.replaceWith(state.range.from, state.range.to, node);
        // Insert a non-breaking space after the node
        // This method finds the position right after the inserted node and inserts the space there
        var spacePos = tr.selection.to;
        if (!tr.doc.resolve(spacePos).nodeAfter) {
            // Insert at the end of the current node
            tr = tr.insert(spacePos, view.state.schema.text('\u00A0')); // \u00A0 is the unicode for non-breaking space
        }
        else {
            // Insert right after the node
            tr = tr.insert(spacePos + 1, view.state.schema.text('\u00A0'));
        }
        //var newState = view.state.apply(tr);
        //view.updateState(newState);
        view.dispatch(tr);
    };
    /**
     * See https://prosemirror.net/docs/ref/#state.Plugin_System
     * for the plugin properties spec.
     */
    return new Plugin({
        key: new PluginKey('autosuggestions'),
        // we will need state to track if suggestion dropdown is currently active or not
        state: {
            init() {
                return getNewState();
            },
            apply(tr, state) {
                // compute state.active for current transaction and return
                var newState = getNewState();
                var selection = tr.selection;
                if (selection.from !== selection.to) {
                    return newState;
                }
                const $position = selection.$from;
                const match = getMatch($position, opts);
                // if match found update state
                if (match) {
                    newState.active = true;
                    newState.range = match.range;
                    newState.type = match.type;
                    newState.text = match.queryText;
                }
                return newState;
            },
        },
        // We'll need props to hi-jack keydown/keyup & enter events when suggestion dropdown
        // is active.
        props: {
            handleKeyDown(view, e) {
                var state = this.getState(view.state);
                // don't handle if no suggestions or not in active mode
                if (state && !state.active && !state.suggestions.length) {
                    return false;
                }
                // if any of the below keys, override with custom handlers.
                var down, up, enter, esc;
                enter = e.keyCode === 13;
                down = e.keyCode === 40;
                up = e.keyCode === 38;
                esc = e.keyCode === 27;
                if (down) {
                    goNext(view, state, opts);
                    return true;
                }
                else if (up) {
                    goPrev(view, state, opts);
                    return true;
                }
                else if (enter) {
                    select(view, state, opts);
                    return true;
                }
                else if (esc) {
                    clearTimeout(showListTimeoutId);
                    hideList();
                    // this.
                    state = getNewState();
                    return true;
                }
                else {
                    // didn't handle. handover to prosemirror for handling.
                    return false;
                }
            },
            // to decorate the currently active @mention text in ui
            decorations(editorState) {
                const { active, range } = this.getState(editorState);
                if (!active)
                    return null;
                return DecorationSet.create(editorState.doc, [
                    Decoration.inline(range.from, range.to, {
                        nodeName: 'span',
                        class: opts.suggestionTextClass,
                    }),
                ]);
            },
        },
        // To track down state mutations and add dropdown reactions
        view() {
            return {
                update: (view) => {
                    var state = this.key?.getState(view.state);
                    if (!state.text) {
                        hideList();
                        clearTimeout(showListTimeoutId);
                        return;
                    }
                    // debounce the call to avoid multiple requests
                    showListTimeoutId = debounce(function () {
                        // get suggestions and set new state
                        opts.getSuggestions(state.type, state.text, function (suggestions) {
                            // update `state` argument with suggestions
                            state.suggestions = suggestions;
                            showList(view, state, suggestions, opts);
                        });
                    }, opts.delay, this);
                },
            };
        },
    });
}
/**
 * See https://prosemirror.net/docs/ref/#model.NodeSpec
 */
const mentionNode = {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: {
        id: '',
        name: '',
        email: '',
    },
    selectable: false,
    draggable: false,
    toDOM: (node) => {
        return [
            'span',
            {
                'data-mention-id': node.attrs.id,
                'data-mention-name': node.attrs.name,
                'data-mention-email': node.attrs.email,
                class: 'prosemirror-mention-node',
            },
            '@' + node.attrs.name || node.attrs.email,
        ];
    },
    parseDOM: [
        {
            // match tag with following CSS Selector
            tag: 'span[data-mention-id][data-mention-name][data-mention-email]',
            getAttrs: (dom) => {
                var id = dom.getAttribute('data-mention-id');
                var name = dom.getAttribute('data-mention-name');
                var email = dom.getAttribute('data-mention-email');
                return {
                    id: id,
                    name: name,
                    email: email,
                };
            },
        },
    ],
};
/**
 * See https://prosemirror.net/docs/ref/#model.NodeSpec
 */
const tagNode = {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: {
        tag: '',
    },
    selectable: false,
    draggable: false,
    toDOM: (node) => {
        return [
            'span',
            {
                'data-tag': node.attrs.tag,
                class: 'prosemirror-tag-node',
            },
            '#' + node.attrs.tag,
        ];
    },
    parseDOM: [
        {
            // match tag with following CSS Selector
            tag: 'span[data-tag]',
            getAttrs: (dom) => {
                var tag = dom.getAttribute('data-tag');
                return {
                    tag: tag,
                };
            },
        },
    ],
};
// import { tagNode, mentionNode } from "./nodes";
/**
 *
 * @param {OrderedMap} nodes
 * @returns {OrderedMap}
 */
function addMentionNodes(nodes) {
    return nodes.append({
        mention: mentionNode,
    });
}
/**
 *
 * @param {OrderedMap} nodes
 * @returns {OrderedMap}
 */
function addTagNodes(nodes) {
    return nodes.append({
        tag: tagNode,
    });
}

const getMentionSuggestionsHTML = (items) => '<div class="suggestion-item-list">' + items.map((i) => '<div class="suggestion-item">' + i.name + '</div>').join('') + '</div>';
const getTagSuggestionsHTML = (items) => '<div class="suggestion-item-list">' + items.map((i) => '<div class="suggestion-item">' + i.tag + '</div>').join('') + '</div>';
const mentionPlugin = (mentionConfig) => getMentionsPlugin({
    getSuggestions: (type, text, done) => {
        setTimeout(() => {
            if (type === 'mention') {
                // call mention suggestions service
                if (mentionConfig) {
                    mentionConfig.autoComplete(text).then((result) => {
                        done(result);
                    });
                }
            }
            else {
                // call tags if it exists
                //   done([{ tag: 'WikiLeaks' }, { tag: 'NetNeutrality' }])
            }
        }, 0);
    },
    getSuggestionsHTML: (items, type) => {
        if (type === 'mention') {
            return getMentionSuggestionsHTML(items);
        }
        else if (type === 'tag') {
            return getTagSuggestionsHTML(items);
        }
    },
});

/* eslint-disable @typescript-eslint/no-var-requires, @typescript-eslint/ban-ts-comment */
/*
 * NOTE: This file is only for local development / generic bundles
 * The prod build configs completely remove highlight.js from the bundle,
 * expecting it to be supplied globally as `window.hljs`, already configured and ready to go
 */
/** Attempts to get the optional highlight.js instance */
function getHljsInstance() {
    //@ts-ignore
    let hljs = globalThis.hljs;
    if (!hljs) {
        try {
            hljs = require('highlight.js');
        }
        catch {
            // Failed to import optional dependency. Do nothing :)
        }
    }
    //@ts-ignore
    return hljs && hljs.highlight ? hljs : null;
}

// Aliases are neatly grouped onto the same line, so tell prettier not to format
// prettier-ignore
/**
 * A mapping of all known language aliases to their proper definition name
 * @see https://meta.stackexchange.com/questions/184108/what-is-syntax-highlighting-and-how-does-it-work
 */
const languageAliases = {
    bsh: 'bash',
    csh: 'bash',
    sh: 'bash',
    // TODO is cpp appropriate or is there a better c-like?
    c: 'cpp',
    cc: 'cpp',
    cxx: 'cpp',
    cyc: 'cpp',
    m: 'cpp',
    cs: 'csharp',
    coffee: 'coffeescript',
    html: 'xml',
    xsl: 'xml',
    js: 'javascript',
    pl: 'perl',
    py: 'python',
    cv: 'python',
    rb: 'ruby',
    clj: 'clojure',
    erl: 'erlang',
    hs: 'haskell',
    mma: 'mathematica',
    tex: 'latex',
    cl: 'lisp',
    el: 'lisp',
    lsp: 'lisp',
    scm: 'scheme',
    ss: 'scheme',
    rkt: 'scheme',
    fs: 'ocaml',
    ml: 'ocaml',
    s: 'r',
    rc: 'rust',
    rs: 'rust',
    vhd: 'vhdl',
    none: 'plaintext',
};
/**
 * Attempts to dealias a language's name into a name we can load/register under
 * @param rawLanguage
 */
function dealiasLanguage(rawLanguage) {
    return (languageAliases[rawLanguage] || rawLanguage);
}
/**
 * Gets the language string from a code_block node
 * @param block The block to get the language string from
 */
function getBlockLanguage(block, fallback = 'none') {
    // commonmark spec suggests that the "first word" in a fence's info string is the language
    // https://spec.commonmark.org/0.29/#info-string
    // https://spec.commonmark.org/0.29/#example-112
    const rawInfoString = block.attrs.params || '';
    const rawLanguage = rawInfoString.split(/\s/)[0].toLowerCase() || fallback || null;
    // attempt to dealias the language before sending out to the highlighter
    return dealiasLanguage(rawLanguage);
}
/**
 * Plugin that highlights all code within all code_blocks in the parent
 */
function CodeBlockHighlightPlugin(defaultFallbackLanguage) {
    const extractor = (block) => {
        const detectedLanguage = block.attrs.detectedHighlightLanguage;
        return detectedLanguage || getBlockLanguage(block, defaultFallbackLanguage);
    };
    const hljs = getHljsInstance();
    // if hljs fails to instantiate (optional dependency), don't crash the entire editor
    if (!hljs)
        return new Plugin({});
    return highlightPlugin$1(hljs, ['code_block'], extractor);
}

class SOMarkdownSerializerState extends MarkdownSerializerState {
    constructor(nodes, marks, options) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error TODO constructor not exposed in types
        super(nodes, marks, options);
        this.linkReferenceDefinitions = {};
    }
    /** Adds a link reference definition for rendering at the very end */
    addLinkReferenceDefinition(label, href, title) {
        const normalizedLabel = normalizeReference(label);
        if (this.linkReferenceDefinitions[normalizedLabel])
            return;
        this.linkReferenceDefinitions[normalizedLabel] = {
            href,
            title: title || 'Click here',
        };
    }
    /** Writes all saved linked reference definitions to the state */
    writeLinkReferenceDefinitions() {
        let refs = Object.keys(this.linkReferenceDefinitions);
        if (!refs.length)
            return;
        // typically, users want numbered references to sort numerically instead of alphabetically
        // i.e. `1, 2, 10` vs `1, 10, 2`
        const numberRefs = [];
        const otherRefs = [];
        refs.forEach((ref) => {
            if (!isNaN(Number(ref)))
                numberRefs.push(+ref);
            else
                otherRefs.push(ref);
        });
        // sort first by number, then by string
        refs = [...numberRefs.sort((a, b) => a - b).map((r) => r.toString()), ...otherRefs.sort()];
        refs.forEach((r) => {
            const def = this.linkReferenceDefinitions[r];
            this.ensureNewLine();
            this.write('[');
            this.text(r);
            this.write(']: ');
            this.text(def.href);
            if (def.title)
                this.text(` "${def.title}"`);
        });
    }
}
class SOMarkdownSerializer extends MarkdownSerializer {
    serialize(content, options) {
        const state = new SOMarkdownSerializerState(this.nodes, this.marks, options || {});
        state.renderContent(content);
        state.writeLinkReferenceDefinitions();
        return state.out;
    }
}
/** Renders a node as wrapped in html tags if the markup attribute is html */
function renderHtmlTag(state, node, tagType) {
    const markup = node.attrs['markup'];
    if (!markup)
        return false;
    // TODO naive check for now, but we should probably do something more robust
    if (!markup.startsWith('<') || !markup.endsWith('>'))
        return false;
    const tag = markup.replace(/[<>/\s]/g, '');
    const openingTagStart = `<${tag}`;
    // start writing the opening tag
    state.text(openingTagStart, false);
    // write the attributes if necessary
    if (supportedTagAttributes[tagType]) {
        // render the attributes in alpha order, since we cannot know what order they were originally written in
        const attributes = supportedTagAttributes[tagType].sort();
        for (const attr of attributes) {
            const value = node.attrs[attr];
            if (value)
                state.text(` ${attr}="${value}"`);
        }
    }
    // if the tag is self closing, just render the closing part of the original markup and return early
    if (selfClosingElements.includes(tagType)) {
        state.text(markup.replace(openingTagStart, ''), false);
        return true;
    }
    // close the opening tag
    state.text('>', false);
    // TODO will this always be inline content?
    state.renderInline(node);
    // @ts-expect-error TODO when writing to a closed block, it injects newline chars...
    state.closed = false;
    state.text(`</${tag}>`, false);
    state.closeBlock(node);
    return true;
}
// TODO There's no way to sanely override these without completely rewriting them
// TODO Should contribute this back upstream and remove
const defaultMarkdownSerializerNodes = {
    ...defaultMarkdownSerializer.nodes,
    blockquote(state, node) {
        if (renderHtmlTag(state, node, TagType.blockquote))
            return;
        const markup = node.attrs['markup'] || '>';
        state.wrapBlock(`${markup} `, null, node, () => state.renderContent(node));
    },
    mention(state, node) {
        state.text(`<@${node.attrs.id}>`);
    },
    code_block(state, node) {
        // TODO could be html...
        const markup = node.attrs['markup'] || '```';
        // indented code blocks have their markup set to "indented" instead of empty
        if (markup === 'indented') {
            const lines = node.textContent.split('\n');
            lines.forEach((l, i) => {
                if (i > 0)
                    state.ensureNewLine();
                state.text(`    ${l}`, false);
            });
        }
        else {
            state.write(`${markup + (node.attrs['params'] || '')}\n`);
            state.text(node.textContent, false);
            state.ensureNewLine();
            state.write(markup);
        }
        state.closeBlock(node);
    },
    heading(state, node) {
        const markup = node.attrs['markup'] || '';
        // eslint-disable-next-line no-empty
        if (renderHtmlTag(state, node, TagType.heading)) {
        }
        else if (markup && !markup.startsWith('#')) {
            // "underlined" heading (Setext heading)
            state.renderInline(node);
            state.ensureNewLine();
            state.write(markup); // TODO write once or write the entire length of the text?
            state.closeBlock(node);
        }
        else {
            // markup is # (ATX heading) or empty
            state.write(`${state.repeat('#', node.attrs['level'])} `);
            state.renderInline(node);
            state.closeBlock(node);
        }
    },
    horizontal_rule(state, node) {
        if (renderHtmlTag(state, node, TagType.horizontal_rule))
            return;
        state.write(node.attrs['markup'] || '----------');
        state.closeBlock(node);
    },
    bullet_list(state, node) {
        if (renderHtmlTag(state, node, TagType.unordered_list))
            return;
        const markup = node.attrs['markup'] || '-';
        state.renderList(node, '  ', () => `${markup} `);
    },
    ordered_list(state, node) {
        if (renderHtmlTag(state, node, TagType.ordered_list))
            return;
        const start = node.attrs['order'] || 1;
        const maxW = String(start + node.childCount - 1).length;
        const space = state.repeat(' ', maxW + 2);
        const markup = node.attrs['markup'] || '.';
        state.renderList(node, space, (i) => {
            const nStr = String(start + i);
            return `${state.repeat(' ', maxW - nStr.length) + nStr + markup} `;
        });
    },
    list_item(state, node) {
        if (renderHtmlTag(state, node, TagType.list_item))
            return;
        state.renderContent(node);
    },
    paragraph(state, node) {
        if (renderHtmlTag(state, node, TagType.paragraph))
            return;
        state.renderInline(node);
        state.closeBlock(node);
    },
    image(state, node) {
        if (renderHtmlTag(state, node, TagType.image))
            return;
        const title = node.attrs['title']
            ? // eslint-disable-next-line operator-linebreak
                // @ts-expect-error TODO types might be wrong here
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/restrict-plus-operands
                ` ${state.quote(node.attrs.title)}`
            : '';
        state.write(`![${state.esc(node.attrs['alt'] || '')}](${state.esc(node.attrs['src'])}${title})`);
    },
    hard_break(state, node, parent, index) {
        if (renderHtmlTag(state, node, TagType.hardbreak))
            return;
        for (let i = index + 1; i < parent.childCount; i++) {
            // eslint-disable-next-line eqeqeq
            if (parent.child(i).type != node.type) {
                // `[space][space][newline]` or `\[newline]`
                state.write(node.attrs['markup'] || '  \n');
                return;
            }
        }
    },
    text(state, node) {
        const linkMark = node.marks.find((m) => m.type.name === 'link');
        let text = node.text;
        // if the text node is from a link, use the original href text if the original markup used it
        if (['linkify', 'autolink'].includes(linkMark?.attrs['markup']))
            text = linkMark.attrs['href'];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const startOfLine = state.atBlank() || state.closed;
        // escape the text using the built in escape code
        let escapedText = state.esc(text || '', startOfLine);
        // built in escape doesn't get all the cases TODO upstream!
        escapedText = escapedText.replace(/\\_/g, '_').replace(/\b_|_\b/g, '\\_');
        escapedText = escapedText.replace(/([<>])/g, '\\$1');
        state.text(escapedText, false);
    },
};
// extend the default markdown serializer's nodes and add our own
const customMarkdownSerializerNodes = {
    html_inline(state, node) {
        state.write(node.attrs['content']);
    },
    mention(state, node) {
        state.text(`<@${node.attrs.id}>`);
    },
    html_block(state, node) {
        state.write(node.attrs['content']);
        state.closeBlock(node);
    },
    // TODO
    html_block_container(state, node) {
        state.write(node.attrs['contentOpen']);
        // ensure the opening content had a newline and write a newline
        // since that terminated the html_block and caused the container creation
        state.ensureNewLine();
        state.write('\n');
        state.renderContent(node);
        state.write(node.attrs['contentClose']);
        state.closeBlock(node);
    },
    // write softbreaks back to a newline character
    softbreak(state) {
        state.write('\n');
    },
    table(state, node) {
        const schema = node.type.schema;
        function serializeTableHead(head) {
            let columnAlignments = [];
            head.forEach((headRow) => {
                if (headRow.type === schema.nodes['table_row'])
                    columnAlignments = serializeTableRow(headRow);
            });
            state.ensureNewLine();
            // write table header separator
            for (const alignment of columnAlignments) {
                state.write('|');
                state.write(alignment === 'left' || alignment === 'center' ? ':' : ' ');
                state.write('---');
                state.write(alignment === 'right' || alignment === 'center' ? ':' : ' ');
            }
            state.write('|');
        }
        function serializeTableBody(body) {
            body.forEach((bodyRow) => {
                if (bodyRow.type === schema.nodes.table_row)
                    serializeTableRow(bodyRow);
            });
        }
        function serializeTableRow(row) {
            const columnAlignment = [];
            state.ensureNewLine();
            row.forEach((cell) => {
                if (cell.type === schema.nodes.table_header || cell.type === schema.nodes.table_cell) {
                    const alignment = serializeTableCell(cell);
                    columnAlignment.push(alignment || '');
                }
            });
            state.write('|');
            return columnAlignment;
        }
        function serializeTableCell(cell) {
            state.write('| ');
            state.renderInline(cell);
            state.write(' ');
            return findAlignment(cell);
        }
        function findAlignment(cell) {
            const alignment = cell.attrs.style;
            if (!alignment)
                return null;
            // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
            const match = alignment.match(/text-align:[ ]?(left|right|center)/);
            if (match && match[1])
                return match[1];
            return null;
        }
        node.forEach((table_child) => {
            if (table_child.type === schema.nodes.table_head)
                serializeTableHead(table_child);
            if (table_child.type === schema.nodes.table_body)
                serializeTableBody(table_child);
        });
        state.closeBlock(node);
    },
    tagLink(state, node) {
        const isMeta = node.attrs.tagType === 'meta-tag';
        const prefix = isMeta ? 'meta-tag' : 'tag';
        const tag = node.attrs.tagName;
        state.write(`[${prefix}:${tag}]`);
    },
    spoiler(state, node) {
        state.wrapBlock('>! ', null, node, () => state.renderContent(node));
    },
};
/**
 * Generates a config from a base config that is aware of special "markup" added by the markdown tokenizer;
 * typically this will be differences in how markdown can be written (e.g. * vs _ for emphasis),
 * but could also be html tags from our extended html support plugin (e.g. * vs <em> for emphasis)
 * @param config The base config to extend
 */
function genMarkupAwareMarkConfig(config) {
    // we don't support function open/close since these could have fairly complicated logic in them
    if (config.open instanceof Function || config.close instanceof Function) {
        // log an error to the console and return the unmodified base config
        error('markdown-serializer genMarkupAwareMarkSpec', 'Unable to extend mark config with open/close as functions', config);
        return config;
    }
    return {
        ...config,
        open(_, mark) {
            const markup = mark.attrs.markup;
            return markup || config.open;
        },
        close(_, mark) {
            let markup = mark.attrs.markup;
            // insert the `/` on html closing tags
            markup = /^<[a-z]+>$/i.test(markup) ? markup.replace(/^</, '</') : markup;
            return markup || config.close;
        },
    };
}
// add support for html/linkify marked-up links (defaulting back to the default serializer otherwise)
const defaultLinkMarkDeserializer = defaultMarkdownSerializer.marks.link;
const extendedLinkMarkDeserializer = {
    open(state, mark, parent, index) {
        if (!mark.attrs.markup) {
            return typeof defaultLinkMarkDeserializer.open === 'string'
                ? defaultLinkMarkDeserializer.open
                : defaultLinkMarkDeserializer.open(state, mark, parent, index);
        }
        if (mark.attrs.markup === 'reference') {
            ;
            state.addLinkReferenceDefinition(mark.attrs.referenceLabel, mark.attrs.href, mark.attrs.title);
            return '[';
        }
        // linkify detected links are left bare
        if (mark.attrs.markup === 'linkify')
            return '';
        if (mark.attrs.markup === 'autolink')
            return '<';
        const titleAttr = mark.attrs.title ? ` title="${mark.attrs.title}"` : '';
        const hrefAttr = mark.attrs.href ? ` href="${mark.attrs.href}"` : '';
        return `<a${hrefAttr}${titleAttr}>`;
    },
    close(state, mark, parent, index) {
        if (!mark.attrs.markup) {
            return typeof defaultLinkMarkDeserializer.close === 'string'
                ? defaultLinkMarkDeserializer.close
                : defaultLinkMarkDeserializer.close(state, mark, parent, index);
        }
        if (mark.attrs.markup === 'reference') {
            switch (mark.attrs.referenceType) {
                case 'full':
                    return `][${mark.attrs.referenceLabel}]`;
                case 'collapsed':
                    return '][]';
                case 'shortcut':
                default:
                    return ']';
            }
        }
        // linkify detected links are left bare
        if (mark.attrs.markup === 'linkify')
            return '';
        if (mark.attrs.markup === 'autolink')
            return '>';
        return '</a>';
    },
};
// add support for <code> markup
const defaultCodeMarkDeserializer = defaultMarkdownSerializer.marks.code;
const extendedCodeMarkDeserializer = {
    open(state, mark, parent, index) {
        if (typeof defaultCodeMarkDeserializer.open === 'string') {
            return mark.attrs.markup || defaultCodeMarkDeserializer.open;
        }
        // run the backing method to get where the markup should be placed
        // TODO the types are incorrect, the correct return type is "string", not "void"
        let defaultResult = defaultCodeMarkDeserializer.open(state, mark, parent, index);
        if (mark.attrs.markup)
            defaultResult = defaultResult.replace('`', mark.attrs.markup);
        return defaultResult;
    },
    close(state, mark, parent, index) {
        if (typeof defaultCodeMarkDeserializer.close === 'string') {
            return mark.attrs.markup || defaultCodeMarkDeserializer.close;
        }
        // run the backing method to get where the markup should be placed
        // TODO the types are incorrect, the correct return type is "string", not "void"
        let defaultResult = defaultCodeMarkDeserializer.close(state, mark, parent, index);
        if (mark.attrs.markup) {
            // insert the `/` on html closing tags
            const markup = mark.attrs.markup.replace(/^</, '</');
            defaultResult = defaultResult.replace('`', markup);
        }
        return defaultResult;
    },
    escape: defaultCodeMarkDeserializer.escape,
};
// extend the default markdown serializer's marks and add our own
const customMarkdownSerializerMarks = {
    ...defaultMarkdownSerializer.marks,
    em: genMarkupAwareMarkConfig(defaultMarkdownSerializer.marks.em),
    strong: genMarkupAwareMarkConfig(defaultMarkdownSerializer.marks.strong),
    code: extendedCodeMarkDeserializer,
    link: extendedLinkMarkDeserializer,
    strike: genMarkupAwareMarkConfig({
        open: '~~',
        close: '~~',
        mixable: true,
        expelEnclosingWhitespace: true,
    }),
    kbd: genMarkupAwareMarkConfig({
        open: '<kbd>',
        close: '</kbd>',
        mixable: true,
        expelEnclosingWhitespace: true,
    }),
    sup: genMarkupAwareMarkConfig({
        open: '<sup>',
        close: '</sup>',
        mixable: true,
        expelEnclosingWhitespace: true,
    }),
    sub: genMarkupAwareMarkConfig({
        open: '<sub>',
        close: '</sub>',
        mixable: true,
        expelEnclosingWhitespace: true,
    }),
};
// export our custom serializer using the extended nodes/marks taken from the default schema
const e11MarkdownSerializer = (externalPluginProvider) => new SOMarkdownSerializer({
    ...defaultMarkdownSerializerNodes,
    ...customMarkdownSerializerNodes,
    ...externalPluginProvider.markdownProps.serializers.nodes,
}, {
    ...customMarkdownSerializerMarks,
    ...externalPluginProvider.markdownProps.serializers.marks,
});

// matches: `some text`, but not ` text `
const inlineCodeRegex = /`(\S(?:|.*?\S))`$/;
// matches: **some text**, but not ** text **
const strongRegexAsterisks = /\*\*(\S(?:|.*?\S))\*\*$/;
// matches: *some text*, but not **text* or * text *
const emphasisRegexAsterisk = /\*([^*\s]([^*])*[^*\s]|[^*\s])\*$/;
// matches: __some text__, but not __ text __
const strongRegexUnderscores = /__(\S(?:|.*?\S))__$/;
// matches: _some text_, but not __text_ or _ text _
const emphasisRegexUnderscore = /_([^_\s]([^_])*[^_\s]|[^_\s])_$/;
// matches: [ *any* thing ]( any thing )
const linkRegex = /\[(.+)\]\((.+)\)$/;
/**
 * Create an input rule that applies a mark to the text matched by a regular expression.
 * @param regexp The regular expression to match the text. The text to be wrapped in a mark needs to be marked by the _first_ capturing group.
 * @param markType The mark type to apply
 * @param getAttrs A function returning the attributes to be applied to the node
 * @param matchValidator An optional function that allows validating the match before applying the mark
 * @returns A mark input rule
 */
function markInputRule(regexp, markType, matchValidator, getAttrs) {
    return new InputRule(regexp, (state, match, start, end) => {
        const attrs = getAttrs ? getAttrs(match) : {};
        const tr = state.tr;
        // if the current node doesn't allow this mark, don't attempt to transform
        if (!state.doc.resolve(start).parent.type.allowsMarkType(markType))
            return null;
        // validate the match if a validator is given
        // and skip applying the mark if the validation fails
        if (matchValidator && !matchValidator(match))
            return null;
        const matchedString = match[0];
        const capturedGroup = match[1];
        if (capturedGroup) {
            const textStart = start + matchedString.indexOf(capturedGroup);
            const textEnd = textStart + capturedGroup.length;
            if (textEnd < end)
                tr.delete(textEnd, end);
            if (textStart > start)
                tr.delete(start, textStart);
            end = start + capturedGroup.length;
        }
        // add mark to matching text
        tr.addMark(start, end, markType.create(attrs));
        // don't use mark for new text that's gonna follow
        tr.removeStoredMark(markType);
        return tr;
    });
}
/**
 * Defines all input rules we're using in our rich-text editor.
 * Input rules are formatting operations that trigger as you type based on regular expressions
 *
 * Examples:
 *      * starting a line with "# " will turn the line into a headline
 *      * starting a line with "> " will insert a new blockquote in place
 */
const richTextInputRules = (schema, features) => {
    const blockquoteInputRule = wrappingInputRule(/^\s*>\s$/, schema.nodes.blockquote);
    const spoilerInputRule = wrappingInputRule(/^\s*>!\s$/, schema.nodes.spoiler);
    const headingInputRule = textblockTypeInputRule(
    // eslint-disable-next-line prefer-regex-literals
    new RegExp('^(#{1,3})\\s$'), schema.nodes.heading, (match) => ({ level: match[1].length }));
    const codeBlockRule = textblockTypeInputRule(/^```$/, schema.nodes.code_block);
    const unorderedListRule = wrappingInputRule(/^\s*[*+-]\s$/, schema.nodes.bullet_list);
    const orderedListRule = wrappingInputRule(/^\s*\d(\.|\))\s$/, schema.nodes.ordered_list, (match) => ({ order: +match[1] }), 
    // eslint-disable-next-line eqeqeq
    (match, node) => node.childCount + node.attrs.order == +match[1]);
    const inlineCodeRule = markInputRule(inlineCodeRegex, schema.marks.code);
    const boldRule = markInputRule(strongRegexAsterisks, schema.marks.strong);
    const emphasisRule = markInputRule(emphasisRegexAsterisk, schema.marks.em, (match) => match.input.charAt(match.input.lastIndexOf(match[0]) - 1) !== '*');
    const boldUnderlineRule = markInputRule(strongRegexUnderscores, schema.marks.strong);
    const emphasisUnderlineRule = markInputRule(emphasisRegexUnderscore, schema.marks.em, (match) => match.input.charAt(match.input.lastIndexOf(match[0]) - 1) !== '_');
    const linkRule = (features) => markInputRule(linkRegex, schema.marks.link, (match) => features.validateLink(match[2]), // only apply link input rule, if the matched URL is valid,
    //@ts-ignore
    (match) => {
        return { href: match[2] };
    });
    return inputRules({
        rules: [
            blockquoteInputRule,
            spoilerInputRule,
            headingInputRule,
            codeBlockRule,
            unorderedListRule,
            orderedListRule,
            inlineCodeRule,
            boldRule,
            boldUnderlineRule,
            emphasisRule,
            emphasisUnderlineRule,
            linkRule(features),
        ],
    });
};
/**
 *  Start preparing emoji loader only once....
 */
const allGroups = [
    [0, '😀', 'smileys-emotion'],
    [1, '👋', 'people-body'],
].map(([id, emoji, name]) => ({ id, emoji, name }));
const groups = allGroups.slice(0);
const database = new Database();
let allEmojis = {};
// create a Map of most common emoji and emoticon for input replacement
groups.forEach(async (val) => {
    const result = await database.getEmojiByGroup(val.id);
    allEmojis = {
        ...allEmojis,
        ...result.reduce((prv, nxt) => {
            let vals = nxt.shortcodes.reduce((p, n) => {
                return {
                    ...p,
                    [n.toLowerCase()]: nxt.unicode,
                };
            }, {});
            if (nxt.emoticon) {
                vals = {
                    ...vals,
                    [nxt.emoticon.toLowerCase()]: nxt.unicode,
                };
            }
            return {
                ...prv,
                ...vals,
            };
        }, {}),
    };
});
// matches: emojis like :smile:
// const emojiInputRegex = /(?:^|\s)((?::)((?:\w+))(?::))$/ // match only emoji
// matches: emoticons and emojis like :smile:, :), :D
const emojiInputRegex = /(?:^|\s)((?::)((?:\w+|\S+))(?::|\s))$/; // also match emoticons
const emojiInputRules = (_schema) => {
    return inputRules({
        rules: [
            new InputRule(emojiInputRegex, (state, match, start, end) => {
                const [, , name] = match;
                const { tr } = state;
                let matchedEmoji = allEmojis[name.toLowerCase()] || allEmojis[`:${name.toLowerCase()}`]; // match emojis first then emoticons
                if (matchedEmoji) {
                    tr.replaceWith(start, end, [state.schema.text(`${matchedEmoji} `)]);
                    return tr;
                }
                return null;
            }),
        ],
    });
};

/**
 * PluginView for @see linkEditorPlugin
 * @internal Exported for testing
 */
class LinkEditor extends PluginInterfaceView {
    get hrefInput() {
        return this.viewContainer.querySelector('.js-link-editor-href');
    }
    get textInput() {
        return this.viewContainer.querySelector('.js-link-editor-text');
    }
    get saveBtn() {
        return this.viewContainer.querySelector('.js-link-editor-save-btn');
    }
    get hrefError() {
        return this.viewContainer.querySelector('.js-link-editor-href-error');
    }
    constructor(view, validateLink) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        super(LINK_EDITOR_KEY);
        this.validateLink = validateLink;
        const randomId = generateRandomId();
        this.viewContainer = document.createElement('form');
        this.viewContainer.className = 'js-link-editor';
        this.viewContainer.innerHTML = escapeHTML `<div class="e11-relative e11-z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">

    <div class="e11-fixed e11-inset-0 e11-z-[5] e11-bg-gray-500 e11-bg-opacity-75 e11-transition-opacity"></div>

    <div class="e11-fixed e11-z-10 e11-inset-0 e11-overflow-y-auto">
      <div class="e11-flex e11-items-end sm:e11-items-center e11-justify-center e11-min-h-full e11-p-4 e11-text-center sm:e11-p-0">

        <div class="e11-opacity-100 e11-translate-y-0 sm:e11-scale-100 e11-relative e11-bg-white e11-rounded-lg e11-px-4 e11-pt-5 e11-pb-4 e11-text-left e11-overflow-hidden e11-shadow-xl e11-transform e11-transition-all sm:e11-my-8 sm:e11-max-w-lg sm:e11-w-full sm:e11-p-6">
          <div class="e11-hidden sm:e11-block e11-absolute e11-top-0 e11-right-0 e11-pt-4 e11-pr-4">
            <button type="reset"  class="e11-bg-white e11-rounded-md e11-text-gray-400 hover:e11-text-gray-500 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-offset-2 focus:e11-ring-gray-300">
              <span class="e11-sr-only">Close</span>
              <span class="material-icons">close </span>
            </button>
          </div>
          <div class="e11-w-full sm:e11-flex sm:e11-items-start">
            <div class="e11-w-full e11-mt-3 e11-text-center sm:e11-mt-0 sm:e11-ml-4 sm:e11-text-left">
              <h3>Add Link</h3>
              <hr class="e11-mt-2" />
              <div class="e11-mt-2">
                <label for="link-editor-href-input-${randomId}" class="e11-block e11-text-sm e11-font-medium e11-text-skin-base">Link</label>
                <div class="e11-mt-1">
                  <input id="link-editor-href-input-${randomId}" type="text" name="href" class=" s-input js-link-editor-href  e11-shadow-sm focus:e11-ring-gray-300 focus:e11-border-gray-300 e11-block e11-w-full sm:e11-text-sm e11-border-gray-300 e11-rounded-md" placeholder="https://...">
                  <p id="link-editor-href-error-${randomId}" class="s-input-message mt4 d-none js-link-editor-href-error e11-text-skin-error e11-mt-1"></p>
                  </div>
              </div>

              <div class="e11-mt-2">
                <label for="link-text-href-input-${randomId}" class="e11-block e11-text-sm e11-font-medium e11-text-skin-base">Text</label>
                <div class="e11-mt-1">
                  <input type="text" name="text" id="link-text-href-input-${randomId}" class="js-link-editor-text  e11-shadow-sm focus:e11-ring-gray-300 focus:e11-border-gray-300 e11-block e11-w-full sm:e11-text-sm e11-border-gray-300 e11-rounded-md" placeholder="Text..">
                </div>
              </div>
            </div>
          </div>
          <div class="e11-mt-5 sm:e11-mt-4 sm:e11-flex sm:e11-flex-row-reverse">
          <button type="submit" disabled class="js-link-editor-save-btn e11-mt-3 e11-w-full e11-inline-flex e11-justify-center e11-rounded-md e11-border e11-border-gray-300 e11-shadow-sm e11-px-4 e11-py-2 e11-bg-blue-400 e11-text-base e11-font-medium e11-text-white hover:e11-text-gray-50 hover:e11-bg-blue-700 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-offset-2 focus:e11-bg-blue-700 sm:e11-mt-0 sm:e11-w-auto sm:e11-text-sm ">
          ${_t('link_editor.save_button')}
          </button>
            <button type="reset" class="e11-mt-3 e11-w-full e11-inline-flex e11-justify-center e11-rounded-md e11-border e11-border-gray-300 e11-shadow-sm e11-px-4 e11-py-2 e11-bg-white e11-text-base e11-font-medium e11-text-skin-base hover:e11-bg-gray-50 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-offset-2 focus:e11-ring-indigo-500 sm:e11-mt-0 sm:e11-w-auto sm:e11-text-sm">
            ${_t('link_editor.cancel_button')}
            </button>

          </div>
        </div>
      </div>
    </div>
  </div>`;
        this.viewContainer.addEventListener('submit', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleSave(view);
        });
        this.viewContainer.addEventListener('reset', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const tr = this.tryHideInterfaceTr(view.state);
            if (tr)
                view.dispatch(tr);
        });
        this.hrefInput.addEventListener('input', (e) => {
            this.validate(e.target.value);
        });
    }
    validate(href) {
        const valid = this.validateLink(href);
        if (!valid)
            this.showValidationError(_t('link_editor.validation_error'));
        else
            this.hideValidationError();
        this.saveBtn.disabled = !valid;
        return valid;
    }
    showValidationError(errorMessage) {
        const parent = this.hrefInput.parentElement;
        const error = this.hrefError;
        if (parent) {
            parent.classList.add('has-error');
        }
        error.textContent = errorMessage;
        error.classList.remove('d-none');
    }
    hideValidationError() {
        const parent = this.hrefInput.parentElement;
        const error = this.hrefError;
        if (parent) {
            parent.classList.remove('has-error');
        }
        error.textContent = '';
        error.classList.add('d-none');
    }
    resetEditor() {
        this.hrefInput.value = '';
        this.textInput.value = '';
        this.hideValidationError();
    }
    handleSave(view) {
        const href = this.hrefInput.value;
        if (!this.validate(href))
            return;
        const text = this.textInput.value || href;
        const node = view.state.schema.text(text, []);
        let tr = this.tryHideInterfaceTr(view.state, {
            url: null,
            text: null,
        }) || view.state.tr;
        // set the text first, inheriting all marks
        tr = tr.replaceSelectionWith(node, true);
        // reselect the now unselected text
        tr = tr.setSelection(TextSelection.create(tr.doc, tr.selection.from - text.length, tr.selection.to));
        // drop any link marks that might already exist
        tr = tr.removeMark(tr.selection.from, tr.selection.to, view.state.schema.marks.link);
        // add our link mark back onto the selection
        tr = tr.addMark(tr.selection.from, tr.selection.to, view.state.schema.marks.link.create({ href }));
        view.dispatch(tr);
    }
    update(view) {
        super.update(view);
        if (this.isShown) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const state = LINK_EDITOR_KEY.getState(view.state);
            if (state?.url) {
                this.hrefInput.value = state.url;
                this.validate(state.url);
            }
            if (state?.text)
                this.textInput.value = state.text;
            let tr = this.tryShowInterfaceTr(view.state);
            if (tr) {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                tr = LINK_EDITOR_KEY.forceHideTooltipTr(view.state.apply(tr));
                view.dispatch(tr);
            }
            this.hrefInput.focus();
        }
        else {
            this.resetEditor();
            const tr = this.tryHideInterfaceTr(view.state);
            if (tr)
                view.dispatch(tr);
        }
    }
    destroy() {
        // this.uploadField.remove();
        this.viewContainer.remove();
    }
    buildInterface(container) {
        // add the view container to the menu area
        container.appendChild(this.viewContainer);
    }
    destroyInterface(container) {
        container.removeChild(this.viewContainer);
    }
}
/**
 * Custom PluginKey with additional methods for interacting with a LinkTooltip
 */
class LinkEditorPluginKey extends ManagedInterfaceKey {
    constructor() {
        super(LinkEditor.name);
    }
    /**
     * Force the link tooltip to hide - useful e.g. when the entire editor is losing
     * focus and we want to make sure the tooltip disappears, too
     */
    forceHideTooltipTr(state) {
        const meta = this.getState(state);
        // if the tooltip is not showing, just return
        if (meta.decorations === DecorationSet.empty)
            return state.tr;
        meta.forceHideTooltip = true;
        return this.setMeta(state.tr, meta);
    }
}
/** The plugin key the image uploader plugin is tied to */
const LINK_EDITOR_KEY = new LinkEditorPluginKey();
/** Manages the decorations necessary for drawing the link editor tooltip */
class LinkTooltip {
    get editButton() {
        return this.content.querySelector('.js-link-tooltip-edit');
    }
    get removeButton() {
        return this.content.querySelector('.js-link-tooltip-remove');
    }
    get link() {
        return this.content.querySelector('a');
    }
    constructor(state) {
        const popoverId = `link-tooltip-popover${generateRandomId()}`;
        this.content = document.createElement('span');
        this.content.className = 'w0';
        this.content.setAttribute('aria-controls', popoverId);
        this.content.setAttribute('data-controller', 's-popover');
        this.content.setAttribute('data-s-popover-placement', 'bottom');
        this.content.innerHTML = escapeHTML `<div class="s-popover is-visible p4 w-auto wmx-initial wmn-initial js-link-tooltip e11-absolute e11-mt-2"
            id="${popoverId}"
            role="menu">
            <div class="arrow e11-shadow-lg"></div>
            <div class="e11-flex e11-items-center e11-gap-1  e11-rounded e11-shadow-lg e11-p-2 e11-bg-white e11-text-blue-500 ">
                <a href="${this.href}"
                    class="e11-ml-2  e11-truncate e11-w-32 e11-text-sm hover:e11-underline"
                    target="_blank"
                    rel="nofollow noreferrer">${this.href}</a>
                <button type="button"
                        class="e11-flex e11-justify-center js-link-tooltip-edit"
                        title="${_t('link_tooltip.edit_button_title')}"><span class="material-icons e11-text-[18px]">edit</span></button>
                <button type="button"
                        class="e11-flex e11-justify-center js-link-tooltip-remove"
                        title="${_t('link_tooltip.remove_button_title')}"><span class="material-icons e11-text-[18px]">delete</span></button>
            </div>
        </div>`;
        // never allow the popover to hide itself. It either exists visibly or not at all
        this.content.addEventListener('s-popover:hide', (e) => {
            e.preventDefault();
        });
        // don't bind the exact listener, call whatever is currently set on `this` at event time
        const removeListener = (e) => {
            // @ts-ignore
            this.removeListener.call(this, e);
        };
        const editListener = (e) => {
            // @ts-ignore
            this.editListener.call(this, e);
        };
        // hook up the click/keyboard events for the supporting buttons
        this.bindElementInteraction(this.removeButton, removeListener);
        this.bindElementInteraction(this.editButton, editListener);
        this.update(state);
    }
    /**
     * Binds both a mousedown and selective keydown listener to replace the purposefully missing "click" event
     * @param element The element to bind the events to
     * @param callback The callback to run on mousedown/keydown
     */
    bindElementInteraction(element, callback) {
        element.addEventListener('mousedown', (e) => {
            e.stopPropagation();
            e.preventDefault();
            callback.call(this, e);
        });
        //@ts-ignore
        element.addEventListener('keydown', (e) => {
            //allow the Tab key to keep doing its thing
            if (e.key === 'Tab')
                return;
            // cancel all other keypresses
            e.stopPropagation();
            e.preventDefault();
            // enter/space should still fire the event as if clicked
            if (e.key === 'Enter' || e.key === ' ')
                callback.call(this, e);
        });
    }
    /**
     * Updates the internal state / tooltip visuals based on the current editor state
     * @param state the current state of the editor
     */
    update(state) {
        if (!this.isLink(state))
            return;
        const linkMarks = this.findMarksInSelection(state);
        if (linkMarks.length > 0) {
            this.href = linkMarks[0].attrs.href;
            const link = this.link;
            link.href = link.title = link.innerText = this.href;
        }
    }
    /**
     * Gets the tooltip decoration from a new PluginState.apply call
     * @param value The existing LinkTooltipState (with forceHideTooltip potentially set)
     * @param newState The state after the transaction
     */
    getDecorations(value, newState) {
        // if we're forced to hide the decorations, don't even attempt to create them
        if ('forceHideTooltip' in value && value.forceHideTooltip)
            return DecorationSet.empty;
        const marks = this.findMarksInSelection(newState);
        // if there are no marks in the current selection, then return empty
        if (!marks.length)
            return DecorationSet.empty;
        // always update the state, regardless of document changes (potential metadata changes can change tooltip visuals)
        this.update(newState);
        // create the widget tooltip via EditorView callback
        const decoration = Decoration.widget(newState.selection.from, (view) => {
            /* NOTE: This function runs on every transaction update */
            this.updateEventListeners(view);
            return this.content;
        }, {
            // place the widget *before* the cursor so it isn't included in text selections
            side: -1,
            ignoreSelection: true,
            // cancel all events coming from inside this widget
            stopEvent: () => true,
        });
        return DecorationSet.create(newState.doc, [decoration]);
    }
    /**
     * Returns true if the focus event caused something in the content to be focused
     * @param e The dispatched focus event
     */
    hasFocus(e) {
        return this.content.contains(e.relatedTarget);
    }
    /**
     * Find out if the current selection contains a link mark
     * @param state The current editor state
     */
    isLink(state) {
        const { from, $from, to, empty } = state.selection;
        if (!empty)
            return state.doc.rangeHasMark(from, to, state.schema.marks.link);
        return state.schema.marks.link.isInSet(state.storedMarks || $from.marks()) !== undefined;
    }
    /**
     * Expand the current selection to contain the entire link mark.
     * This allows us to remove the link mark from the entire link in the document
     * if the user didn't explicitly select a region to be toggled.
     */
    expandSelection(state, tr) {
        const expanded = this.linkAround(state);
        tr = tr.setSelection(TextSelection.create(tr.doc, expanded.from, expanded.to));
        return tr;
    }
    /**
     * Gets the positions immediately before and after a link mark in the current selection
     * @param state
     */
    linkAround(state) {
        const $pos = state.selection.$from;
        const start = $pos.parent.childAfter($pos.parentOffset);
        if (!start.node)
            return;
        const link = findMarksInSet(start.node.marks, state.schema.marks.link)[0];
        if (!link)
            return;
        let startIndex = $pos.index();
        let startPos = $pos.start() + start.offset;
        while (startIndex > 0 && link.isInSet($pos.parent.child(startIndex - 1).marks)) {
            startIndex -= 1;
            startPos -= $pos.parent.child(startIndex).nodeSize;
        }
        let endIndex = $pos.indexAfter();
        let endPos = startPos + start.node.nodeSize;
        while (endIndex < $pos.parent.childCount && link.isInSet($pos.parent.child(endIndex).marks)) {
            endPos += $pos.parent.child(endIndex).nodeSize;
            endIndex += 1;
        }
        return { from: startPos, to: endPos };
    }
    /**
     * Finds all marks in the current selection
     * @param state The current editor state
     */
    findMarksInSelection(state) {
        const linkMarks = [];
        const { to, from, $from, empty } = state.selection;
        if (empty)
            return findMarksInSet($from.marks(), state.schema.marks.link);
        if (to > from) {
            state.doc.nodesBetween(from, to, (node) => {
                linkMarks.push(findMarksInSet(node.marks, state.schema.marks.link));
            });
        }
        const returnValue = [];
        return returnValue.concat(...linkMarks);
    }
    /**
     * Updates apply/delete button events with the current editor view
     * @param view The current editor view
     */
    updateEventListeners(view) {
        this.removeListener = () => {
            let state = view.state;
            if (view.state.selection.empty) {
                const tr = this.expandSelection(state, state.tr);
                state = view.state.apply(tr);
            }
            toggleMark(view.state.schema.marks.link)(state, view.dispatch.bind(view));
        };
        this.editListener = () => {
            let tr = view.state.tr;
            if (view.state.selection.empty)
                tr = this.expandSelection(view.state, view.state.tr);
            const state = view.state.apply(tr);
            const { from, to } = state.selection;
            const text = state.doc.textBetween(from, to);
            const href = this.findMarksInSelection(state)[0].attrs.href;
            tr =
                LINK_EDITOR_KEY.showInterfaceTr(state, {
                    forceHideTooltip: true,
                    url: href,
                    text,
                }) || tr;
            view.dispatch(tr);
        };
    }
}
/**
 * A plugin view that shows a tooltip when selecting a link in rich-text mode.
 * The tooltip shows the href attribute of the selected link and allows removing
 * the link mark from the document. Clicking on the tooltip's edit button will launch
 * a plugin view that allows editing the link's href and text.
 *
 * Note: This is not a _NodeView_ because when dealing with links, we're dealing with
 * _marks_, not _nodes_.
 */
const linkEditorPlugin = (features) => new StatefulPlugin({
    key: LINK_EDITOR_KEY,
    state: {
        init(_, instance) {
            return {
                visible: false,
                linkTooltip: new LinkTooltip(instance),
                decorations: DecorationSet.empty,
                shouldShow: false,
            };
        },
        apply(tr, value, _, newState) {
            // check if force hide was set and add to value for getDecorations to use
            const meta = this.getMeta(tr) || value;
            if ('forceHideTooltip' in meta)
                value.forceHideTooltip = meta.forceHideTooltip;
            // update the linkTooltip and get the decorations
            const decorations = value.linkTooltip.getDecorations(value, newState);
            return {
                ...meta,
                forceHideTooltip: value.forceHideTooltip && decorations !== DecorationSet.empty,
                linkTooltip: value.linkTooltip,
                decorations,
            };
        },
    },
    props: {
        //@ts-ignore
        decorations(state) {
            return this.getState(state).decorations || DecorationSet.empty;
        },
        handleDOMEvents: {
            /** Handle editor blur and close the tooltip if it isn't focused */
            blur(view, e) {
                const linkTooltip = LINK_EDITOR_KEY.getState(view.state).linkTooltip;
                // if the editor blurs, but NOT because of the tooltip, hide the tooltip
                if (!view.hasFocus() && !linkTooltip.hasFocus(e)) {
                    view.dispatch(LINK_EDITOR_KEY.forceHideTooltipTr(view.state));
                }
                // always return false since we're not cancelling/handling the blur
                return false;
            },
        },
        /** Handle mod-click to open links in a new window */
        handleClick(view, pos, event) {
            const mark = findMarksInSet(view.state.doc.resolve(pos).marks(), view.state.schema.marks.link)[0];
            const modPressed = getPlatformModKey() === 'Cmd' ? event.metaKey : event.ctrlKey;
            const handled = mark && modPressed;
            // a link was mod-clicked, so open it in a new window
            if (handled)
                window.open(mark.attrs.href, '_blank');
            return !!handled;
        },
    },
    view(editorView) {
        return new LinkEditor(editorView, features.validateLink);
    },
});
/**
 * Dispatches a transaction to show the link editor with the given url and text filled in
 * @param view The current editor view
 * @param url The value to prefill the url input with
 * @param text The value to prefill the text input with
 */
function showLinkEditor(view, url, text) {
    const tr = LINK_EDITOR_KEY.showInterfaceTr(view.state, {
        url,
        text,
    });
    if (tr)
        view.dispatch(tr);
}
/**
 * Dispatches a transaction to hide the link editor
 * @param view The current editor view
 */
function hideLinkEditor(view) {
    const tr = LINK_EDITOR_KEY.hideInterfaceTr(view.state, {
        url: null,
        text: null,
    });
    if (tr)
        view.dispatch(tr);
}
/**
 * Finds all marks in a set that are of the given type
 * @param marks The set of marks to search
 * @param type The type of mark to find
 */
function findMarksInSet(marks, type) {
    return marks.filter((mark) => mark.type === type);
}

/**
 * Returns a transaction that inserts a new paragraph if there is no node after the cursor.
 * This is useful for when inserting block nodes, since the cursor cannot be placed after those blocks at the end of a document.
 * This makes document navigation much more intuitive for end users.
 * @param tr The most recent transaction to the document
 * @param cursorOffset TODO HACK The offset of where the cursor is vs where the inserted node is
 */
function insertParagraphIfAtDocEnd(tr, cursorOffset = 0) {
    if (!tr.docChanged)
        return tr;
    // check the (parent-most) node the cursor is in is the final node in the document
    const currNodePos = tr.doc.resolve(tr.selection.to - cursorOffset).before(1);
    const lastChidPos = tr.doc.content.size - tr.doc.lastChild.nodeSize;
    const shouldInsertPara = currNodePos === lastChidPos;
    if (shouldInsertPara) {
        const p = tr.doc.type.schema.nodes.paragraph.create(null);
        tr = tr.insert(tr.doc.content.size, p);
    }
    return tr;
}

function isTableType(schema, type) {
    const tableNodes = [
        schema.nodes.table,
        schema.nodes.table_head,
        schema.nodes.table_body,
        schema.nodes.table_row,
        schema.nodes.table_cell,
        schema.nodes.table_header,
    ];
    return tableNodes.includes(type);
}
function inTable(schema, selection) {
    return isTableType(schema, selection.$head.parent.type);
}
function inTableHead(schema, selection) {
    return selection.$head.parent.type === schema.nodes.table_header;
}
const exitBlockCommand = chainCommands(exitCode, (state, dispatch) => {
    dispatch(state.tr.replaceSelectionWith(state.schema.nodes.hard_break.create()).scrollIntoView());
    return true;
});
function moveSelectionAfterTableCommand(state, dispatch) {
    return exitTableCommand(state, dispatch, false);
}
function moveSelectionBeforeTableCommand(state, dispatch) {
    return exitTableCommand(state, dispatch, true);
}
function exitTableCommand(state, dispatch, before = false) {
    if (!inTable(state.schema, state.selection))
        return false;
    if (dispatch) {
        // our hierarchy is table > table_head | table_body > table_row > table_cell
        // and we're relying on that to be always true.
        // That's why .after(-3) selects the parent _table_ node from a table_cell node
        const type = state.schema.nodes.paragraph;
        const newPosition = before ? state.selection.$head.before(-3) - 1 : state.selection.$head.after(-3) + 1;
        const tr = state.tr;
        // if the position before/after the table doesn't exist, let's insert a paragraph there
        try {
            tr.doc.resolve(newPosition);
        }
        catch (e) {
            const insertionPosition = before ? newPosition + 1 : newPosition - 1;
            tr.insert(insertionPosition, type.create());
        }
        tr.setSelection(Selection.near(tr.doc.resolve(Math.max(0, newPosition)), 1));
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function insertTableRowBeforeCommand(state, dispatch) {
    return insertTableRowCommand(true, state, dispatch);
}
function insertTableRowAfterCommand(state, dispatch) {
    return insertTableRowCommand(false, state, dispatch);
}
function insertTableRowCommand(before, state, dispatch) {
    if (!inTable(state.schema, state.selection) || inTableHead(state.schema, state.selection))
        return false;
    if (dispatch) {
        const { $head } = state.selection;
        const tableRowNode = $head.node(-1);
        const newTableCells = [];
        tableRowNode.forEach((cell) => {
            newTableCells.push(state.schema.nodes.table_cell.create(cell.attrs));
        });
        const newTableRow = state.schema.nodes.table_row.create(null, newTableCells);
        const positionToInsert = before ? $head.before(-1) : $head.after(-1);
        const tr = state.tr.insert(positionToInsert, newTableRow);
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function insertTableColumnAfterCommand(state, dispatch) {
    return insertTableColumnCommand(false, state, dispatch);
}
function insertTableColumnBeforeCommand(state, dispatch) {
    return insertTableColumnCommand(true, state, dispatch);
}
/**
 *  Insert a new table column in this table
 * 1. find the index of the selected table cell in the current table row
 * 2. walk through the entire document to traverse all rows in our selected table
 * 3. for each table row, find the table cell at the desired index and get its position
 * 4. insert a new table_cell or table_header node before/after the found position
 */
function insertTableColumnCommand(before, state, dispatch) {
    if (!inTable(state.schema, state.selection))
        return false;
    if (dispatch) {
        const $head = state.selection.$head;
        const selectedTable = $head.node(-3);
        const selectedCellIndex = $head.index(-1);
        // find and store all positions where we need to insert new cells
        const newCells = [];
        const tableOffset = $head.start(-3);
        let targetCell;
        // traverse the current table to find the absolute positions of our cells to be inserted
        selectedTable.descendants((node, pos) => {
            if (!isTableType(state.schema, node.type))
                return false; // don't descend into non-table nodes
            if (node.type === state.schema.nodes.table_row)
                targetCell = node.child(selectedCellIndex);
            // eslint-disable-next-line eqeqeq
            if (targetCell && node == targetCell) {
                const position = before ? selectedTable.resolve(pos + 1).before() : selectedTable.resolve(pos + 1).after();
                // position is relative to the start of the table, so we need
                // to add the table offset (distance to start of document)
                newCells.push([node.type, tableOffset + position]);
            }
        });
        // insert new cells from bottom to top (reverse order)
        // to avoid inserted cells making our found positions obsolete
        let tr = state.tr;
        for (const newCell of newCells.reverse())
            tr = tr.insert(newCell[1], newCell[0].create());
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function removeRowCommand(state, dispatch) {
    if (!inTable(state.schema, state.selection) || inTableHead(state.schema, state.selection))
        return false;
    if (dispatch) {
        const tr = state.tr;
        const $head = state.selection.$head;
        // delete entire table if we're deleting the last row in the table body
        if ($head.node(-2).childCount === 1)
            return removeTableCommand(state, dispatch);
        // delete from start to end of this row (node at -1 position from the table cell)
        tr.delete($head.start(-1) - 1, $head.end(-1) + 1);
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function removeColumnCommand(state, dispatch) {
    if (!inTable(state.schema, state.selection))
        return false;
    if (dispatch) {
        const $head = state.selection.$head;
        const table = $head.node(-3);
        // remove entire table if this is the last remaining column
        if ($head.node(-1).childCount === 1)
            return removeTableCommand(state, dispatch);
        const cellIndex = $head.index(-1);
        let targetCell;
        const resolvedPositions = [];
        const tableOffset = $head.start(-3);
        table.descendants((node, pos) => {
            if (!isTableType(state.schema, node.type))
                return false; // don't descend into non-table nodes
            if (node.type === state.schema.nodes.table_row) {
                targetCell = node.childCount >= cellIndex + 1 ? node.child(cellIndex) : null;
            }
            // eslint-disable-next-line eqeqeq
            if (targetCell && node == targetCell)
                resolvedPositions.push(table.resolve(pos + 1));
        });
        let tr = state.tr;
        for (const cellPosition of resolvedPositions.reverse()) {
            tr = tr.delete(tableOffset + cellPosition.start() - 1, tableOffset + cellPosition.end() + 1);
        }
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function removeTableContentCommand(state, dispatch) {
    if (!inTable(state.schema, state.selection))
        return false;
    const { $from, $to } = state.selection;
    // selection includes entire table
    if ($from.start(-3) >= $from.pos - 3 && $from.end(-3) <= $to.pos + 3)
        return removeTableCommand(state, dispatch);
    // selection includes entire row
    if ($from.start(-1) >= $from.pos - 1 && $from.end(-1) <= $to.pos + 1)
        return removeRowCommand(state, dispatch);
    // selection contains two arbitrary cells?
    // prevent delete operation to prevent deleting the cell nodes
    // themselves and breaking the table structure
    if (!$from.sameParent($to))
        return true;
    return false;
}
function moveToCellCommand(state, dispatch, direction) {
    if (direction !== -1 && direction !== 1)
        return false;
    if (!inTable(state.schema, state.selection))
        return false;
    const $head = state.selection.$head;
    for (let level = -1; level > -4; level--) {
        const parentIndex = $head.index(level);
        const parent = $head.node(level);
        if (!parent)
            continue;
        // every time we want to skip the boundaries of a node (a cell, a row, ...)
        // we have to consider the node's opening and closing positions, too. For
        // each level, this will add an additional offset of 2 that we need to skip
        const nodeOffset = 2;
        const target = parent.maybeChild(parentIndex + direction);
        if (!target)
            continue;
        const newPos = direction === -1 ? $head.start() - nodeOffset * (level * -1) : $head.end() + nodeOffset * (level * -1);
        dispatch(state.tr.setSelection(Selection.near(state.tr.doc.resolve(newPos), 1)).scrollIntoView());
        return true;
    }
    // we're at the end of the table and still want to move forward?
    // let's move the cursor below the table!
    if (direction === 1)
        return moveSelectionAfterTableCommand(state, dispatch);
    else
        return moveSelectionBeforeTableCommand(state, dispatch);
}
function moveToPreviousCellCommand(state, dispatch) {
    return moveToCellCommand(state, dispatch, -1);
}
function moveToNextCellCommand(state, dispatch) {
    return moveToCellCommand(state, dispatch, +1);
}
function removeTableCommand(state, dispatch) {
    const $head = state.selection.$head;
    if (dispatch)
        dispatch(state.tr.deleteRange($head.start(-3) - 1, $head.end(-3) + 1));
    return true;
}
function insertTableCommand(state, dispatch) {
    if (!setBlockType$1(state.schema.nodes.table)(state))
        return false;
    if (!dispatch)
        return true;
    let headerIndex = 1;
    let cellIndex = 1;
    const cell = () => state.schema.nodes.table_cell.create(null, state.schema.text(`cell ${cellIndex++}`));
    const header = () => state.schema.nodes.table_header.create(null, state.schema.text(`header ${headerIndex++}`));
    const row = (...cells) => state.schema.nodes.table_row.create(null, cells);
    const head = (row) => state.schema.nodes.table_head.create(null, row);
    const body = (...rows) => state.schema.nodes.table_body.create(null, rows);
    const table = (head, body) => state.schema.nodes.table.createChecked(null, [head, body]);
    const t = table(head(row(header(), header())), body(row(cell(), cell()), row(cell(), cell())));
    let tr = state.tr.replaceSelectionWith(t);
    tr = insertParagraphIfAtDocEnd(tr, 2);
    dispatch(tr.scrollIntoView());
    return true;
}

/**
 * Builds a command which wraps/unwraps the current selection with the passed in node type
 * @param nodeType the type of node to wrap the selection in
 * @returns A command to toggle the wrapper node
 * Commands are functions that take a state and an optional
 * transaction dispatch function and...
 *
 *  - determine whether they apply to this state
 *  - if not, return false
 *  - if `dispatch` was passed, perform their effect, possibly by
 *    passing a transaction to `dispatch`
 *  - return true
 */
function toggleWrapIn(nodeType) {
    const nodeCheck = nodeTypeActive(nodeType);
    const listItemTypes = ['bullet_list', 'ordered_list'];
    const wrapInCommand = listItemTypes.includes(nodeType.name) ? wrapInList(nodeType) : wrapIn$1(nodeType);
    return (state, dispatch) => {
        // if the node is not wrapped, go ahead and wrap it
        if (!nodeCheck(state))
            return wrapInCommand(state, dispatch);
        // check if it is list item
        if (listItemTypes.includes(nodeType.name)) {
            return liftListItem(state.schema.nodes.list_item)(state, dispatch);
        }
        const { $from, $to } = state.selection;
        const range = $from.blockRange($to);
        // check if there is a valid target to lift to
        const target = range && liftTarget(range);
        // if we cannot unwrap, return false
        if (target == null)
            return false;
        if (dispatch)
            dispatch(state.tr.lift(range, target));
        return true;
    };
}
/**
 * Creates a command that toggles the NodeType of the current node to the passed type
 * @param nodeType The type to toggle to
 * @param attrs? A key-value map of attributes that must be present on this node for it to be toggled off
 */
function toggleBlockType(nodeType, attrs) {
    return (state, dispatch) => {
        const nodeCheck = nodeTypeActive(nodeType, attrs);
        // if the node is set, toggle it off
        if (nodeCheck(state))
            return setBlockType$1(state.schema.nodes.paragraph)(state, dispatch);
        const setBlockTypeCommand = setBlockType$1(nodeType, attrs);
        return setBlockTypeCommand(state, (t) => {
            if (dispatch) {
                // when adding a block node, make sure the user can navigate past it
                dispatch(insertParagraphIfAtDocEnd(t));
            }
        });
    };
}
/**
 * Creates a command that toggles heading and cycles through heading levels
 * @param attrs? A key-value map of attributes that must be present on this node for it to be toggled off
 * @internal
 */
function toggleHeadingLevel(attrs) {
    return (state, dispatch) => {
        const nodeType = state.schema.nodes.heading;
        const nodeCheck = nodeTypeActive(nodeType, attrs);
        const headingLevel = getHeadingLevel(state);
        // if the node is a heading and is either level 6 or matches the current level, toggle it off
        if (nodeCheck(state) && (headingLevel === 6 || headingLevel === attrs?.level))
            return setBlockType$1(state.schema.nodes.paragraph)(state, dispatch);
        const updatedAttrs = !attrs?.level ? { ...attrs, level: headingLevel + 1 } : attrs;
        const setBlockTypeCommand = setBlockType$1(nodeType, updatedAttrs);
        return setBlockTypeCommand(state, (t) => {
            if (dispatch) {
                // when adding a block node, make sure the user can navigate past it
                dispatch(insertParagraphIfAtDocEnd(t));
            }
        });
    };
}
/**
 * Returns the first heading level of the current selection
 * @param state The current editor state
 */
function getHeadingLevel(state) {
    const { from, to } = state.selection;
    let level = 0;
    state.doc.nodesBetween(from, to, (node) => {
        if (node.type.name === 'heading') {
            level = node.attrs.level;
            return true;
        }
    });
    return level;
}
function insertHorizontalRuleCommand(state, dispatch) {
    if (inTable(state.schema, state.selection))
        return false;
    if (!dispatch)
        return true;
    const isAtEnd = state.doc.content.size - 1 === Math.max(state.selection.from, state.selection.to);
    const isAtBeginning = state.tr.selection.from === 1;
    let tr = state.tr.replaceSelectionWith(state.schema.nodes.horizontal_rule.create());
    if (isAtBeginning)
        tr = tr.insert(0, state.schema.nodes.paragraph.create());
    if (isAtEnd)
        tr = tr.insert(tr.selection.to, state.schema.nodes.paragraph.create());
    dispatch(tr);
    return true;
}
function insertImageCommand(state, dispatch, view) {
    if (!imageUploaderEnabled(view.state))
        return false;
    if (!dispatch)
        return true;
    showImageUploader(view);
    return true;
}
/**
 * Inserts a link into the document and opens the link edit tooltip at the cursor
 */
function insertLinkCommand(state, dispatch, view) {
    // never actually toggle the mark, as that is done in the link editor
    // we do want to *pretend* to, as toggleMark checks for validity
    const valid = toggleMark(state.schema.marks.link, { href: null })(state, null);
    if (dispatch && valid) {
        let selectedText;
        let linkUrl;
        const $anchor = state.selection.$anchor;
        // if selection is empty, but inside link mark, use the link url/text from it
        if (state.selection.empty && $anchor.textOffset) {
            const currentTextNode = getCurrentTextNode(state);
            const mark = currentTextNode.marks.find((m) => m.type === state.schema.marks.link);
            if (mark) {
                selectedText = currentTextNode.text;
                linkUrl = mark.attrs.href;
                // expand the selection so we're editing the entire link
                const pos = $anchor.pos;
                dispatch(state.tr.setSelection(TextSelection.create(state.doc, pos - $anchor.textOffset, pos - $anchor.textOffset + selectedText.length)));
            }
        }
        else {
            selectedText = state.selection.content().content.firstChild?.textContent ?? null;
            const linkMatch = /^http(s)?:\/\/\S+$/.exec(selectedText);
            linkUrl = linkMatch?.length > 0 ? linkMatch[0] : '';
        }
        showLinkEditor(view, linkUrl, selectedText);
    }
    return valid;
}
/**
 * Creates an `active` method that returns true if the current selection is/contained in the current block type
 * @param nodeType The type of the node to check for
 * @param attrs? A key-value map of attributes that must be present on this node
 */
function nodeTypeActive(nodeType, attrs) {
    return function (state) {
        const { from, to } = state.selection;
        let isNodeType = false;
        let passesAttrsCheck = !attrs;
        // check all nodes in the selection for the right type
        state.doc.nodesBetween(from, to, (node) => {
            isNodeType = node.type.name === nodeType.name;
            for (const attr in attrs)
                passesAttrsCheck = node.attrs[attr] === attrs[attr];
            // stop recursing if the current node is the right type
            return !(isNodeType && passesAttrsCheck);
        });
        return isNodeType && passesAttrsCheck;
    };
}
/**
 * Creates an `active` method that returns true of the current selection has the passed mark
 * @param mark The mark to check for
 */
function markActive(mark) {
    return function (state) {
        const { from, $from, to, empty } = state.selection;
        if (empty)
            return !!mark.isInSet(state.storedMarks || $from.marks());
        else
            return state.doc.rangeHasMark(from, to, mark);
    };
}
/**
 * Exits an inclusive mark that has been marked as exitable by toggling the mark type
 * and optionally adding a trailing space if the mark is at the end of the document
 */
function exitInclusiveMarkCommand(state, dispatch) {
    const $cursor = state.selection.$cursor;
    const marks = state.storedMarks || $cursor.marks();
    if (!marks?.length)
        return false;
    // check if the current mark is exitable
    const exitables = marks.filter((mark) => mark.type.spec.exitable);
    if (!exitables?.length)
        return false;
    // check if we're at the end of the exitable mark
    const nextNode = $cursor.nodeAfter;
    let endExitables;
    let tr = state.tr;
    if (nextNode && nextNode.marks?.length) {
        // marks might be nested, so check each mark
        endExitables = exitables.filter((mark) => !mark.type.isInSet(nextNode.marks));
    }
    else {
        // no next node, so *all* marks are exitable
        endExitables = exitables;
    }
    if (!endExitables.length)
        return false;
    if (dispatch) {
        // remove the exitable marks from the cursor
        endExitables.forEach((e) => {
            tr = tr.removeStoredMark(e);
        });
        // if there's no characters to the right of the cursor, add a space
        if (!nextNode)
            tr = tr.insertText(' ');
        dispatch(tr);
    }
    return true;
}
const tableDropdown = () => makeMenuDropdown('Table', _t('commands.table_edit'), 'table-dropdown', (state) => inTable(state.schema, state.selection), () => false, dropdownSection('Column', 'columnSection'), dropdownItem(_t('commands.table_column.remove'), removeColumnCommand, 'remove-column-btn'), dropdownItem(_t('commands.table_column.insert_before'), insertTableColumnBeforeCommand, 'insert-column-before-btn'), dropdownItem(_t('commands.table_column.insert_after'), insertTableColumnAfterCommand, 'insert-column-after-btn'), dropdownSection('Row', 'rowSection'), dropdownItem(_t('commands.table_row.remove'), removeRowCommand, 'remove-row-btn'), dropdownItem(_t('commands.table_row.insert_before'), insertTableRowBeforeCommand, 'insert-row-before-btn'), dropdownItem(_t('commands.table_row.insert_after'), insertTableRowAfterCommand, 'insert-row-after-btn'));
const headingDropdown = (schema) => makeMenuDropdown('text_fields', _t('commands.heading.dropdown', { shortcut: getShortcut('Mod-H') }), 'heading-dropdown', () => true, nodeTypeActive(schema.nodes.heading), dropdownItem(_t('commands.heading.entry', { level: 1 }), toggleHeadingLevel({ level: 1 }), 'h1-btn', nodeTypeActive(schema.nodes.heading, { level: 1 }), ['fs-body3', 'mt8']), dropdownItem(_t('commands.heading.entry', { level: 2 }), toggleHeadingLevel({ level: 2 }), 'h2-btn', nodeTypeActive(schema.nodes.heading, { level: 2 }), ['fs-body2']), dropdownItem(_t('commands.heading.entry', { level: 3 }), toggleHeadingLevel({ level: 3 }), 'h3-btn', nodeTypeActive(schema.nodes.heading, { level: 3 }), ['fs-body1']));
// TODO ensure that all names and priorities match those found in the rich-text editor
/**
 * Creates all menu entries for the commonmark editor
 * @param schema The finalized rich-text schema
 * @param options The options for the editor
 * @internal
 */
const createMenuEntries = (schema, options) => [
    {
        name: 'formatting1', // TODO better name?
        priority: 0,
        entries: [
            // headingDropdown(schema),
            {
                key: 'toggleBold',
                command: toggleMark(schema.marks.strong),
                dom: makeMenuButton('format_bold', _t('commands.bold', { shortcut: getShortcut('Mod-B') }), 'bold-btn'),
                active: markActive(schema.marks.strong),
            },
            {
                key: 'toggleEmphasis',
                command: toggleMark(schema.marks.em),
                dom: makeMenuButton('format_italic', _t('commands.emphasis', { shortcut: getShortcut('Mod-I') }), 'italic-btn'),
                active: markActive(schema.marks.em),
            },
            addIf({
                key: 'toggleStrike',
                command: toggleMark(schema.marks.strike),
                dom: makeMenuButton('strikethrough_s', _t('commands.strikethrough'), 'strike-btn'),
                active: markActive(schema.marks.strike),
            }, options.parserFeatures.extraEmphasis),
            {
                key: 'toggleLink',
                command: insertLinkCommand,
                dom: makeMenuButton('link', _t('commands.link', { shortcut: getShortcut('Mod-L') }), 'insert-link-btn'),
            },
            //  {
            //   key: 'toggleBlockquote',
            //   command: toggleWrapIn(schema.nodes.blockquote),
            //   dom: makeMenuButton(
            //     'format_quote',
            //     _t('commands.blockquote', {
            //       shortcut: getShortcut('Mod-Q'),
            //     }),
            //     'blockquote-btn',
            //   ),
            //   active: nodeTypeActive(schema.nodes.blockquote),
            // },
        ],
    },
    {
        name: 'formatting2', // TODO better name?
        priority: 10,
        entries: [
            {
                key: 'toggleCode',
                command: toggleMark(schema.marks.code),
                dom: makeMenuButton('code', _t('commands.inline_code', {
                    shortcut: getShortcut('Mod-K'),
                }), 'code-btn'),
                active: markActive(schema.marks.code),
            },
            {
                key: 'toggleCodeblock',
                command: toggleBlockType(schema.nodes.code_block),
                dom: makeMenuButton('developer_mode', _t('commands.code_block', {
                    shortcut: getShortcut('Mod-M'),
                }), 'code-block-btn'),
                active: nodeTypeActive(schema.nodes.code_block),
            },
            // addIf(
            //   {
            //     key: 'insertImage',
            //     command: insertImageCommand,
            //     dom: makeMenuButton(
            //       'Image',
            //       _t('commands.image', {
            //         shortcut: getShortcut('Mod-G'),
            //       }),
            //       'insert-image-btn',
            //     ),
            //   },
            //   !!options.imageUpload?.handler,
            // ),
            // addIf(
            //   {
            //     key: 'insertTable',
            //     command: insertTableCommand,
            //     dom: makeMenuButton(
            //       'Table',
            //       _t('commands.table_insert', {
            //         shortcut: getShortcut('Mod-E'),
            //       }),
            //       'insert-table-btn',
            //     ),
            //     visible: (state: EditorState) =>
            //       !inTable(state.schema, state.selection),
            //   },
            //   options.parserFeatures.tables,
            // ),
            // addIf(tableDropdown(), options.parserFeatures.tables),
        ],
    },
    {
        name: 'formatting3', // TODO better name?
        priority: 20,
        entries: [
            {
                key: 'toggleOrderedList',
                command: toggleWrapIn(schema.nodes.ordered_list),
                dom: makeMenuButton('format_list_numbered', _t('commands.ordered_list', {
                    shortcut: getShortcut('Mod-O'),
                }), 'numbered-list-btn'),
                active: nodeTypeActive(schema.nodes.ordered_list),
            },
            {
                key: 'toggleUnorderedList',
                command: toggleWrapIn(schema.nodes.bullet_list),
                dom: makeMenuButton('format_list_bulleted', _t('commands.unordered_list', {
                    shortcut: getShortcut('Mod-U'),
                }), 'bullet-list-btn'),
                active: nodeTypeActive(schema.nodes.bullet_list),
            },
            // {
            //   key: 'insertRule',
            //   command: insertHorizontalRuleCommand,
            //   dom: makeMenuButton(
            //     'HorizontalRule',
            //     _t('commands.horizontal_rule', {
            //       shortcut: getShortcut('Mod-R'),
            //     }),
            //     'horizontal-rule-btn',
            //   ),
            // },
        ],
    },
    {
        name: 'history',
        priority: 30,
        entries: [
            {
                key: 'undo',
                command: undo,
                dom: makeMenuButton('undo', _t('commands.undo', { shortcut: getShortcut('Mod-Z') }), 'undo-btn'),
            },
            {
                key: 'redo',
                command: redo,
                dom: makeMenuButton('redo', _t('commands.redo', { shortcut: getShortcut('Mod-Y') }), 'redo-btn'),
            },
        ],
        visible: () => false,
        classes: ['sm:d-inline-flex'],
    },
    {
        name: 'other',
        priority: 40,
        entries: [
            // TODO eventually this will mimic the "help" dropdown in the prod editor
            makeMenuLinkEntry('Help', _t('commands.help'), options.editorHelpLink, 'help-link'),
        ],
    },
];

function exitOnEnter(state) {
    const event = new Event('markdown-enter');
    document.dispatchEvent(event);
    return true;
}
function allKeymaps(schema, parserFeatures) {
    const tableKeymap = keymap({
        ...bindLetterKeymap('Mod-e', insertTableCommand),
        'Mod-Enter': moveSelectionAfterTableCommand,
        'Shift-Enter': moveSelectionAfterTableCommand,
        Enter: moveToNextCellCommand,
        Backspace: removeTableContentCommand,
        Delete: removeTableContentCommand,
        'Mod-Backspace': removeTableContentCommand,
        'Mod-Delete': removeTableContentCommand,
        Tab: moveToNextCellCommand,
        'Shift-Tab': moveToPreviousCellCommand,
    });
    const isMobile = () => {
        if ('maxTouchPoints' in navigator)
            return navigator.maxTouchPoints > 0;
        const mQ = matchMedia?.('(pointer:coarse)');
        if (mQ?.media === '(pointer:coarse)')
            return !!mQ.matches;
        if ('orientation' in window)
            return true;
        return (/\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(navigator.userAgent) ||
            /\b(Android|Windows Phone|iPad|iPod)\b/i.test(navigator.userAgent));
    };
    const richTextKeymap = keymap({
        ...bindLetterKeymap('Mod-z', undo),
        ...bindLetterKeymap('Mod-y', redo),
        ...bindLetterKeymap('Mod-Shift-z', redo),
        Backspace: undoInputRule,
        Enter: isMobile() ? chainCommands(splitListItem(schema.nodes.list_item), splitBlock, exitBlockCommand) : exitOnEnter,
        Tab: sinkListItem(schema.nodes.list_item),
        'Shift-Tab': liftListItem(schema.nodes.list_item),
        'Mod-Enter': chainCommands(splitBlock, exitBlockCommand),
        'Shift-Enter': chainCommands(splitListItem(schema.nodes.list_item), splitBlock, exitBlockCommand),
        ...bindLetterKeymap('Mod-b', toggleMark(schema.marks.strong)),
        ...bindLetterKeymap('Mod-i', toggleMark(schema.marks.em)),
        ...bindLetterKeymap('Mod-l', insertLinkCommand),
        ...bindLetterKeymap('Ctrl-q', wrapIn$1(schema.nodes.blockquote)),
        ...bindLetterKeymap('Mod-k', toggleMark(schema.marks.code)),
        ...bindLetterKeymap('Mod-g', insertImageCommand),
        ...bindLetterKeymap('Ctrl-g', insertImageCommand),
        ...bindLetterKeymap('Mod-o', wrapInList(schema.nodes.ordered_list)),
        ...bindLetterKeymap('Mod-u', wrapInList(schema.nodes.bullet_list)),
        ...bindLetterKeymap('Mod-h', toggleHeadingLevel()),
        ...bindLetterKeymap('Mod-r', insertHorizontalRuleCommand),
        ...bindLetterKeymap('Mod-m', setBlockType$1(schema.nodes.code_block)),
        // users expect to be able to leave certain blocks/marks using the arrow keys
        ArrowRight: exitInclusiveMarkCommand,
        ArrowDown: exitCode,
    });
    const keymaps = [richTextKeymap, keymap(baseKeymap)];
    if (parserFeatures.tables)
        keymaps.unshift(tableKeymap);
    return keymaps;
}

/**
 * View with <code> wrapping/decorations for code_block nodes
 */
class CodeBlockView {
    constructor(node, view, getPos, additionalProcessors) {
        this.additionalProcessors = additionalProcessors;
        this.language = null;
        this.dom = document.createElement('div');
        this.dom.classList.add('ps-relative', 'p0', 'ws-normal', 'ow-normal', 'e11-bg-gray-100', 'e11-p-2', 'e11-rounded-md');
        this.render(view, getPos);
        this.contentDOM = this.dom.querySelector('.content-dom');
        this.update(node);
    }
    update(node) {
        // don't allow the node to be changed into anything other than a code_block
        if (node.type.name !== 'code_block')
            return false;
        const rawLanguage = this.getLanguageFromBlock(node);
        const processorApplies = this.getValidProcessorResult(rawLanguage, node);
        if (processorApplies)
            this.updateProcessor(node, processorApplies);
        else
            this.updateCodeBlock(rawLanguage);
        this.toggleView(!!processorApplies, !!node.attrs.isEditingProcessor);
        return true;
    }
    render(view, getPos) {
        const randomId = generateRandomId();
        this.dom.innerHTML = escapeHTML `
        <div class="e11-ml-auto e11-w-full e11-text-right e11-text-sm t2 r4 fs-fine pe-none us-none e11-text-gray-500 js-language-indicator" contenteditable=false></div>
        <div class="e11-absolute e11-top-0 e11-right-0 e11-hidden js-processor-toggle">
            <label class="e11-hidden e11-mr4" for="js-editor-toggle-${randomId}">
                Edit
            </label>
            <div class="flex--item s-toggle-switch">
                <input class="js-processor-is-editing" id="js-editor-toggle-${randomId}" type="checkbox">
                <div class="s-toggle-switch--indicator"></div>
            </div>
        </div>
        <div class="e11-none js-processor-view e11-text-sm"></div>
        <pre class="s-code-block js-code-view js-code-mode e11-text-sm"><code class="content-dom"></code></pre>`;
        this.contentDOM = this.dom.querySelector('.content-dom');
        if (typeof getPos !== 'function')
            return;
        this.dom.querySelector('.js-processor-is-editing').addEventListener('change', (e) => {
            e.stopPropagation();
            const isEditing = !!e.target.checked;
            const pos = getPos();
            const nodeAttrs = view.state.doc.nodeAt(pos).attrs;
            view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, {
                ...nodeAttrs,
                isEditingProcessor: isEditing,
            }));
        });
    }
    /** Switches the view between editor mode and processor mode */
    toggleView(showProcessor, showProcessorEdit) {
        const toggle = (selector, show) => this.dom.querySelector(selector).classList.toggle('d-none', !show);
        toggle('.js-code-view', !showProcessor || showProcessorEdit);
        toggle('.js-processor-toggle', showProcessor);
        toggle('.js-language-indicator', !showProcessor);
        toggle('.js-processor-view', showProcessor && !showProcessorEdit);
    }
    /** Gets the codeblock language from the node */
    getLanguageFromBlock(node) {
        let autodetectedLanguage = node.attrs.detectedHighlightLanguage;
        if (autodetectedLanguage) {
            autodetectedLanguage = _t('nodes.codeblock_lang_auto', {
                lang: autodetectedLanguage,
            });
        }
        return autodetectedLanguage || getBlockLanguage(node);
    }
    /** Updates the edit/code view */
    updateCodeBlock(rawLanguage) {
        if (this.language !== rawLanguage) {
            this.dom.querySelector('.js-language-indicator').textContent = rawLanguage;
            this.language = rawLanguage;
        }
    }
    /** Updates the processor view */
    updateProcessor(node, content) {
        const renderContainer = this.dom.querySelector('.js-processor-view');
        const isEditing = !!node.attrs.isEditingProcessor;
        this.dom.querySelector('.js-code-view').classList.toggle('d-none', !isEditing);
        renderContainer.classList.toggle('d-none', isEditing);
        renderContainer.innerHTML = '';
        renderContainer.append(...content.childNodes);
        return true;
    }
    /** Checks all the processors to see if any apply */
    getValidProcessorResult(rawLanguage, node) {
        const renderContainer = document.createElement('div');
        const processors = this.getProcessors(rawLanguage);
        if (!processors.length)
            return null;
        let appliedProcessor = false;
        for (const processor of processors) {
            appliedProcessor = processor(node.textContent, renderContainer);
            if (appliedProcessor)
                break;
        }
        return appliedProcessor ? renderContainer : null;
    }
    /** Gets all processors for the specified language string */
    getProcessors(rawLanguage) {
        const processors = [];
        // add in the language specific processors first
        if (rawLanguage in this.additionalProcessors)
            processors.push(...this.additionalProcessors[rawLanguage]);
        // followed by the generic processors
        if ('*' in this.additionalProcessors)
            processors.push(...this.additionalProcessors['*']);
        return processors;
    }
}

class HtmlBlock {
    constructor(node) {
        // TODO
        this.dom = document.createElement('div');
        this.dom.className = 'html_block ProseMirror-widget';
        // TODO need to indicate that this can't be edited
        // this.dom.classList.add("bg-red-100");
        // NOTE XSS safe, content is sanitized before getting here
        this.dom.innerHTML = node.attrs.content;
    }
}
class HtmlBlockContainer {
    constructor(node) {
        // TODO
        this.dom = document.createElement('div');
        this.dom.className = 'html_block_container ProseMirror-widget';
        // check for children, just to be safe
        if (!node.childCount) {
            this.dom.innerHTML = 'invalid html_block_container';
            return;
        }
        const contentDomPlaceholder = '<div class="ProseMirror-contentdom"></div>';
        const wrappingHtmlString = node.attrs.contentOpen + contentDomPlaceholder + node.attrs.contentClose;
        // NOTE XSS safe, content is sanitized before getting here
        this.dom.innerHTML = wrappingHtmlString;
        this.contentDOM = this.dom.querySelector('.ProseMirror-contentdom');
    }
}

class ImageView {
    constructor(node, view, getPos) {
        this.id = generateRandomId();
        this.img = this.createImage(node);
        this.form = this.createForm();
        this.form.addEventListener('submit', (event) => this.handleChangedImageAttributes(event, getPos, view));
        this.popover = this.createPopover();
        this.dom = document.createElement('span');
        this.dom.appendChild(this.img);
        this.dom.appendChild(this.popover);
        this.dom.addEventListener('s-popover:hide', (event) => this.preventClose(event));
    }
    /**
     * We want to trigger Stacks' showing and hiding of popovers whenever an image is considered
     * "selected" by prosemirror. This can happen by mouse-clicking or arrowing through the editor
     */
    selectNode() {
        this.img.classList.add('bs-ring');
        this.selectionActive = true;
        // tell Stacks to hide the popover
        this.img.dispatchEvent(new Event('image-popover-show'));
        const inputFields = this.form.querySelectorAll('input');
        if (inputFields.length > 0) {
            inputFields[0].focus({
                preventScroll: true,
            });
        }
    }
    deselectNode() {
        this.img.classList.remove('bs-ring');
        this.selectionActive = false;
        // tell Stacks to hide the popover
        this.img.dispatchEvent(new Event('image-popover-hide'));
    }
    stopEvent(event) {
        // prevent elements from within the tooltip to bubble up to the outer editor
        // as this would make the editor view close the tooltip view right away
        return this.popover.contains(event.target);
    }
    ignoreMutation() {
        return true;
    }
    destroy() {
        this.img.remove();
        this.popover.remove();
        this.dom = null;
        this.form.remove();
    }
    createImage(node) {
        const img = document.createElement('img');
        img.setAttribute('aria-controls', `img-popover-${this.id}`);
        img.setAttribute('data-controller', 's-popover');
        img.setAttribute('data-action', 'image-popover-show->s-popover#show image-popover-hide->s-popover#hide');
        img.src = node.attrs.src;
        if (node.attrs.alt)
            img.alt = node.attrs.alt;
        if (node.attrs.title)
            img.title = node.attrs.title;
        return img;
    }
    createForm() {
        const form = document.createElement('form');
        form.className = 'd-flex fd-column';
        form.innerHTML = escapeHTML `
            <label class="flex--item s-label mb4" for="img-src-${this.id}">Image source</label>
            <div class="d-flex ps-relative mb12">
                <input class="flex--item s-input" type="text" name="src" id="img-src-${this.id}" value="${this.img.src}" placeholder="https://example.com/image.png"/>
            </div>
            <label class="flex--item s-label mb4" for="img-alt-${this.id}">Image description</label>
            <div class="d-flex ps-relative mb12">
                <input class="flex--item s-input" type="text" name="alt" id="img-alt-${this.id}" value="${this.img.alt}" placeholder="A description for the image"/>
            </div>
            <label class="flex--item s-label mb4" for="img-title-${this.id}">Title</label>
            <div class="d-flex ps-relative mb12">
                <input class="flex--item s-input" type="text" name="title" id="img-title-${this.id}" value="${this.img.title}" placeholder="A title shown on hover"/>
            </div>

            <button class="s-btn s-btn__primary" type="submit" aria-pressed="false">Apply</button>
        `;
        return form;
    }
    createPopover() {
        const popover = document.createElement('div');
        popover.className = 's-popover ws-normal wb-normal js-img-popover';
        popover.id = `img-popover-${this.id}`;
        // TODO added `ws-normal` to fix FF only bug. Will file bug against Stacks and revisit
        popover.innerHTML = '<div class="s-popover--arrow ws-normal"></div>';
        popover.append(this.form);
        return popover;
    }
    handleChangedImageAttributes(event, getPos, view) {
        event.preventDefault();
        if (typeof getPos !== 'function')
            return;
        const findInput = (selector) => this.form.querySelector(selector);
        const src = findInput(`#img-src-${this.id}`);
        const alt = findInput(`#img-alt-${this.id}`);
        const title = findInput(`#img-title-${this.id}`);
        view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, {
            src: src.value,
            alt: alt.value,
            title: title.value,
        }));
        view.focus();
    }
    // don't let Stacks close the popover if the image
    // element is still selected in the editor
    preventClose(event) {
        if (this.selectionActive)
            event.preventDefault();
    }
}

// TODO instead of a NodeView, should we use marks and an editor like `link-tooltip`?
class TagLink {
    constructor(node, options) {
        this.dom = document.createElement('a');
        this.dom.setAttribute('href', '#');
        this.dom.setAttribute('rel', 'tag');
        this.dom.classList.add('s-tag');
        this.dom.innerText = node.attrs.tagName;
        if (options?.render) {
            const rendered = options.render(node.attrs.tagName, node.attrs.tagType === 'meta-tag');
            // the renderer failed to return the bare minimum necessary to link the tag
            // log an error to the console, but don't crash the user input
            if (!rendered || !rendered?.link) {
                error('TagLink NodeView', 'Unable to render taglink due to invalid response from options.renderer: ', rendered);
                return;
            }
            const additionalClasses = rendered.additionalClasses || [];
            additionalClasses.forEach((c) => this.dom.classList.add(c));
            this.dom.setAttribute('href', rendered.link);
            this.dom.setAttribute('title', rendered.linkTitle);
        }
    }
}

function isInlineCode(state) {
    const { from, $from, to, empty } = state.selection;
    const schema = state.schema;
    if (!empty)
        return state.doc.rangeHasMark(from, to, schema.marks.code);
    return !!schema.marks.code.isInSet(state.storedMarks || $from.marks());
}
/** Plugin that detects if a URL is being pasted in and automatically formats it as a link */
const linkPasteHandler = (features) => new Plugin({
    props: {
        handlePaste(view, event) {
            const selectedNode = view.state.selection.$from.node();
            if (selectedNode.type.name === 'code_block') {
                // We don't need to do anything special to handle pasting into code blocks
                // except to avoid overriding that default behavior.
                return false;
            }
            const link = event.clipboardData.getData('text/plain');
            // ensure what we're pasting is actually some sort of uri in addition to if it passes link validation
            if (!link || !/^.+?:\/\//.test(link) || !features.validateLink(link))
                return false;
            if (isInlineCode(view.state)) {
                // TODO: Would be nice to support smarter pasting into inline code in general.
                // This supports the bare minimum (not breaking the inline block if we're pasting a link in)
                // But any other pasting into inline code ends up breaking its styling.
                view.dispatch(view.state.tr.insertText(link));
            }
            else {
                let linkText = link;
                if (!view.state.tr.selection.empty) {
                    const selection = view.state.tr.selection;
                    let selectedText = '';
                    view.state.doc.nodesBetween(selection.from, selection.to, (node, position) => {
                        if (!node.isText)
                            return;
                        const start = Math.max(0, selection.from - position);
                        const end = Math.max(0, selection.to - position);
                        selectedText += node.textBetween(start, end);
                    });
                    if (selectedText)
                        linkText = selectedText;
                }
                const schema = view.state.schema;
                const linkAttrs = {
                    href: link,
                    markup: linkText === link ? 'linkify' : null,
                };
                const node = schema.text(linkText, [schema.marks.link.create(linkAttrs)]);
                view.dispatch(view.state.tr.replaceSelectionWith(node, false));
            }
            return true;
        },
    },
});

/** The cache of url -> content for link previews so we don't have to continually refetch */
const previewResultCache = {};
/**
 * Gets the first valid provider for a node from all registered providers
 * @param providers All registered providers
 * @param node The node to check for applicable links to match to the registered providers
 */
function getValidProvider(providers, node, parent) {
    if (!node?.isText || !parent)
        return null;
    const n = node; // TODO CLEANUP
    const url = n?.marks.find((m) => m.type.name === 'link')?.attrs?.href;
    // if there is no href, then nothing will match
    if (!url)
        return null;
    // check all providers for this
    for (const provider of providers) {
        if (!provider)
            continue;
        // full preview providers require links to be in a paragraph by themselves
        if (!provider.textOnly && !isStandalonePreviewableLink(parent))
            continue;
        // text only providers must be matched to text nodes
        if (provider.textOnly && !node.isText)
            continue;
        // Text-only provider could apply but this link already has a custom text,
        // so skip it
        if (provider.textOnly && url !== n?.textContent)
            continue;
        if (provider.domainTest && provider.domainTest.test(url))
            return { url, provider };
    }
    return null;
}
/**
 * Gets nodes in the document that are able to be resolved by a preview provider
 * @param doc The document to search through
 * @param providers The list of registered providers
 */
function getValidNodes(currState, prevState, providers) {
    // if the document didn't change, then we don't need to do anything
    // TODO can we do one step better and get only the added nodes?
    if (!docNodeChanged(currState, prevState))
        return;
    const validNodes = [];
    // iterate over current document structure
    currState.doc.descendants((node, pos, parent) => {
        const provider = getValidProvider(providers, node, parent);
        if (provider) {
            validNodes.push({ provider, pos, node });
            // no need to go into this node's descendants
            return false;
        }
    });
    return validNodes;
}
/**
 * Run over the entire document and find all previewable links and create a link preview decoration
 * for each.
 * @param {Document} doc - The document to find previewable link candidates in
 */
function generateAllPreviewDecorations(state, providers) {
    const nodes = getValidNodes(state, null, providers);
    const mapped = nodes.map((n) => {
        return {
            content: previewResultCache[n.provider.url],
            href: n.provider.url,
            isTextOnly: n.provider.provider.textOnly,
            pos: n.pos,
        };
    });
    return generateRecentPreviewDecorations(state.doc, mapped);
}
/**
 * Create a link preview decorations for a set of specific link preview results.
 * @param {Document} doc - The document to generate decorations against
 */
function generateRecentPreviewDecorations(doc, recentlyUpdated) {
    const linkPreviewDecorations = [];
    recentlyUpdated.forEach((n) => {
        if (!n.isTextOnly && n.content) {
            // if the url is in the cache, insert the link preview
            linkPreviewDecorations.push(insertLinkPreview(n.pos, n.content));
        }
        else if (!n.content) {
            // otherwise, add the loading styles
            // attach the node decorations to the text's parent node
            const resolved = doc.resolve(n.pos);
            const parentPos = resolved.posAtIndex(0, resolved.depth - 1);
            linkPreviewDecorations.push(Decoration.node(parentPos, parentPos + resolved.parent.nodeSize, {
                class: 'is-loading js-link-preview-loading',
                title: 'Loading...',
            }));
        }
    });
    return DecorationSet.create(doc, linkPreviewDecorations);
}
/**
 * Inserts the link preview's content into the link's decoration/placeholder
 * @param placeholder The placeholder originally created to house this content
 * @param content The content returned from the link preview to insert
 */
function insertLinkPreview(pos, content) {
    const placeholder = document.createElement('div');
    // give this a targetable class for external use / e2e testing
    placeholder.className = 'js-link-preview-decoration';
    if (content)
        placeholder.appendChild(content.cloneNode(true));
    return Decoration.widget(pos, placeholder, {
        side: -1,
    });
}
/**
 * Figure out if a given node in the document is a candidate for a link preview.
 * This will find all paragraphs that consist only of a link and nothing else.
 *
 * @param {Node} node - The node that should be checked
 */
function isStandalonePreviewableLink(node) {
    const child = node.content.firstChild;
    if (!child)
        return false;
    const hasOnlyOneChild = node.childCount === 1;
    const childIsTextNode = child.type.name === 'text';
    const childHasLinkMark = child.marks.some((mark) => mark.type.name === 'link');
    // TODO don't return true if the preview has already rendered
    return hasOnlyOneChild && childIsTextNode && childHasLinkMark;
}
/**
 * Fetches and caches all link preview content for every link node in the view
 * @param view The view to search for valid link nodes
 * @param providers The list of registered providers
 */
function fetchLinkPreviewContent(view, prevState, providers) {
    const nodes = getValidNodes(view.state, prevState, providers);
    // if there's no new nodes to render, just reject (no need to update the state)
    if (!nodes.length)
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject(null);
    const results = nodes.map((n) => {
        const previouslyCached = n.provider.url in previewResultCache;
        const cachedContent = previewResultCache[n.provider.url] || null;
        // if the content is already cached, don't call the renderer again
        const basePromise = previouslyCached ? Promise.resolve(cachedContent) : n.provider.provider.renderer(n.provider.url);
        const output = {
            pos: n.pos,
            content: cachedContent,
            isTextOnly: n.provider.provider.textOnly,
            href: n.provider.url,
        };
        const promise = basePromise
            .then((content) => {
            // cache results so we don't call over and over...
            previewResultCache[n.provider.url] = content;
            return {
                content,
                isTextOnly: n.provider.provider.textOnly,
                href: n.provider.url,
                pos: n.pos,
            };
        })
            // don't let any errors crash our `.all` below
            // "catch" and fake a resolution
            .catch(() => {
            // TODO make this look nice
            const errorPlaceholder = document.createElement('div');
            errorPlaceholder.innerText = 'Error fetching content.';
            // set the cache here too, so we don't refetch errors every time...
            previewResultCache[n.provider.url] = errorPlaceholder;
            return Promise.resolve({});
        });
        output.promise = promise;
        return output;
    });
    // trigger the rendering immediately
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    LINK_PREVIEWS_KEY.dispatchCallbackData(view, results);
    return Promise.all(results.map((p) => p.promise));
}
const LINK_PREVIEWS_KEY = new AsyncPluginKey('linkPreviews');
/**
 * Creates a plugin that searches the entire document for potentially previewable links
 * and creates a widget decoration to render the link preview in.
 */
function linkPreviewPlugin(providers) {
    const previewProviders = providers || [];
    return new AsyncPlugin({
        key: LINK_PREVIEWS_KEY,
        asyncCallback: (view, prevState) => {
            return fetchLinkPreviewContent(view, prevState, previewProviders);
        },
        state: {
            init(_, state) {
                return {
                    decorations: generateAllPreviewDecorations(state, previewProviders),
                };
            },
            apply(tr, value) {
                // only update the decorations if they changed at all
                const callbackData = this.getCallbackData(tr);
                if (callbackData) {
                    // make sure the positions are up to date with any changes
                    const updatedData = callbackData.map((d) => ({
                        ...d,
                        pos: tr.mapping.map(d.pos),
                    }));
                    return {
                        decorations: generateRecentPreviewDecorations(tr.doc, updatedData),
                        recentlyUpdated: updatedData,
                    };
                }
                // else update the mappings to their new positions in the doc
                return {
                    decorations: value.decorations.map(tr.mapping, tr.doc),
                };
            },
        },
        props: {
            //@ts-ignore
            decorations(state) {
                return this.getState(state).decorations;
            },
        },
        appendTransaction(trs, _, newState) {
            const data = LINK_PREVIEWS_KEY.getState(newState);
            // if no nodes were added or if there aren't any textOnly nodes added, return
            if (!data.recentlyUpdated?.length || !data.recentlyUpdated.some((d) => d.isTextOnly))
                return null;
            let tr = null;
            data.recentlyUpdated.forEach((n) => {
                if (!n.content?.textContent || !n.isTextOnly)
                    return;
                let pos = n.pos;
                trs.forEach((t) => {
                    pos = t.mapping.map(pos);
                });
                const schema = newState.schema;
                const newNode = schema.text(n.content.textContent, [schema.marks.link.create({ href: n.href, markup: null })]);
                const node = newState.doc.nodeAt(pos);
                const nodeSize = node.nodeSize;
                tr = (tr || newState.tr).replaceWith(pos, pos + nodeSize, newNode);
            });
            return tr;
        },
    });
}

const plainTextPasteHandler = new Plugin({
    props: {
        handlePaste(view, event, slice) {
            // user is only pasting plain text, so don't process
            if (!event.clipboardData?.getData('text/html'))
                return false;
            let textNode = slice.content.firstChild;
            // content might be a paragraph with a single text node
            if (textNode?.type?.name === 'paragraph' && textNode.childCount === 1)
                textNode = textNode.firstChild;
            // if the pasted content is not plain text, don't process
            if (!textNode || textNode.type.name !== 'text' || textNode.marks.length)
                return false;
            // check if we're pasting into a node with marks to avoid unnecessary capturing
            const { selection } = view.state;
            const insertLocation = selection.$from.nodeBefore;
            if (!insertLocation?.marks.length)
                return false;
            // finally, insert the text with *marks inherited*
            view.dispatch(view.state.tr.replaceSelectionWith(textNode, true));
            return true;
        },
    },
});

/**
 * Updates all spoiler nodes within the state's selection
 * @param tr The transaction to modify
 * @param state The state whose selection to check for spoiler nodes (does not have to be the current state!)
 * @param shouldReveal Whether to force the spoiler to be revealed or not
 * @param transactions The array of transactions to map node positions through (if the state is not current)
 */
function updateSpoilers(tr, state, shouldReveal, transactions) {
    const { from, to } = state.selection;
    state.doc.nodesBetween(from, to, (node, pos) => {
        // if spoiler node, set revealed attribute
        if (node.type.name === 'spoiler') {
            // inherit whatever attributes are already on the node,
            // but do NOT update the node directly (or else the view will not register as changed)
            const attrs = { ...node.attrs };
            attrs.revealed = shouldReveal;
            let wasDeleted = false;
            if (transactions?.length) {
                // map the position through each transaction to make sure the node we're altering still exists
                transactions.forEach((t) => {
                    const result = t.mapping.mapResult(pos);
                    // if the node was outright deleted, skip it!
                    if (result.deleted) {
                        wasDeleted = true;
                        return false;
                    }
                    // set pos to the current position of the node
                    pos = result.pos;
                });
            }
            // if the node was deleted, then there's nothing to do
            if (wasDeleted)
                return false;
            tr = tr.setNodeMarkup(pos, null, attrs);
            // don't recurse into this node's children
            return false;
        }
    });
    return tr;
}
/** Plugin that forces the `revealed` attr on "spoiler" nodes on selection and removes it on de-selection */
const spoilerToggle = new Plugin({
    appendTransaction(transactions, oldState, newState) {
        // if the doc / selection has not changed, nothing to do
        if (!docChanged(oldState, newState))
            return null;
        let tr = newState.tr;
        // un-reveal all spoilers from the old state/selection
        tr = updateSpoilers(tr, oldState, false, transactions);
        // reveal all spoilers in the new selection
        tr = updateSpoilers(tr, newState, true);
        // no nodes changed, just return
        if (!tr.steps.length)
            return null;
        // make sure this doesn't get added to the history
        tr = tr.setMeta('addToHistory', false);
        return tr;
    },
});

const tables = new Plugin({
    key: new PluginKey('tablesPlugin'),
    props: {
        /**
         * Users can copy & paste tables that originate from anywhere and
         * that use a structure we're not supporting (colspans, rowspans, etc).
         * Our markdown-based table format is pretty limited and trying to deal
         * with all those extravagant tables users might paste is a major pain
         * in the rear and can lead to countless ways of breaking the rich-text
         * document structure.
         *
         * This is why we're intercepting the paste event for tables,
         * find table nodes and replace them with a plain representation of their
         * text content only.
         * This way we avoid major headache at the expense of not allowing to copy
         * and paste table structures.
         *
         * Currently this only goes two levels deep, so this problem persists if
         * someone's pasting a table in e.g. deeply nested blockquotes.
         * A recursive approach would be able to tackle this but prosemirror's `forEach`
         * based implementation makes this awkward to pull off.
         */
        transformPasted(slice) {
            return new Slice(tablesToPlainText(slice.content), 1, 1);
        },
        /**
         * Handles pasting content into rich-text tables and makes sure
         * that every pasted content is pasted as plain text.
         *
         * Users could paste arbitrary stuff into table cells, including
         * nasty stuff like other (partial) table structures. If we don't
         * prevent this, there's a high chance that users can break our
         * quite strict concept of a table layout as prescribed by markdown
         * (each row must have the same amount of cells, etc).
         *
         * To prevent dealing with all edge cases that arise from pasting
         * weird rich-text nodes into a table, this plugin will intercept
         * every paste action that's supposed to insert content _into_ a
         * rich-text table and paste the copied contents as plain text.
         */
        handlePaste(view, event, slice) {
            if (inTable(view.state.schema, view.state.selection)) {
                view.dispatch(view.state.tr.insertText(findCellContent(slice)));
                return true;
            }
            return false;
        },
    },
});
function tablesToPlainText(fragment) {
    const sanitized = [];
    fragment.forEach((node) => {
        if (node.type === node.type.schema.nodes.table) {
            sanitized.push(node.type.schema.nodes.paragraph.createAndFill({}, node.type.schema.text(node.textContent)));
        }
        else {
            sanitized.push(node.copy(tablesToPlainText(node.content)));
        }
    });
    return Fragment.fromArray(sanitized);
}
function findCellContent(slice) {
    if (!slice.size)
        return null;
    if (slice.content && slice.content.firstChild)
        return slice.content.firstChild.textContent;
    return null;
}

const KEEP_OPEN = 'KEEP_OPEN';
var ActionKind;
(function (ActionKind) {
    ActionKind["open"] = "open";
    ActionKind["close"] = "close";
    ActionKind["filter"] = "filter";
    ActionKind["up"] = "ArrowUp";
    ActionKind["down"] = "ArrowDown";
    ActionKind["left"] = "ArrowLeft";
    ActionKind["right"] = "ArrowRight";
    ActionKind["enter"] = "enter";
})(ActionKind || (ActionKind = {}));

const pluginKey = new PluginKey('autocomplete');
function inSuggestion(selection, decorations) {
    return decorations.find(selection.from, selection.to).length > 0;
}

function openAutocomplete(view, trigger, filter) {
    // TODO: Can activate a type?
    const plugin = pluginKey.get(view.state);
    const meta = { action: 'add', trigger, filter, type: null };
    const tr = view.state.tr
        .insertText(`${trigger}${filter ?? ''}`)
        .scrollIntoView()
        .setMeta(plugin, meta);
    view.dispatch(tr);
}
function closeAutocomplete(view) {
    const plugin = pluginKey.get(view.state);
    const meta = { action: 'remove' };
    const tr = view.state.tr.setMeta(plugin, meta);
    view.dispatch(tr);
    return true;
}

const inactiveAutocompleteState = {
    active: false,
    decorations: DecorationSet.empty,
};
function actionFromEvent(event) {
    switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
            return event.key;
        case 'Tab':
        case 'Enter':
            return ActionKind.enter;
        case 'Escape':
            return ActionKind.close;
        default:
            return null;
    }
}
function cancelIfInsideAndPass(view) {
    const plugin = pluginKey.get(view.state);
    const { decorations } = plugin.getState(view.state);
    if (inSuggestion(view.state.selection, decorations)) {
        closeAutocomplete(view);
    }
    return false;
}
function getDecorationPlugin(reducer) {
    const plugin = new Plugin({
        key: pluginKey,
        view() {
            return {
                update: (view, prevState) => {
                    const prev = plugin.getState(prevState);
                    const next = plugin.getState(view.state);
                    const started = !prev.active && next.active;
                    const stopped = prev.active && !next.active;
                    const changed = next.active && !started && !stopped && prev.filter !== next.filter;
                    const action = {
                        view,
                        trigger: next.trigger ?? prev.trigger,
                        filter: next.filter ?? prev.filter,
                        range: next.range ?? prev.range,
                        type: next.type ?? prev.type,
                    };
                    if (started)
                        reducer({ ...action, kind: ActionKind.open });
                    if (changed)
                        reducer({ ...action, kind: ActionKind.filter });
                    if (stopped)
                        reducer({ ...action, kind: ActionKind.close });
                },
            };
        },
        state: {
            init: () => inactiveAutocompleteState,
            apply(tr, state) {
                const meta = tr.getMeta(plugin);
                if (meta?.action === 'add') {
                    const { trigger, filter, type } = meta;
                    const from = tr.selection.from - trigger.length - (filter?.length ?? 0);
                    const to = tr.selection.from;
                    const className = type?.decorationAttrs?.class ? ['autocomplete', type?.decorationAttrs?.class].join(' ') : 'autocomplete';
                    const attrs = { ...type?.decorationAttrs, class: className };
                    const deco = Decoration.inline(from, to, attrs, {
                        inclusiveStart: false,
                        inclusiveEnd: true,
                    });
                    return {
                        active: true,
                        trigger: meta.trigger,
                        decorations: DecorationSet.create(tr.doc, [deco]),
                        filter: filter ?? '',
                        range: { from, to },
                        type,
                    };
                }
                const { decorations } = state;
                const nextDecorations = decorations.map(tr.mapping, tr.doc);
                const hasDecoration = nextDecorations.find().length > 0;
                // If no decoration, explicitly remove, or click somewhere else in the editor
                if (meta?.action === 'remove' || !inSuggestion(tr.selection, nextDecorations) || !hasDecoration)
                    return inactiveAutocompleteState;
                const { active, trigger, type } = state;
                // Ensure that the trigger is in the decoration
                const { from, to } = nextDecorations.find()[0];
                const text = tr.doc.textBetween(from, to);
                if (!text.startsWith(trigger))
                    return inactiveAutocompleteState;
                return {
                    active,
                    trigger,
                    decorations: nextDecorations,
                    filter: text.slice(trigger.length),
                    range: { from, to },
                    type,
                };
            },
        },
        props: {
            decorations: (state) => plugin.getState(state)?.decorations,
            handlePaste: (view) => cancelIfInsideAndPass(view),
            handleDrop: (view) => cancelIfInsideAndPass(view),
            handleKeyDown(view, event) {
                const { trigger, active, decorations, type } = plugin.getState(view.state);
                if (!active || !inSuggestion(view.state.selection, decorations))
                    return false;
                const { from, to } = decorations.find()[0];
                const text = view.state.doc.textBetween(from, to);
                // Be defensive, just in case the trigger doesn't exist
                const filter = text.slice(trigger?.length ?? 1);
                const checkCancelOnSpace = type?.cancelOnFirstSpace ?? true;
                if (checkCancelOnSpace && filter.length === 0 && (event.key === ' ' || event.key === 'Spacebar')) {
                    closeAutocomplete(view);
                    // Take over the space creation so no other input rules are fired
                    view.dispatch(view.state.tr.insertText(' ').scrollIntoView());
                    return true;
                }
                if (filter.length === 0 && event.key === 'Backspace') {
                    undoInputRule(view.state, view.dispatch);
                    closeAutocomplete(view);
                    return true;
                }
                const kind = actionFromEvent(event);
                const action = {
                    view,
                    trigger,
                    filter,
                    range: { from, to },
                    type,
                };
                switch (kind) {
                    case ActionKind.close:
                        // The user action will be handled in the view code above
                        // Allows clicking off to be handled in the same way
                        return closeAutocomplete(view);
                    case ActionKind.enter: {
                        // Only trigger the cancel if it is not expliticly handled in the select
                        const result = reducer({ ...action, kind: ActionKind.enter });
                        if (result === KEEP_OPEN)
                            return true;
                        return result || closeAutocomplete(view);
                    }
                    case ActionKind.up:
                    case ActionKind.down:
                        return Boolean(reducer({ ...action, kind }));
                    case ActionKind.left:
                    case ActionKind.right:
                        if (!type?.allArrowKeys)
                            return false;
                        return Boolean(reducer({ ...action, kind }));
                    default:
                        break;
                }
                return false;
            },
        },
    });
    return plugin;
}

function createInputRule(plugin, type) {
    const trigger = typeof type.trigger === 'string' ? RegExp(`(?:^|\\s|\\n|[^\\d\\w])(${type.trigger.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})$`) : type.trigger;
    return new InputRule(trigger, (state, match) => {
        const { decorations } = plugin.getState(state);
        // If we are currently suggesting, don't activate
        if (inSuggestion(state.selection, decorations))
            return null;
        // We are taking over the text input here
        const tr = state.tr.insertText(match[1][match[1].length - 1]).scrollIntoView();
        const meta = { action: 'add', trigger: match[1], type };
        tr.setMeta(plugin, meta);
        return tr;
    });
}

function defaultReducer(options) {
    return (action) => {
        switch (action.kind) {
            case ActionKind.open:
                return options.onOpen?.(action) ?? false;
            case ActionKind.close:
                return options.onClose?.(action) ?? false;
            case ActionKind.up:
            case ActionKind.down:
            case ActionKind.left:
            case ActionKind.right:
                return options.onArrow?.(action) ?? false;
            case ActionKind.filter:
                return options.onFilter?.(action) ?? false;
            case ActionKind.enter:
                return options.onEnter?.(action) ?? false;
            default:
                return false;
        }
    };
}
function autocomplete(opts = {}) {
    const options = {
        triggers: [],
        reducer: defaultReducer(opts),
        ...opts,
    };
    const { reducer, triggers } = options;
    const plugin = getDecorationPlugin(reducer);
    const rules = [
        plugin,
        inputRules({
            // Create an input rule for each trigger
            rules: triggers.map((type) => createInputRule(plugin, type)),
        }),
    ];
    return rules;
}

const defaultConfig = {
    targetElementId: 'emoji',
    emojiContainerId: 'emoji-picker-container',
    emojiHeight: '400px',
    emojiTheme: 'light',
};
const emojiButtonHandler = (editorView, opts = {}) => {
    var opened = false;
    let outsideClickListener = null;
    const config = {
        ...defaultConfig,
        ...opts,
    };
    const checkIfExistingEmojiInstance = () => {
        const isExist = document.querySelectorAll(`#${config.emojiContainerId}`);
        return isExist.length > 0 ? isExist : false;
    };
    const getEmojiPickerInstance = () => {
        return new Picker();
    };
    const prepareEmojiAttachment = () => {
        const picker = getEmojiPickerInstance();
        picker.className = config.emojiTheme;
        const container = emojiContainer();
        container.append(picker);
        return { container, picker };
    };
    const emojiContainer = () => {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.zIndex = '99999';
        div.id = config.emojiContainerId;
        div.style.maxHeight = config.emojiHeight;
        document.body.appendChild(div);
        return div;
    };
    const attachElement = (targetId, node) => {
        const rect = document.getElementById(targetId)?.getBoundingClientRect() || document.getElementsByClassName(targetId)[0]?.getBoundingClientRect();
        if (!rect)
            return;
        node.style.top = `${rect.top - 100}px`;
        node.style.left = `${rect.left + 10}px`;
    };
    const existingNodes = checkIfExistingEmojiInstance();
    if (existingNodes) {
        existingNodes.forEach((item) => item.remove());
        opened = false;
        if (outsideClickListener) {
            document.removeEventListener('click', outsideClickListener);
        }
        return;
    }
    const hideOnClickOutside = (selector) => {
        outsideClickListener = (event) => {
            const $target = event.target;
            const specifiedElement = document.getElementById(config.emojiContainerId);
            const isClickedInside = specifiedElement && specifiedElement.contains($target);
            if (!$target.closest(selector) && checkIfExistingEmojiInstance() && !opened) {
                opened = true;
            }
            else {
                if (!isClickedInside) {
                    removeClickListener();
                    const existingNodes = checkIfExistingEmojiInstance();
                    if (existingNodes) {
                        existingNodes.forEach((item) => item.remove());
                        opened = false;
                    }
                }
            }
        };
        const removeClickListener = () => {
            document.removeEventListener('click', outsideClickListener);
        };
        document.addEventListener('click', outsideClickListener);
    };
    const { picker, container } = prepareEmojiAttachment();
    attachElement(config.targetElementId, container);
    hideOnClickOutside(`#${config.emojiContainerId}`);
    picker.addEventListener('emoji-click', (event) => {
        editorView.updateState(editorView.state.apply(editorView.state.tr.insertText(`${event.detail.unicode}`)));
        opened = false;
        if (outsideClickListener) {
            document.removeEventListener('click', outsideClickListener);
        }
        emojiButtonHandler(editorView);
    });
};

const suggestion = document.querySelector('#suggestion');
const info = document.querySelector('#info');
const picker = {
    view: null,
    open: false,
    current: 0,
    range: null,
};
const NUM_SUGGESTIONS = suggestion?.children?.length || 0;
function setInfo(action) {
    const info = document.querySelector('#info');
    info.innerText = `Action: ${action.kind}, Range: ${action.range.from}-${action.range.to}, Filter: ${action.filter}, Trigger: ${action.trigger}, Type: ${action.type?.name}`;
}
function placeSuggestion(action) {
    if (!action)
        return;
    switch (action.trigger) {
        case '@':
            break;
        case ':':
            emojiButtonHandler(picker.view, {
                targetElementId: 'autocomplete',
            });
            break;
        default:
            break;
    }
}
function reducer(action) {
    picker.view = action.view;
    // setInfo(action)
    switch (action.kind) {
        case ActionKind.open:
            picker.current = 0;
            picker.open = true;
            picker.range = action.range;
            placeSuggestion(action);
            return true;
        case ActionKind.close:
            picker.open = false;
            placeSuggestion(action);
            return true;
        case ActionKind.up:
            picker.current -= 1;
            picker.current += NUM_SUGGESTIONS; // negative modulus doesn't work
            picker.current %= NUM_SUGGESTIONS;
            placeSuggestion(action);
            return true;
        case ActionKind.down:
            picker.current += 1;
            picker.current %= NUM_SUGGESTIONS;
            placeSuggestion(action);
            return true;
        case ActionKind.enter: {
            const values = ['😄', '🔥', '😅'];
            const tr = action.view.state.tr.deleteRange(action.range.from, action.range.to).insertText(values[picker.current]);
            action.view.dispatch(tr);
            return true;
        }
        default:
            return false;
    }
}
setTimeout(() => {
    ;
    [].forEach.call(suggestion?.children || [], (item, i) => {
        item.addEventListener('click', () => {
            if (!picker.view)
                return;
            closeAutocomplete(picker.view);
            picker.open = false;
            placeSuggestion();
            if (!picker.range)
                return;
            const tr = picker.view.state.tr.deleteRange(picker.range.from, picker.range.to).insertText(`Clicked on ${i + 1}`);
            picker.view.dispatch(tr);
            picker.view.focus();
        });
    });
}, 1000);

const counterHandler = (options) => new Plugin({
    props: {
        handleTextInput: (view, event, slice) => {
            if (event >= options.maxLength) {
                options.currentState(false, true, event);
                return false;
            }
            if (event < options.maxLength && options.minShowCounterLength < event) {
                options.currentState(true, true, event);
                return false;
            }
            options.currentState(true, false, event);
            return false;
        },
        handlePaste: (view, event, slice) => {
            const pasteData = event.clipboardData?.getData('text/plain');
            if (!pasteData) {
                return false;
            }
            const charLength = pasteData.length + view.state.doc.textContent.length;
            if (charLength >= options.maxLength) {
                options.currentState(false, true, charLength);
                return false;
            }
            if (charLength < options.maxLength && options.minShowCounterLength < charLength) {
                options.currentState(true, true, charLength);
                return false;
            }
            options.currentState(true, false, charLength);
            return false;
        },
    },
    view: () => {
        return {
            update: (view) => {
                const charLength = view.state.doc.textContent.length;
                if (charLength >= options.maxLength) {
                    options.currentState(false, true, charLength);
                    return;
                }
                if (charLength < options.maxLength && options.minShowCounterLength < charLength) {
                    options.currentState(true, true, charLength);
                    return;
                }
                options.currentState(true, false, charLength);
            },
        };
    },
});

/*
 * Implements an WYSIWYG-style editor. Content will be rendered immediately by prosemirror but the in- and output will still be markdown
 */
class RichTextEditor extends BaseView {
    constructor(target, content, pluginProvider, options = {}) {
        super();
        this.options = deepMerge(RichTextEditor.defaultOptions, options);
        this.externalPluginProvider = pluginProvider;
        this.markdownSerializer = e11MarkdownSerializer(this.externalPluginProvider);
        this.finalizedSchema = new Schema(this.externalPluginProvider.getFinalizedSchema(richTextSchemaSpec));
        this.finalizedSchema = new Schema({
            nodes: addTagNodes(addMentionNodes(this.finalizedSchema.spec.nodes)),
            marks: this.finalizedSchema.spec.marks,
        });
        this.markdownParser = buildMarkdownParser(this.options.parserFeatures, this.finalizedSchema, this.externalPluginProvider, this.options.mentions);
        const doc = this.parseContent(content);
        const menuEntries = this.externalPluginProvider.getFinalizedMenu(createMenuEntries(this.finalizedSchema, this.options), EditorType.RichText, doc.type.schema);
        const menu = createMenuPlugin(menuEntries, this.options.menuParentContainer);
        const tagLinkOptions = this.options.parserFeatures.tagLinks;
        const autoCompleteOptions = {
            reducer,
            triggers: [
            // For now only enable emoji trigger (:)
            // { name: 'hashtag', trigger: /(#)$/ },
            // { name: 'mention', trigger: /(@)$/ },
            // { name: 'emoji', trigger: ':' },
            ],
        };
        this.editorView = new EditorView((node) => {
            node.classList.add(...(this.options.classList || []));
            target.appendChild(node);
        }, {
            editable: editableCheck,
            state: EditorState.create({
                schema: this.finalizedSchema,
                doc,
                plugins: [
                    mentionPlugin(this.options.mentions),
                    history(),
                    emojiInputRules(this.finalizedSchema),
                    ...autocomplete(autoCompleteOptions),
                    ...allKeymaps(this.finalizedSchema, this.options.parserFeatures),
                    menu,
                    richTextInputRules(this.finalizedSchema, this.options.parserFeatures),
                    linkPreviewPlugin(this.options.linkPreviewProviders),
                    CodeBlockHighlightPlugin(this.options.codeblockOverrideLanguage),
                    interfaceManagerPlugin(this.options.pluginParentContainer),
                    linkEditorPlugin(this.options.parserFeatures),
                    placeholderPlugin(this.options.placeholderText),
                    dragPlugin(),
                    fileUploader(this.options.fileUpload),
                    readonlyPlugin(),
                    spoilerToggle,
                    tables,
                    richTextCodePasteHandler,
                    linkPasteHandler(this.options.parserFeatures),
                    ...this.externalPluginProvider.plugins.richText,
                    counterHandler(this.options.counterOption),
                    // IMPORTANT: the plainTextPasteHandler must be added after *all* other paste handlers
                    plainTextPasteHandler,
                ],
            }),
            nodeViews: {
                code_block: (node, view, getPos) => {
                    return new CodeBlockView(node, view, getPos, this.externalPluginProvider.codeblockProcessors);
                },
                image(node, view, getPos) {
                    return new ImageView(node, view, getPos);
                },
                tagLink(node) {
                    return new TagLink(node, tagLinkOptions);
                },
                html_block(node) {
                    return new HtmlBlock(node);
                },
                html_block_container(node) {
                    return new HtmlBlockContainer(node);
                },
                ...this.externalPluginProvider.nodeViews,
            },
            plugins: [],
        });
        log('prosemirror rich-text document', 
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.editorView.state.doc.toJSON().content);
    }
    static get defaultOptions() {
        return {
            parserFeatures: defaultParserFeatures,
            editorHelpLink: null,
            linkPreviewProviders: [],
            codeblockOverrideLanguage: null,
            menuParentContainer: null,
            imageUpload: {
                //@ts-ignore
                handler: defaultImageUploadHandler,
            },
            editorPlugins: [],
            counterOption: {
                maxLength: 12000,
                minShowCounterLength: 9000,
                currentState: (valid, showCounter, currentCounter) => console.log({
                    valid,
                    showCounter,
                    currentCounter,
                }),
            },
        };
    }
    parseContent(content) {
        let doc;
        try {
            doc = this.markdownParser.parse(content);
        }
        catch (e) {
            // there was a catastrophic error! Try not to lose the user's doc...
            error('RichTextEditorConstructor markdownParser.parse', 'Catastrophic parse error!', e);
            doc = CodeStringParser.fromSchema(this.finalizedSchema).parseCode(content);
            // manually add an h1 warning to the newly parsed doc
            const tr = new Transform(doc).insert(0, this.finalizedSchema.node('heading', { level: 1 }, this.finalizedSchema.text('WARNING! There was an error parsing the document')));
            doc = tr.doc;
        }
        return doc;
    }
    serializeContent() {
        return this.markdownSerializer.serialize(this.editorView.state.doc);
    }
}

/**
 * {@inheritDoc IExternalPluginProvider}
 * @internal
 */
class ExternalPluginProvider {
    /** {@inheritDoc IExternalPluginProvider.codeblockProcessors} */
    get codeblockProcessors() {
        return Object.assign({}, this._codeblockProcessors);
    }
    /** {@inheritDoc IExternalPluginProvider.plugins} */
    get plugins() {
        return this._plugins;
    }
    /** {@inheritDoc IExternalPluginProvider.markdownProps} */
    get markdownProps() {
        return this._markdownProps;
    }
    /** {@inheritDoc IExternalPluginProvider.nodeViews} */
    get nodeViews() {
        return this._nodeViews;
    }
    constructor(plugins, options) {
        this._codeblockProcessors = {};
        this._plugins = {
            richText: [],
            commonmark: [],
        };
        this._markdownProps = {
            parser: {},
            serializers: {
                nodes: {},
                marks: {},
            },
        };
        this._nodeViews = {};
        this.menuCallbacks = [];
        this.schemaCallbacks = [];
        this.markdownItCallbacks = [];
        if (plugins?.length) {
            for (const plugin of plugins)
                this.applyConfig(plugin(options));
        }
    }
    /** {@inheritDoc IExternalPluginProvider.getFinalizedSchema} */
    getFinalizedSchema(schema) {
        let alteredSchema = {
            nodes: OrderedMap.from(schema.nodes),
            marks: OrderedMap.from(schema.marks),
        };
        for (const callback of this.schemaCallbacks) {
            if (callback)
                alteredSchema = callback(alteredSchema);
        }
        return alteredSchema;
    }
    /** {@inheritDoc IExternalPluginProvider.alterMarkdownIt} */
    alterMarkdownIt(instance) {
        for (const callback of this.markdownItCallbacks) {
            if (callback)
                callback(instance);
        }
    }
    /** {@inheritDoc IExternalPluginProvider.getFinalizedMenu} */
    getFinalizedMenu(menu, editorType, schema) {
        const ret = [...menu];
        const aggBlocks = [];
        // call each callback and aggregate the results
        for (const callback of this.menuCallbacks) {
            if (!callback)
                continue;
            const blocks = callback(schema);
            for (const block of blocks) {
                let existing = aggBlocks.find((b) => b.name === block.name);
                if (!existing) {
                    aggBlocks.push(block);
                    existing = block;
                }
                else {
                    existing.entries.push(...block.entries);
                }
                // set the priority to the most recently declared if there are multiple
                existing.priority = block.priority || Infinity;
            }
        }
        // add the blocks to the menu
        for (const block of aggBlocks) {
            const entries = this.convertMenuCommandEntries(block.entries, editorType);
            // try to find an existing block with the same name
            const match = ret.find((b) => b.name === block.name);
            if (match) {
                // if there is a match, add the entries to it
                match.entries.push(...entries);
                // set the priority to the lowest of the two
                match.priority = Math.min(match.priority || 0, block.priority);
            }
            else {
                ret.push({
                    name: block.name,
                    priority: block.priority,
                    visible: block.visible,
                    classes: block.classes,
                    entries,
                });
            }
        }
        return ret;
    }
    /** Applies the config of a single plugin to this provider */
    applyConfig(config) {
        config.codeBlockProcessors?.forEach(({ lang, callback }) => {
            this.addCodeBlockProcessor(lang, callback);
        });
        config.commonmark?.plugins?.forEach((plugin) => {
            this._plugins.commonmark.push(plugin);
        });
        config.richText?.plugins?.forEach((plugin) => {
            this._plugins.richText.push(plugin);
        });
        this.schemaCallbacks.push(config.extendSchema);
        if (config.richText?.nodeViews) {
            this._nodeViews = {
                ...this._nodeViews,
                ...config.richText?.nodeViews,
            };
        }
        this.extendMarkdown(config.markdown, config.markdown?.alterMarkdownIt);
        this.menuCallbacks.push(config.menuItems);
    }
    /** Applies the markdownProps of a config to this provider */
    extendMarkdown(props, callback) {
        // TODO sanitize input to ensure nodes/marks for added parsers and vice versa?
        if (props?.parser) {
            this._markdownProps.parser = {
                ...this._markdownProps.parser,
                ...props.parser,
            };
        }
        if (props?.serializers?.nodes) {
            this._markdownProps.serializers.nodes = {
                ...this._markdownProps.serializers.nodes,
                ...props.serializers.nodes,
            };
        }
        if (props?.serializers?.marks) {
            this._markdownProps.serializers.marks = {
                ...this._markdownProps.serializers.marks,
                ...props.serializers.marks,
            };
        }
        if (callback)
            this.markdownItCallbacks.push(callback);
    }
    /** Applies the codeblockProcessors of a config to this provider */
    addCodeBlockProcessor(lang, callback) {
        if (!(lang in this._codeblockProcessors))
            this._codeblockProcessors[lang] = [];
        this._codeblockProcessors[lang].push(callback);
    }
    /** Converts a PluginMenuCommandEntry to a regular MenuCommandEntry */
    convertMenuCommandEntries(entries, editorType) {
        const ret = [];
        if (!entries?.length)
            return [];
        for (const entry of entries) {
            let commandEntry = {
                command: null,
                visible: undefined,
                active: undefined,
                dom: null,
                key: entry.key,
                //@ts-ignore
                children: this.convertMenuCommandEntries(entry.children, editorType),
            };
            const command = editorType === EditorType.RichText ? entry.richText : entry.commonmark;
            // check for an extended vs simple command
            if (command) {
                commandEntry = {
                    ...commandEntry,
                    ...('command' in command ? command : { command }),
                };
            }
            if (entry.children?.length) {
                commandEntry = makeMenuDropdown(entry.display.svg, entry.display.label, commandEntry.key, commandEntry.visible, commandEntry.active, ...commandEntry.children);
            }
            else if (this.displayIsButton(entry.display)) {
                commandEntry.dom = makeMenuButton(entry.display.svg, entry.display.label, commandEntry.key, []);
            }
            else {
                commandEntry.dom = entry.display;
            }
            // TODO add keybind and update the dom label properly
            ret.push(commandEntry);
        }
        return ret;
    }
    /** Helper method for checking the type of {@link PluginMenuItem.display} */
    displayIsButton(display) {
        return 'svg' in display;
    }
}

/**
 * A full editor that wraps and manages the state of both RichText and Commonmark editors
 */
class E11Editor {
    static { this.READONLY_CLASSES = ['s-input__readonly']; }
    constructor(target, content, options = {}) {
        // do a deep merge of the passed options with our default options
        this.options = deepMerge(E11Editor.defaultOptions, options);
        this.target = target;
        // naively generate a random internalId for this editor instance
        this.internalId = generateRandomId();
        this.innerTarget = document.createElement('div');
        this.target.appendChild(this.innerTarget);
        this.setupPluginContainer();
        this.pluginProvider = new ExternalPluginProvider(this.options.editorPlugins, this.options);
        this.setBackingView(this.options.defaultView, content);
    }
    get editorView() {
        return this.backingView?.editorView;
    }
    get content() {
        return this.backingView?.content || '';
    }
    set content(value) {
        this.backingView.content = value;
    }
    get document() {
        return this.editorView.state.doc;
    }
    get dom() {
        return this.editorView.dom;
    }
    get readonly() {
        if (!this.editorView)
            return false;
        return !this.editorView.editable;
    }
    get currentViewType() {
        return this.backingView instanceof RichTextEditor ? EditorType.RichText : EditorType.Commonmark;
    }
    static get defaultOptions() {
        const commonClasses = [
            'fl-grow1',
            'outline-none',
            'p12',
            'pt6',
            'w100',
            'e11--prose',
            // "s-prose", //TODO re-add once s-prose is the default in Core and we can hardcode here
            // in case this needs to be reference by outside code or e2e tests
            'js-editor',
            'e11-h-max-[250px]',
            'e11-min-h-32',
            'e11-p-4',
            'e11-bg-white',
            'e11-resize-y',
            'e11-overflow--y-scroll',
            'e11-outline-0',
            // added automatically, but let's be explicit for code clarity
            'ProseMirror',
        ];
        return {
            defaultView: EditorType.RichText,
            targetClassList: [],
            parserFeatures: RichTextEditor.defaultOptions.parserFeatures,
            commonmarkOptions: {
                classList: commonClasses,
                preview: {
                    enabled: false,
                },
            },
            richTextOptions: {
                classList: commonClasses,
            },
        };
    }
    focus() {
        this.backingView.focus();
    }
    destroy() {
        this.backingView.destroy();
    }
    /**
     * Sets the editor view the the passed type
     * @param type The type of editor to set the view to
     */
    setView(type) {
        this.setBackingView(type, null);
    }
    /**
     * Enables the editor view
     */
    enable() {
        toggleReadonly(false, this.editorView.state, this.editorView.dispatch.bind(null));
        this.innerTarget.classList.remove(...E11Editor.READONLY_CLASSES);
    }
    /**
     * Disables the editor view
     */
    disable() {
        toggleReadonly(true, this.editorView.state, this.editorView.dispatch.bind(null));
        this.innerTarget.classList.add(...E11Editor.READONLY_CLASSES);
    }
    /**
     * Reinitializes the editor with a subset of options that are merged into the current options
     * @param options The options that are changing between initializations
     */
    reinitialize(options = {}) {
        this.options = deepMerge(this.options, options);
        this.setBackingView(this.currentViewType, this.content);
    }
    /**
     * Sets up the plugin and menu containers at the top of the editor. These are areas set aside for
     * plugins to render their content in a reliable manner. This area cancels mousedown events to keep the editor
     * from blurring and also handles sticking the content to the top of the editor on scroll.
     */
    setupPluginContainer() {
        // create an area where plugins can be placed
        this.pluginContainer = document.createElement('div');
        this.pluginContainer.className = ` s-editor-shadow js-plugin-container e11-sticky e11-z-[5] e11-top ps-sticky ${STICKY_OBSERVER_CLASS}`;
        // create specific area for the editor menu
        const menuTarget = document.createElement('div');
        menuTarget.className = 'e11-flex e11-justify-between e11-items-center e11-pt-2 e11-px-2';
        this.pluginContainer.appendChild(menuTarget);
        // set the editors' menu containers to be the combo container
        const menuContainerFn = () => menuTarget;
        this.options.menuParentContainer = menuContainerFn;
        // create specific area for the editor menu
        const previewTarget = document.createElement('div');
        previewTarget.className = 'py16';
        this.target.appendChild(previewTarget);
        // set the editors' preview containers to be the combo container
        const previewContainerFn = () => previewTarget;
        this.options.commonmarkOptions.preview.parentContainer = previewContainerFn;
        // create a specific area for the editor plugins
        const pluginTarget = document.createElement('div');
        this.pluginContainer.appendChild(pluginTarget);
        const pluginContainerFn = () => pluginTarget;
        this.options.pluginParentContainer = pluginContainerFn;
        this.innerTarget.appendChild(this.pluginContainer);
        this.createEditorSwitcher(this.options.defaultView, menuTarget);
        // watch the sticky header and add additional styling when it becomes unstuck
        startStickyObservers(this.innerTarget);
        document.addEventListener('sticky-change', ((e) => {
            const target = e.detail.target;
            if (!target.classList.contains('js-plugin-container'))
                return;
            target.classList.toggle('is-stuck', e.detail.stuck);
        }));
    }
    /**
     * Sets the backing editor view to the passed type filled with the passed content
     * @param type The type to set the backing view to
     * @param content The markdown content to fill the editor with
     */
    setBackingView(type, content) {
        // save the readonly value and reinstate after swapping the views
        const readonly = this.readonly;
        if (this.backingView) {
            content = content || this.backingView.content;
            this.backingView.destroy();
        }
        // inner editor destroy does not clear the added classes, so reset them here
        this.innerTarget.classList.add(...this.options.targetClassList);
        if (type === EditorType.RichText) {
            this.backingView = new RichTextEditor(this.innerTarget, content, this.pluginProvider, deepMerge(this.options, this.options.richTextOptions));
        }
        else if (type === EditorType.Commonmark) {
            this.backingView = new CommonmarkEditor(this.innerTarget, content, this.pluginProvider, deepMerge(this.options, this.options.commonmarkOptions));
        }
        else {
            // eslint-disable-next-line no-throw-literal
            throw `Unable to set editor to unknown type: ${EditorType[type]}`;
        }
        // set up focus/blur listeners so we can style the dom to match
        this.backingView.editorView.props.handleDOMEvents = {
            focus: () => {
                // this.innerTarget.classList.add('e11-ring', 'bc-blue-300')
                return false;
            },
            blur: () => {
                // this.innerTarget.classList.remove('e11-ring', 'bc-blue-300')
                return false;
            },
        };
        // re-sync the view readonly state / classes
        if (readonly)
            this.disable();
        else
            this.enable();
    }
    /**
     * Creates the button that toggles the backing view type
     * @param defaultItem The type that is set as the default
     * @param menuTarget The container to append the created element to
     */
    createEditorSwitcher(defaultItem, menuTarget) {
        const checkedProp = defaultItem === EditorType.Commonmark ? 'checked' : '';
        const container = document.createElement('div');
        container.className = `sm:e11-flex e11-items-center e11-gap-1 ml24 fc-medium`;
        container.innerHTML = escapeHTML `<label class="flex--item fs-caption mr4 sm:e11-none e11-text-sm" for="js-editor-toggle-${this.internalId}">${_t('menubar.mode_toggle_label')}</label>
            <label class="flex--item mr4 d-none sm:d-block" for="js-editor-toggle-${this.internalId}">
                <span class="icon-bg iconMarkdown"></span>
            </label>
            <div class="flex--item s-toggle-switch js-editor-mode-switcher">
                <input class="js-editor-toggle-state focus:e11-ring-blue-400 e11-h-4 e11-w-4 e11-text-blue-400 e11-border-gray-300 e11-rounded" id="js-editor-toggle-${this.internalId}" type="checkbox" ${checkedProp}/>
                <div class="s-toggle-switch--indicator"></div>
            </div>`;
        container.title = _t('menubar.mode_toggle_title');
        container.querySelector(`#js-editor-toggle-${this.internalId}`).addEventListener('change', this.editorSwitcherChangeHandler.bind(this));
        menuTarget.appendChild(container);
    }
    /**
     * Handles toggling the editor when the switcher is clicked
     * and fires a change event from the target element for consumers to listen for
     * @param e The click event
     */
    editorSwitcherChangeHandler(e) {
        e.stopPropagation();
        e.preventDefault();
        // get opposing type
        const type = this.currentViewType === EditorType.Commonmark ? EditorType.RichText : EditorType.Commonmark;
        // set the view type for this button
        this.setView(type);
        // ensure the checkbox matches the selected editor
        this.target.querySelector(`#js-editor-toggle-${this.internalId}`).checked = type === EditorType.Commonmark;
        // TODO better event name?
        // trigger an event on the target for consumers to listen for
        dispatchEditorEvent(this.target, 'view-change', {
            editorType: type,
        });
        // TODO do we always want to focus the editor?
        this.focus();
    }
}

const ValueChangesPlugin = (cb) => {
    return new Plugin({
        view() {
            return {
                update(updateView) {
                    cb();
                },
            };
        },
    });
};
const ValueChangesExternalPlugin = (cb) => () => {
    return {
        richText: {
            plugins: [ValueChangesPlugin(cb)],
        },
        commonmark: {
            plugins: [ValueChangesPlugin(cb)],
        },
    };
};

const DEFAULT_EDITOR_OPTIONS = {
    header: {
        text: [
            {
                type: 'bold',
                icon: 'format_bold',
                visible: true,
            },
            {
                type: 'italic',
                icon: 'format_italic',
                visible: true,
            },
            {
                type: 'strikethrough',
                icon: 'strikethrough_s',
                visible: true,
            },
        ],
        link: [
            {
                type: 'link',
                icon: 'link',
                visible: true,
            },
        ],
        list: [
            {
                type: 'ordered-list',
                icon: 'format_list_numbered',
                visible: true,
            },
            {
                type: 'unordered-list',
                icon: 'format_list_bulleted',
                visible: true,
            },
            {
                type: 'task',
                icon: 'checklist',
                visible: true,
            },
        ],
        code: [
            {
                type: 'code',
                icon: 'code',
                visible: true,
            },
            {
                type: 'code-block',
                icon: 'developer_mode',
                visible: true,
            },
        ],
        full_screen: [
            {
                type: 'full_screen',
                icon: 'open_in_full',
                visible: true,
            },
        ],
    },
    footer: {
        attachment: [
            {
                type: 'file',
                icon: 'attach_file',
                visible: true,
            },
        ],
        audio: [
            {
                type: 'video',
                icon: 'videocam',
                visible: true,
            },
            {
                type: 'voice',
                icon: 'mic',
                visible: true,
            },
            {
                type: 'audio',
                icon: 'graphic_eq',
                visible: true,
            },
        ],
        social: [
            {
                type: 'emoji',
                icon: 'add_reaction',
                visible: true,
            },
            {
                type: 'mention',
                icon: 'alternate_email',
                visible: true,
            },
        ],
        action: [
            {
                type: 'send',
                icon: 'send',
                visible: true,
            },
        ],
    },
};

class E11MarkdownEditorComponent {
    set editorOption(options) {
        this._editorOptions = mergeDeep(DEFAULT_EDITOR_OPTIONS, options ?? {});
    }
    set fileInputs(files) {
        setTimeout(() => {
            this.selectFile(files);
        }, 100);
    }
    constructor(zone) {
        this.zone = zone;
        /**
         * @internal
         */
        this.randomId = 'e11-markdown-editor' + generateRandomId();
        this._editorOptions = DEFAULT_EDITOR_OPTIONS;
        this.isDisabled = false;
        this.send = new EventEmitter();
        this.stateChanged = new EventEmitter();
        this.recordVideo = new EventEmitter();
        this.onFileSelection = new EventEmitter();
        this._onError = new EventEmitter();
        this.onReady = new EventEmitter();
        this.onFileRemove = new EventEmitter();
        this.tempFiles = [];
        this.message = '';
        this.placeholder = 'Start typing';
        this.hasError = false;
        this.maxLength = 12000;
        this.minShowCounterLength = 10000;
        this.maxFileSize = 8; // in MB
        this.fieldClassList = [];
        this.loading = false;
        this.currentCharacterCount = 0;
        this.showCounter = false;
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    writeValue(initialValue) {
        this.message = initialValue;
        if (this.editorInstance) {
            this.editorInstance.content = initialValue;
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
        if (this.editorInstance) {
            this.markEditorReadOnly(isDisabled);
        }
    }
    ngAfterViewInit() {
        this.initEditor();
        // this.emitMessageChanges() // Replace with custom plugin
        this.zone.runOutsideAngular(() => {
            window.document.addEventListener('markdown-enter', () => {
                this.enterKeyPress();
            });
        });
        this.onReady.emit();
    }
    get editorElementHTMLElement() {
        return document.querySelector(`#${this.randomId}`);
    }
    ngOnDestroy() { }
    reset() {
        this.tempFiles = [];
        if (this.editorInstance)
            this.editorInstance.content = '';
    }
    fileAction(event) {
        switch (event.action) {
            case FileActions.Remove:
                const removedFile = this.tempFiles.splice(event.index, 1);
                this.onFileRemove.emit(removedFile[0]);
                this.tempFiles = [...this.tempFiles];
                break;
        }
    }
    async selectFile(files) {
        // lets do a validation for the file type and file size
        if (this.tempFiles.length + files.length > 8) {
            // show error
            this._onError.emit({ type: 'length' });
            return false;
        }
        // file type checker
        if (!files.reduce((acc, file) => acc && validateFileSize(file, this.maxFileSize), true)) {
            // show error
            this._onError.emit({ type: 'size' });
            return false;
        }
        // convert image files
        const validatedFile = await validateFileUploads(files);
        const newFiles = validatedFile.map((file) => generateIMarkdownFile(file));
        this.tempFiles = [...this.tempFiles, ...newFiles];
        this.onFileSelection.emit(this.tempFiles);
        this.stateChanged.emit(this.getCurrentState());
    }
    processAction(action) {
        // do something
        switch (action.type) {
            case MarkDownEditorPublicActionType.Send:
                if (this.hasError) {
                    return;
                }
                this.message = this.editorInstance.content;
                this.send.emit({
                    message: this.message,
                    files: this.tempFiles,
                });
                this.message = this.editorInstance.content = '';
                this.tempFiles = [];
                break;
            case MarkDownEditorPublicActionType.Upload:
                this.onFileSelection.emit(this.tempFiles);
                break;
            case MarkDownEditorPublicActionType.Video_record:
                // pass for video recored
                this.recordVideo.emit(true);
                break;
            case MarkDownEditorPublicActionType.Emoji:
                emojiButtonHandler(this.editorInstance.editorView);
                break;
        }
    }
    enterKeyPress() {
        if (this.editorInstance?.content && this.editorInstance.content.trim().length > 0) {
            const _e = this._editorOptions.footer.action.find((action) => action.type === MarkDownEditorPublicActionType.Send);
            if (_e) {
                this.zone.run(() => {
                    this.processAction(_e);
                });
            }
        }
    }
    getCurrentState() {
        const message = this.editorInstance?.content ?? '';
        return { message, files: this.tempFiles };
    }
    /**
     * Should be called from ValueChangesExternalPlugin
     */
    emitMessageChanges() {
        this.stateChanged.emit(this.getCurrentState());
        this.onChange(this.getCurrentState().message);
    }
    counterHandler(valid, showCounter, currentCount) {
        this.currentCharacterCount = (currentCount === 0 ? 1 : currentCount) ?? 1;
        this.showCounter = showCounter ?? false;
        this.hasError = !valid;
    }
    autocompleteHandler(query) {
        // logic to retrieve mentions ....
        if (this.mentionConfig?.autoComplete) {
            return this.mentionConfig.autoComplete.call(this, query);
        }
        return new Promise((resolve) => resolve([]));
    }
    markEditorReadOnly(isReadOnly) {
        if (this.editorInstance) {
            const readOnlyMethod = isReadOnly ? 'disable' : 'enable';
            this.editorInstance[readOnlyMethod]();
        }
    }
    initEditor() {
        const options = {
            editorPlugins: [ValueChangesExternalPlugin(this.emitMessageChanges.bind(this))],
            placeholderText: this.placeholder,
            richTextOptions: {
                counterOption: {
                    maxLength: this.maxLength,
                    minShowCounterLength: this.minShowCounterLength,
                    currentState: this.counterHandler.bind(this),
                },
                fileUpload: {
                    maxFileSizeInMB: this.maxFileSize,
                    handler: (files) => this.selectFile(files),
                },
                mentions: {
                    autoComplete: this.autocompleteHandler.bind(this),
                    mentionDetail: this.mentionConfig?.mentionDetail,
                },
                classList: this.fieldClassList,
            },
        };
        this.editorInstance = new E11Editor(this.editorElementHTMLElement, this.message, options);
        this.markEditorReadOnly(this.isDisabled);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MarkdownEditorComponent, selector: "e11-markdown-editor", inputs: { isDisabled: "isDisabled", editorOption: "editorOption", tempFiles: "tempFiles", message: "message", placeholder: "placeholder", hasError: "hasError", maxLength: "maxLength", minShowCounterLength: "minShowCounterLength", autocomplete: "autocomplete", maxFileSize: "maxFileSize", mentionConfig: "mentionConfig", fieldClassList: "fieldClassList", loading: "loading", fileInputs: "fileInputs" }, outputs: { send: "send", stateChanged: "stateChanged", recordVideo: "recordVideo", onFileSelection: "onFileSelection", _onError: "_onError", onReady: "onReady", onFileRemove: "onFileRemove" }, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => E11MarkdownEditorComponent), multi: true }], viewQueries: [{ propertyName: "markdownElement", first: true, predicate: ["markdownElement "], descendants: true }], ngImport: i0, template: "<div\n  class=\"e11-markdown e11-relative e11-mb-4 e11-w-full e11-bg-white e11-rounded-lg e11-border e11-border-gray-200\"\n  [ngClass]=\"{\n    'e11-border-skin-error': hasError,\n    'e11-skeleton e11-border-transparent': loading,\n    'e11-cursor-not-allowed e11-opacity-70': isDisabled\n  }\"\n>\n  <div [ngClass]=\"{ 'e11-invisible': loading }\">\n    <div #markdownElement [id]=\"randomId\" class=\" \"></div>\n    <div id=\"suggestion\" style=\"display: none\"></div>\n    <div *ngIf=\"tempFiles && tempFiles.length > 0\" class=\"e11-flex e11-gap-2 e11-items-center e11-px-4\">\n      <e11-markdown-editor-attachments [files]=\"tempFiles\" (fileAction)=\"fileAction($event)\"></e11-markdown-editor-attachments>\n    </div>\n    <div\n      *ngIf=\"showCounter\"\n      class=\"e11-absolute e11-text-xs e11-right-2 e11-bottom-10 e11-inline-flex e11-items-center e11-rounded-full e11-px-2.5 e11-py-0.5\"\n      [ngClass]=\"{\n        'e11-bg-red-100 e11-text-red-800': hasError,\n        'e11-text-gray-800 e11-bg-gray-100': !hasError\n      }\"\n    >\n      {{ currentCharacterCount }} / {{ maxLength }}\n    </div>\n    <e11-markdown-editor-footer\n      [footer]=\"_editorOptions.footer\"\n      (action)=\"processAction($event)\"\n      (fileSelected)=\"selectFile($event)\"\n    ></e11-markdown-editor-footer>\n  </div>\n</div>\n", styles: [".e11-markdown{word-break:break-word}.autocomplete{border-radius:2px 2px 0 0;border-bottom-color:#fff;padding:2px 5px}.command{color:red}#info{margin-top:1em;background-color:transparent;min-height:1.5em;display:block}emoji-picker{width:100%;height:300px}.ProseMirror{max-height:250px;overflow-y:scroll}.ProseMirror p{margin-bottom:.2rem}.ProseMirror-dragging{background-color:#0006!important;outline:2px dashed white!important;outline-offset:-10px!important}.ProseMirror-dragging:after{content:\"Drop File to upload\";position:absolute;color:#fff;margin-left:auto;margin-right:auto;text-align:center;top:20px;left:45%}.suggestion-item-active{background:#08c;color:#fff}.prosemirror-mention-node,.prosemirror-tag-node{color:#08c}.prosemirror-suggestion{background:#0000000d;border:1px solid #dddada;border-radius:5px}.suggestion-item-list{background:#fff;border:1px solid #dddada;border-radius:5px}.suggestion-item{padding:5px}.suggestion-item:before{border-top:1px solid}em del,em strong del{font-style:italic!important}.ProseMirror .s-code-block.markdown[data-placeholder]{overflow:visible}.ProseMirror code,.ProseMirror pre{word-wrap:break-word;white-space:pre-wrap}.ProseMirror>code,.ProseMirror>pre{background-color:inherit;border-radius:0;margin:0;max-height:unset;padding:0;width:auto}.ProseMirror>ul li,.ProseMirror>ol li{margin-bottom:unset!important}.ProseMirror-separator{display:none!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: E11MarkdownEditorFooterComponent, selector: "e11-markdown-editor-footer", inputs: ["footer"], outputs: ["fileSelected", "action"] }, { kind: "component", type: E11MarkdownEditorImageComponent, selector: "e11-markdown-editor-attachments", inputs: ["files"], outputs: ["fileAction"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-markdown-editor', encapsulation: ViewEncapsulation.None, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => E11MarkdownEditorComponent), multi: true }], template: "<div\n  class=\"e11-markdown e11-relative e11-mb-4 e11-w-full e11-bg-white e11-rounded-lg e11-border e11-border-gray-200\"\n  [ngClass]=\"{\n    'e11-border-skin-error': hasError,\n    'e11-skeleton e11-border-transparent': loading,\n    'e11-cursor-not-allowed e11-opacity-70': isDisabled\n  }\"\n>\n  <div [ngClass]=\"{ 'e11-invisible': loading }\">\n    <div #markdownElement [id]=\"randomId\" class=\" \"></div>\n    <div id=\"suggestion\" style=\"display: none\"></div>\n    <div *ngIf=\"tempFiles && tempFiles.length > 0\" class=\"e11-flex e11-gap-2 e11-items-center e11-px-4\">\n      <e11-markdown-editor-attachments [files]=\"tempFiles\" (fileAction)=\"fileAction($event)\"></e11-markdown-editor-attachments>\n    </div>\n    <div\n      *ngIf=\"showCounter\"\n      class=\"e11-absolute e11-text-xs e11-right-2 e11-bottom-10 e11-inline-flex e11-items-center e11-rounded-full e11-px-2.5 e11-py-0.5\"\n      [ngClass]=\"{\n        'e11-bg-red-100 e11-text-red-800': hasError,\n        'e11-text-gray-800 e11-bg-gray-100': !hasError\n      }\"\n    >\n      {{ currentCharacterCount }} / {{ maxLength }}\n    </div>\n    <e11-markdown-editor-footer\n      [footer]=\"_editorOptions.footer\"\n      (action)=\"processAction($event)\"\n      (fileSelected)=\"selectFile($event)\"\n    ></e11-markdown-editor-footer>\n  </div>\n</div>\n", styles: [".e11-markdown{word-break:break-word}.autocomplete{border-radius:2px 2px 0 0;border-bottom-color:#fff;padding:2px 5px}.command{color:red}#info{margin-top:1em;background-color:transparent;min-height:1.5em;display:block}emoji-picker{width:100%;height:300px}.ProseMirror{max-height:250px;overflow-y:scroll}.ProseMirror p{margin-bottom:.2rem}.ProseMirror-dragging{background-color:#0006!important;outline:2px dashed white!important;outline-offset:-10px!important}.ProseMirror-dragging:after{content:\"Drop File to upload\";position:absolute;color:#fff;margin-left:auto;margin-right:auto;text-align:center;top:20px;left:45%}.suggestion-item-active{background:#08c;color:#fff}.prosemirror-mention-node,.prosemirror-tag-node{color:#08c}.prosemirror-suggestion{background:#0000000d;border:1px solid #dddada;border-radius:5px}.suggestion-item-list{background:#fff;border:1px solid #dddada;border-radius:5px}.suggestion-item{padding:5px}.suggestion-item:before{border-top:1px solid}em del,em strong del{font-style:italic!important}.ProseMirror .s-code-block.markdown[data-placeholder]{overflow:visible}.ProseMirror code,.ProseMirror pre{word-wrap:break-word;white-space:pre-wrap}.ProseMirror>code,.ProseMirror>pre{background-color:inherit;border-radius:0;margin:0;max-height:unset;padding:0;width:auto}.ProseMirror>ul li,.ProseMirror>ol li{margin-bottom:unset!important}.ProseMirror-separator{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }], propDecorators: { markdownElement: [{
                type: ViewChild,
                args: ['markdownElement ', { static: false }]
            }], isDisabled: [{
                type: Input
            }], editorOption: [{
                type: Input
            }], send: [{
                type: Output
            }], stateChanged: [{
                type: Output
            }], recordVideo: [{
                type: Output
            }], onFileSelection: [{
                type: Output
            }], _onError: [{
                type: Output
            }], onReady: [{
                type: Output
            }], onFileRemove: [{
                type: Output
            }], tempFiles: [{
                type: Input
            }], message: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], hasError: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minShowCounterLength: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], maxFileSize: [{
                type: Input
            }], mentionConfig: [{
                type: Input
            }], fieldClassList: [{
                type: Input
            }], loading: [{
                type: Input
            }], fileInputs: [{
                type: Input
            }] } });

class E11MarkdownEditorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorModule, declarations: [E11MarkdownEditorComponent,
            E11MarkdownEditorFooterComponent,
            E11MarkdownEditorHeaderComponent,
            E11MarkdownEditorIconComponent,
            E11MarkdownEditorImageComponent], imports: [CommonModule], exports: [E11MarkdownEditorComponent,
            E11MarkdownEditorFooterComponent,
            E11MarkdownEditorHeaderComponent,
            E11MarkdownEditorIconComponent,
            E11MarkdownEditorImageComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MarkdownEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        E11MarkdownEditorComponent,
                        E11MarkdownEditorFooterComponent,
                        E11MarkdownEditorHeaderComponent,
                        E11MarkdownEditorIconComponent,
                        E11MarkdownEditorImageComponent,
                    ],
                    imports: [CommonModule],
                    exports: [
                        E11MarkdownEditorComponent,
                        E11MarkdownEditorFooterComponent,
                        E11MarkdownEditorHeaderComponent,
                        E11MarkdownEditorIconComponent,
                        E11MarkdownEditorImageComponent,
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11MarkdownEditorComponent, E11MarkdownEditorFooterComponent, E11MarkdownEditorHeaderComponent, E11MarkdownEditorIconComponent, E11MarkdownEditorImageComponent, E11MarkdownEditorModule, checkIfImageIsAllowedType, checkIfVideoIsAllowedType, generateIMarkdownFile, mimeTypeIconMap, validateFileSize, validateFileUploads };
